import { useState, useEffect, useRef } from "react";
import { safeTrace, describePrintLine } from "../../../../lib/printer/giftCardPrintType";
import { message, Modal } from "antd";
import { WarningOutlined } from "@ant-design/icons";
import { v4 as uuidv4 } from "uuid";
import dayjs from "dayjs";
import db from "../../../../database";
import { dateValidator } from "../../../../utils/clock";
import { SyncData } from "../../Restaurant/Tables-Management/SyncData";
import PrintController from "../../../../lib/printer/printController";
import upsertPOSLog, { correctLogPreviewDocumentNo } from "../../Retail/posLog";
import {
  posOrderProcessor1,
  confirmPOSLayawayOrder1,
  RFIDTag,
  upsertPOSLogService,
  generateGiftVoucher,
  upsertCart,
  getEInvoiceDetails,
  getEInvoiceURLs,
  salesHistoryDetails,
} from "../../../../services";
import useCartStore from "../../../../stores/useCartStore";
import usePOSConfigStore from "../../../../stores/usePOSConfigStore";
import useTransactionStore from "../../../../stores/useTransactionStore";
import useAuthStore from "../../../../stores/useAuthStore";
import useSessionStore from "../../../../stores/useSessionStore";
import useRestaurantStore from "../../../../stores/useRestaurantStore";
import { roundTo } from "../../../../utils/tillHelpers";
import {
  assertOrderTotals,
  assertPaymentBalance,
  assertOrderJSON,
  assertOrderLinesMatchHeader,
  assertOrderPayloadFinite,
  assertLineWeightScaling,
  reVerifyStockAtCheckout,
  assertDocumentNoUnique,
  recoverStuckSyncOrders,
  reconcileOrderTotalDrift,
  verifyMirrorOrders,
} from "../../../../utils/dataIntegrity";
import { enrichLineItemFields, groupItemsByTaxRate } from "../utils/taxCalculations";
import { buildApprovalMeta, withApprovalMeta } from "../PricingRules/discountApproval";
import { verifyStock as verifyStockService } from "../../../../services";
import { stock } from "../stock";
import { notifySyncFailure } from "../../../../utils/pos/syncFailureAlert";
import { trace } from "../../../../utils/debugTrace";
import { cleanupNormalTable, cleanupMergedTables, cleanupSplitTable, cleanupTableForBackgroundSyncedOrder } from "../../../../utils/restaurantOrderCleanup";
import { resolveSequenceType, generateDocumentNo } from "../../../../utils/documentSequenceUtils";
import { logSequenceEvent } from "../../../../utils/sequenceAudit";
import { shouldRegenerateIdentityAtSave, findDuplicateEstimateOrder, captureOrderCreationSnapshot } from "../../../../utils/cartIdentityGuard";
import { getIsInterState } from "../../../../utils/taxUtils";
import { resolveActingUserId } from "../../../../utils/actingUser";
import {
  advanceOrderFloorViolation,
  isAdvanceOrderMissingAdvanceItem,
  resolveIsCompleteOrderAction,
  resolveIsFinalizingAdvanceToInvoice,
  resolveIsAdvanceFlag,
  pairReaddedAdvanceLines,
} from "../../../../utils/advanceOrderValidation";
import messages from "../../../../errors/messages.en-IN.json";
import { buildPaymentDataDetails, applyPaymentDataToLine } from "../payment/paymentDataMeta";

/**
 * TSK-2470 — Resolve the csUomId to send for an addon line.
 * Restaurant addons (selectedAddons) only carry { mProductId, name }, so the
 * UOM has to come from the addon product stored in db.products. Prefer the
 * product's alternate UOM id when it has one; otherwise fall back to the base
 * UOM (uomData[0] or the product's own csUomId). Returns null when the product
 * can't be resolved.
 */
const resolveAddonUom = async (productId) => {
  if (!productId) return null;
  try {
    const product = await db.products.where("mProductId").equals(productId).first();
    if (!product) return null;
    if (product.alternateUom?.length > 0 && product.alternateUom[0]?.csUomId) return product.alternateUom[0].csUomId;
    return product.uomData?.[0]?.csUomId || product.csUomId || null;
  } catch (e) {
    console.error("[TSK-2470] resolveAddonUom lookup failed for", productId, e);
    return null;
  }
};

/**
 * TSK-3249 — orders[i].orderDate is written as either "DD-MM-YYYY" (SuperMarket/
 * Preorder/ParkedBills/useOrderProcessing) or "YYYY-MM-DD" (Restaurant), never
 * parsed with dayjs's customParseFormat plugin (only registered as a side effect
 * of Dashboard2/index.js happening to be loaded — not guaranteed in a given
 * session, and dayjs silently mis-parses "DD-MM-YYYY" as US-style MM-DD without
 * it). Manual, format-agnostic conversion to "YYYY-MM-DD" avoids that trap.
 */
const toISOBusinessDate = (d) => {
  if (!d) return null;
  if (/^\d{4}-\d{2}-\d{2}/.test(d)) return d.slice(0, 10);
  const m = /^(\d{2})-(\d{2})-(\d{4})$/.exec(d);
  return m ? `${m[3]}-${m[2]}-${m[1]}` : null;
};

/**
 * DI-36 support — minimal, side-effect-free existence check for a single
 * order, used by verifyMirrorOrders (dataIntegrity.js) to decide whether a
 * mirror-received order (POS_ORDER_UPSERT) was actually synced by its owning
 * device before promoting it into the normal create-sync loop.
 *
 * Deliberately does NOT reuse getSalesHistoryDetails (salesHistoryUtils.js) --
 * that helper has UI side effects (print-guard claiming, KOT/invoice printing,
 * selectedOrderToPrint state) that must never fire from a silent background
 * check. This calls the raw salesHistoryDetails(data) service directly with a
 * trimmed query requesting only sOrderID.
 *
 * @returns {Promise<boolean|null>} true = found on server, false = confirmed
 *   not found, null = lookup failed (unknown, caller should retry later).
 */
export const checkOrderExistsOnServer = async (sOrderID) => {
  if (!sOrderID) return null;
  try {
    const response = await salesHistoryDetails({
      query: `query {salesHistoryDetails(orderId: "${sOrderID}") { sOrderID }}`,
    });
    // salesHistoryDetails() (orderServices.js) swallows network/axios errors
    // internally and returns undefined rather than rejecting -- response.data.data
    // (the GraphQL envelope) only exists on an actual successful round trip, so
    // its absence means "lookup failed", NOT "order not found". Conflating the
    // two would promote isSynced to 0 on a network hiccup and risk a real
    // duplicate create the next time posOrderProcessor1 fires.
    if (!response?.data?.data) return null;
    const found = response.data.data.salesHistoryDetails?.[0];
    return Boolean(found?.sOrderID);
  } catch (err) {
    console.warn("[DI-36] checkOrderExistsOnServer lookup failed:", sOrderID, err?.message);
    return null;
  }
};

/**
 * SYNC-DIAG throttle — the auto-sync tick runs every 10s (~8,640 ticks/day). Without
 * this, a single order stuck in one of the new sync-diag-* states would write ~8,640
 * near-identical rows to db.sequenceAuditLog over one day, and a till with many stuck
 * orders could produce hundreds of thousands of rows — bloating IndexedDB and making
 * the exported diagnostic dump impractical to review. Module-level (not per-render) so
 * it persists for the life of the tab; resets on reload, which just re-logs once more.
 * Keyed by event+sOrderID so different orders/failure types are tracked independently.
 */
const SYNC_DIAG_THROTTLE_MS = 30 * 60 * 1000; // log each order/event combo at most once per 30 min
const syncDiagLastLogged = new Map();
const logSyncDiagThrottled = (event, details, dbAdapter) => {
  // The pre-loop exclusion summary has no single sOrderID (it's an aggregate across
  // orders) — falls back to a shared per-event key so it still throttles to once per
  // window instead of once per tick.
  const key = `${event}:${details?.sOrderID || "aggregate"}`;
  const now = Date.now();
  const last = syncDiagLastLogged.get(key);
  if (last && now - last < SYNC_DIAG_THROTTLE_MS) return;
  syncDiagLastLogged.set(key, now);
  logSequenceEvent(event, details, dbAdapter);
};

/**
 * useOrderProcessing — extracted from Core/index.js (~lines 7000–10316)
 *
 * Manages processOrder, processOrderApproved, syncOrders,
 * print template selection, and the massive GraphQL mutation builder.
 */
const useOrderProcessing = ({
  cart,
  setCart,
  setSwitchToggle,
  tillData,
  tillDataPaymentMethods,
  posConfig,
  precision,
  orderType,
  taxIncludeFlag,
  authHeaders,
  giftCardData,
  setGiftCardData,
  documentSequence,
  setDocumnetSequence,
  deleteCart,
  overPayedAmount,
  setOverPayedAmount,
  amount,
  setAmount,
  selectedPaymentMethod,
  setSelectedPaymentMethod,
  setPaymentModal,
  setShowPaymentMethods,
  setLoader,
  setLoading,
  splitCarts,
  selectedSaleTypeData,
  setSelectedSaleType,
  setSelectedSaleTypeData,
  setSelectedProductInCart,
  setSelectedRowKeys,
  setStartIndex,
  setCustomMixItems,
  setSelectedDate,
  setSelectedDocumentType,
  setPaymentProcessFlag,
  setNotesValue,
  setIsInputFocused,
  hasShownCustomerModalRef,
  searchInputRef,
  setDisplayCustomerSearch,
  setIsRefundWalletOpen,
  setSelectedCoupon,
  documents,
  setDocuments,
  retailCustomerFields,
  customerForm,
  tillLayout,
  history,
  showOrderHistory,
  setActiveInput,
  eBillConfig,
  sendWebHookData,
  orderTypeSelection,
  isCallCenterFlow,
  pricePrecision,
  initialShowPayments,
  setShowPayments,
  saleTypeData,
  setShowIRNModal,
  setIrnStatus,
  setIrnNumber,
  setIrnPdfUrl,
  setIrnLoading,
  setCompletedOrderId,
  setInvoiceProcessing,
  setInvoicePrinted,
  setIrnGenerating,
  setIrnDownloadReady,
  setPendingIRNOrderId,
  setIrnFailureMessage,
  setProductStock,
  setTempCart,
  setShowCompleteSaleStock,
  setCompleteSaleStockData,
}) => {
  // ─── State ───
  const [orderDelay, setOrderDelay] = useState(dayjs().format("YYYY-MM-DD HH:mm:ss"));
  const [activeOrderProcess, setActiveOrderProcess] = useState(false);
  const orderState = useRef(0);

  const [showPrintTemplateModal, setShowPrintTemplateModal] = useState(false);
  const [selectedPrintTemplate, setSelectedPrintTemplate] = useState(null);
  const [pendingOrderParam, setPendingOrderParam] = useState(null);
  const [pendingDocumentNo, setPendingDocumentNo] = useState(null);
  const [pendingIsSynced, setPendingIsSynced] = useState(null);
  const [isPendingDirectApproved, setIsPendingDirectApproved] = useState(false);
  const [isPendingSyncOrder, setIsPendingSyncOrder] = useState(false);
  // BUG-2261 follow-up: RefundWalletDrawer's Confirm Refund clears selectedReturnType
  // and closes the drawer as soon as requestPayment resolves -- but for a multi-print-
  // template till, that resolution only means the print template modal was opened; the
  // order itself isn't submitted until handlePrintTemplateConfirm runs. Latching whether
  // this print-template flow started from a STORE_CREDIT return lets handlePrintTemplateCancel
  // restore that context (reopen the wallet drawer) instead of dropping the user onto the
  // now-uncovered, editable cart/payment screen with an incomplete return still pending.
  const isPendingStoreCreditReturnRef = useRef(false);

  const enableMultiPrintTemplates = tillData?.tillAccess?.cwrTill?.enableMultiPrintTemplates === "Y";
  const printTemplates = tillData?.tillAccess?.cwrTill?.printTemplates ? JSON.parse(tillData?.tillAccess?.cwrTill?.printTemplates) : [];
  const defaultCustomer = tillData.tillAccess.csBunit.b2cCustomer;
  const tillId = tillData.tillAccess?.cwrTill?.cwrTillID;
  const { userData, tillValue } = useAuthStore.getState();
  const tillaccess = JSON.parse(tillData?.tillAccess?.userAccessController);
  const printerURL = tillData?.tillAccess?.cwrTill?.hardwareController?.imageUrl;

  // Shared by both fetchIRNDetails' polling success branch and the posOrderProcessor1
  // direct-success fast path (TSK-3774 follow-up): once an eInvoiceNo is known-good,
  // fetch its download URL and populate the IRN modal. Kept as one function so the two
  // call sites can't drift on how a "success" is turned into a downloadable PDF.
  //
  // knownDownloadUrl: once the backend adds eInvoice.downloadUrl to posOrderProcessor1's
  // response, pass it straight through here -- no getEInvoiceURLs call needed at all in
  // that case. Until the backend field exists (or on an order where it's absent for some
  // other reason), this falls back to the existing getEInvoiceURLs retry below, unchanged.
  const resolveIrnDownload = async (orderId, eInvoiceNo, knownDownloadUrl) => {
    setIrnStatus?.("SUCCESS");
    setIrnNumber?.(eInvoiceNo);
    setIrnGenerating?.("SUCCESS");

    if (knownDownloadUrl) {
      setIrnPdfUrl?.(knownDownloadUrl);
      setIrnDownloadReady?.("SUCCESS");
      return;
    }

    setIrnDownloadReady?.("PENDING");

    // The e-invoice record itself (irn/eInvoiceNo/qrCode) exists the instant
    // posOrderProcessor1 returns "SUC", but the downloadable PDF/URL is prepared
    // slightly later on the backend -- confirmed live: getEInvoiceURLs returned
    // "No E-Invoice URLs found" immediately after a SUC response, yet the same
    // order downloaded fine from backoffice moments later. fetchIRNDetails'
    // polling path never hit this because it only reaches getEInvoiceURLs after
    // several getEInvoiceDetails poll intervals have already elapsed. Retry a
    // few times here (URL readiness only, not full invoice generation) before
    // treating it as a real failure.
    const URL_MAX_RETRIES = 4;
    const URL_RETRY_INTERVAL = 3000;

    for (let attempt = 1; attempt <= URL_MAX_RETRIES; attempt += 1) {
      try {
        const urlPayload = {
          query: `query { getEInvoiceURLs(orderId: "${orderId}") }`,
        };
        const urlResponse = await getEInvoiceURLs(urlPayload);
        const urlResult = urlResponse?.data?.data?.getEInvoiceURLs ? JSON.parse(urlResponse.data.data.getEInvoiceURLs) : null;
        if (urlResult?.urls) {
          setIrnPdfUrl?.(urlResult.urls);
          setIrnDownloadReady?.("SUCCESS");
          return;
        }
      } catch {
        // fall through to retry
      }
      if (attempt < URL_MAX_RETRIES) {
        await new Promise((resolve) => setTimeout(resolve, URL_RETRY_INTERVAL));
      }
    }
    setIrnDownloadReady?.("FAILED");
  };

  const fetchIRNDetails = async (orderId) => {
    const MAX_RETRIES = 6;
    const POLL_INTERVAL = 5000;
    let retryCount = 0;

    return new Promise((resolve) => {
      const pollTimer = setInterval(async () => {
        retryCount++;
        try {
          const queryPayload = {
            query: `query { getEInvoiceDetails(orderId: "${orderId}")
            }`,
          };
          const response = await getEInvoiceDetails(queryPayload);
          const result = response?.data?.data?.getEInvoiceDetails ? JSON.parse(response.data.data.getEInvoiceDetails) : null;
          // Backend eInvoiceStatus is a genuine terminal signal: "IG" (generated) and "IF"
          // (failed) both come from the e-invoice provider and won't change on their own.
          // Anything else ("PEN", or no ref/status/no at all -> "EInvoice Not Generated" /
          // "EInvoice Not Found for Order") means the backend hasn't resolved it yet, so that
          // case alone is retryable/incomplete rather than a definitive failure.
          if (result?.eInvoiceStatus === "IG") {
            clearInterval(pollTimer);
            logSequenceEvent("gst-diag-irn-poll-generated", {
              sOrderID: orderId,
              retryCount,
              eInvoiceNo: result.eInvoiceNo,
            }, db);
            await resolveIrnDownload(orderId, result.eInvoiceNo);
            resolve(true);
          } else if (result?.eInvoiceStatus === "IF") {
            clearInterval(pollTimer);
            logSequenceEvent("gst-diag-irn-poll-failed", {
              sOrderID: orderId,
              retryCount,
              eInvoiceStatus: result?.eInvoiceStatus,
              message: result?.message,
              rawResult: JSON.stringify(result)?.slice(0, 500),
            }, db);
            setIrnStatus?.("FAILED");
            setIrnGenerating?.("FAILED");
            setIrnFailureMessage?.(result?.message || "EInvoice Failed");
            resolve(false);
          } else if (retryCount >= MAX_RETRIES) {
            clearInterval(pollTimer);
            logSequenceEvent("gst-diag-irn-poll-timeout", {
              sOrderID: orderId,
              retryCount,
              lastEInvoiceStatus: result?.eInvoiceStatus,
              lastMessage: result?.message,
              rawResult: JSON.stringify(result)?.slice(0, 500),
            }, db);
            setIrnStatus?.("INCOMPLETE");
            setIrnGenerating?.("INCOMPLETE");
            setIrnFailureMessage?.(result?.message || "IRN generation timed out");
            resolve(false);
          }
        } catch (pollError) {
          if (retryCount >= MAX_RETRIES) {
            clearInterval(pollTimer);
            logSequenceEvent("gst-diag-irn-poll-error", {
              sOrderID: orderId,
              retryCount,
              errorMessage: pollError?.message,
            }, db);
            setIrnStatus?.("INCOMPLETE");
            setIrnGenerating?.("INCOMPLETE");
            setIrnFailureMessage?.("Failed to fetch IRN details");
            resolve(false);
          }
        }
      }, POLL_INTERVAL);
    });
  };

  const getDefaultSaleType = async () => {
    const saleData = await db.posSaletypes.toArray();
    return saleData.find((sale) => sale?.cwrSaletype?.isdefault === "Y");
  };

  // BUG-2020 / BUG-2404: shared completion-time advance-payment floor gate. Returns
  // true (and shows the popup) when `cartToCheck` is a retrieved, paid
  // Advance/Layaway order whose revised total is below the amount already paid.
  const showAdvanceFloorViolationIfAny = (cartToCheck) => {
    const floorViolation = advanceOrderFloorViolation(cartToCheck, cartToCheck?.total, { atOrderProcessing: true });
    if (!floorViolation) return false;
    Modal.warning({
      title: <div style={{ fontWeight: "500", fontSize: "1.2em", color: "#0F0718" }}>Amount Cannot Be Reduced Below Paid Amount</div>,
      content: (
        <div style={{ fontSize: "14px", color: "#0F0718", marginTop: "8px" }}>
          <p style={{ marginBottom: 0, color: "#0F0718", fontSize: "1em" }}>{floorViolation}</p>
        </div>
      ),
      icon: <WarningOutlined />,
      okText: "Ok",
      okButtonProps: { style: { display: "inline-block" } },
      modalRender: (node) => <div className="custom-confirm-modal-wrapper">{node}</div>,
    });
    return true;
  };

  // ─── processOrder ───
  const beginPrintTemplateFlow = (param, isSyncOrder) => {
    // Read before RefundWalletDrawer's own post-await cleanup nulls it out.
    isPendingStoreCreditReturnRef.current = useTransactionStore.getState().selectedReturnType === "STORE_CREDIT";
    setPendingOrderParam(param);
    setPendingDocumentNo(null);
    setPendingIsSynced(null);
    setIsPendingDirectApproved(false);
    setIsPendingSyncOrder(isSyncOrder);
    setSelectedPrintTemplate(printTemplates[0]?.cwr_printtemplate_id || null);
    setShowPrintTemplateModal(true);
  };

  const processOrder = async (param) => {
    // TSK-2935: a Prepare-Bill-originated order must be submitted with layAway:"Y" so
    // the completion classifier in syncOrders routes it to confirmPOSLayawayOrder1
    // (reusing its already-issued document number) instead of posOrderProcessor1 again.
    // This is deliberately NOT set persistently on the cart any earlier than this —
    // handleOrderCompletion (OrderListComponent.js) unconditionally treats any cart with
    // layAway==="Y" as a fresh advance-booking-in-progress on every payment click, so
    // setting it before the final submit click would hijack ordinary payment entry into
    // that flow. Flipping it here, only at the exact point of the final processOrder()
    // call (never reached by handleOrderCompletion — see its own layAway checks), avoids
    // that collision while still reaching the classifier with the right value.
    if (param !== "layaway") {
      const freshCartForPrepareBill = useCartStore.getState().cart;
      if (freshCartForPrepareBill?.preparedBillOrigin === "Y" && freshCartForPrepareBill?.layAway !== "Y") {
        useCartStore.getState().setCart((prev) => ({ ...prev, layAway: "Y" }));
      }
    }
    // Enforce posConfig.customerRequired before locking in the order. Without
    // this, cashiers could complete a sale against the default B2C anonymous
    // customer even when the backend explicitly required a real one. (Issue #9)
    if (posConfig?.customerRequired === "Y") {
      const freshCart = useCartStore.getState().cart || cart;
      const b2cId = tillData?.tillAccess?.csBunit?.b2cCustomer?.cwrCustomerId;
      const cartCustomerId = freshCart?.customer?.cwrCustomerId;
      if (!cartCustomerId || cartCustomerId === b2cId) {
        message.warning("Customer is required to complete this order. Please add a customer.");
        orderState.current = 0;
        return;
      }
    }
    // DI-02: Validate payment total matches order total before processing.
    // Skip for exchange orders (return credit not in payments array),
    // credit sales (credit bypasses normal payment flow), and
    // layaway/advance/pre-orders (partial payment is intentional).
    {
      const isExchange = useTransactionStore.getState().selectedReturnType === "EXCHANGE";
      const isCreditSaleActive = useTransactionStore.getState().isCreditSale === "true";
      const freshCartForLayawayCheck = useCartStore.getState().cart || cart;
      const isLayawayFlow = param === "layaway" || freshCartForLayawayCheck?.layAway === "Y";
      const isCreditSaleSettlement = freshCartForLayawayCheck?.creditSale === "Y" || freshCartForLayawayCheck?.isCreditSale === "Y";
      if (!isExchange && !isCreditSaleActive && !isCreditSaleSettlement && !isLayawayFlow) {
        const freshCartForCheck = useCartStore.getState().cart || cart;
        const paymentCheck = assertPaymentBalance(freshCartForCheck, "processOrder");
        if (!paymentCheck.valid) {
          message.error(`Cannot process order: ${paymentCheck.message}`);
          orderState.current = 0;
          return;
        }
      }
    }

    // BUG-2404 (TIC-11240): the floor check in processOrderApproved is never reached
    // when posConfig.disableOfflineBilling === "Y" (or its print-template variant) --
    // those branches below call syncOrders(param) directly. Since 208e373c the /pos
    // edit-time check is off by design, so a retrieved advance order edited below its
    // paid amount was saved and invoiced (gpr_test HBGPOS/722: ₹760 total, ₹1000
    // paid). Enforce it here, before any branch, on the live cart. Covers every
    // save button and the auto-complete effect, which all call processOrder.
    if (showAdvanceFloorViolationIfAny(useCartStore.getState().cart || cart)) {
      orderState.current = 0;
      if (setPaymentProcessFlag) setPaymentProcessFlag(false);
      return false;
    }

    // BUG-2342: advance orders are booked without a stock check (by design), and
    // Complete Sale (ManagePreorder.js) only checks stock when the order is
    // retrieved -- nothing re-checked it when the balance was finally paid, so the
    // Goods Shipment posted even on an allowNegativeStock="N" till and on-hand went
    // negative. Re-check here, once a Complete-Sale order (preOrder="Y") is fully
    // paid on the POS screen (Complete Sale hands off to /pos for payment; the
    // /preorder screen itself is excluded): every completion route (the
    // auto-complete effect in useCoreEffects, manual completion, and the
    // disableOfflineBilling -> syncOrders path below) funnels through this point
    // before the order is built or synced.
    {
      const freshCartForStockCheck = useCartStore.getState().cart || cart;
      const isPreOrderPage = window.location.pathname.includes("/preorder");
      const isFullyPaid = parseFloat(freshCartForStockCheck?.paid || 0) >= Math.abs(parseFloat(freshCartForStockCheck?.total || 0));
      const isReturnCart = freshCartForStockCheck?.isReturn === true || freshCartForStockCheck?.isReturn === "Y";
      if (
        !isPreOrderPage &&
        freshCartForStockCheck?.preOrder === "Y" &&
        isFullyPaid &&
        !isReturnCart &&
        tillData?.tillAccess?.csBunit?.allowNegativeStock !== "Y" &&
        navigator.onLine
      ) {
        const stockCart = {
          ...freshCartForStockCheck,
          items: (freshCartForStockCheck.items || []).filter((item) => item.productSegment !== "CB" && item.type === "I"),
        };
        const stockCheckResult =
          stockCart.items.length === 0 ||
          (await stock({
            cart: stockCart,
            setCart,
            tillData,
            setProductStock,
            isFinalValidation: false,
            setTempCart,
            isCompleteSale: true,
            setShowCompleteSaleStock,
            setCompleteSaleStockData,
          }));
        if (!stockCheckResult) {
          setSelectedProductInCart({});
          orderState.current = 0;
          if (setPaymentProcessFlag) setPaymentProcessFlag(false);
          return false;
        }
      }
    }

    orderState.current += 1;
    try {
      if (orderState.current === 1 && cart.items.length !== 0 && posConfig?.disableOfflineBilling !== "Y") {
        if (enableMultiPrintTemplates && printTemplates.length > 0) {
          beginPrintTemplateFlow(param, false);
        } else {
          await processOrderApproved(param);
          // BUG-1647: not all success/failure paths inside reset this latch, so it can drift
          // upward forever across a till session and permanently block later submissions.
          orderState.current = 0;
        }
      } else if (orderState.current === 1 && cart.items.length !== 0 && posConfig?.disableOfflineBilling === "Y") {
        if (enableMultiPrintTemplates && printTemplates.length > 0) {
          beginPrintTemplateFlow(param, true);
        } else {
          await syncOrders(param);
          // BUG-1647: same latch-never-resets defect as above, hit via the dedicated
          // "Credit Sale" button (SMPaymentBar.js) calling processOrder("layaway").
          orderState.current = 0;
        }
      } else {
        setPaymentModal(false);
      }
    } catch (error) {
      console.error("processOrder error:", error);
      orderState.current = 0;
      setActiveOrderProcess(false);
      setPaymentModal(false);
    }
  };

  // ─── handlePrintTemplateConfirm ───
  const handlePrintTemplateConfirm = async () => {
    setShowPrintTemplateModal(false);
    useTransactionStore.getState().setSelectedPrintTemplateId(selectedPrintTemplate);
    if (isPendingSyncOrder) {
      await syncOrders(pendingOrderParam);
    } else if (isPendingDirectApproved) {
      await processOrderApproved(pendingOrderParam, pendingDocumentNo, pendingIsSynced);
    } else {
      await processOrderApproved(pendingOrderParam);
    }
    // BUG-1647: mirrors handlePrintTemplateCancel's existing reset — print-confirm never
    // reset the latch itself, same defect as processOrder above.
    orderState.current = 0;
    setPendingOrderParam(null);
    setPendingDocumentNo(null);
    setPendingIsSynced(null);
    setIsPendingDirectApproved(false);
    setIsPendingSyncOrder(false);
    isPendingStoreCreditReturnRef.current = false;
  };

  // ─── handlePrintTemplateCancel ───
  const handlePrintTemplateCancel = () => {
    setShowPrintTemplateModal(false);
    orderState.current = 0;
    setPendingOrderParam(null);
    setPendingDocumentNo(null);
    setPendingIsSynced(null);
    setIsPendingDirectApproved(false);
    setIsPendingSyncOrder(false);
    // BUG-2261 follow-up: the order was never actually submitted (processOrderApproved/
    // syncOrders only runs from handlePrintTemplateConfirm), so backing out of print
    // template selection for a STORE_CREDIT return must put the user back at "Refund to
    // Customer Wallet", never at the bare cart/payment screen where they could edit or
    // cancel the still-pending return.
    if (isPendingStoreCreditReturnRef.current) {
      isPendingStoreCreditReturnRef.current = false;
      useTransactionStore.getState().setSelectedReturnType("STORE_CREDIT");
      setIsRefundWalletOpen(true);
    }
  };

  // ─── processOrderApproved ───
  const processOrderApproved = async (param, documentNo, isSynced, skipActiveCheck = false, eInvoiceData = null, orderSnapshot = null) => {
    // TIC/BUG advance-seq-race diagnostic (temporary): stamped once per call and
    // carried into every advance-seq-race-diagnostic* log this call emits below.
    // Two of these IDs showing up with overlapping timestamps for the same
    // sOrderID/till is direct proof of two concurrent calls into this function
    // racing each other, independent of whether the shared preorderSequence value
    // itself was ever observed to disagree.
    const orderSaveCallId = uuidv4();
    setSelectedPaymentMethod({});
    if (activeOrderProcess && !skipActiveCheck) {
      orderState.current = 0;
      return;
    }
    setOverPayedAmount(0);
    setAmount(0);
    setActiveOrderProcess(true);
    // Notify customer display to show processing/loading state
    window.dispatchEvent(new CustomEvent("cd:startProcessing"));
    const tableData = useRestaurantStore.getState().tableName || {};

    // BUG-1963/1966: when reconciling a specific already-submitted order (isSynced=1,
    // called with that order's own captured data as `orderSnapshot`), use the snapshot
    // instead of re-reading the live cart. The live cart can have moved on to a
    // different sale by the time this async reconciliation fires (background sync loop
    // iterates multiple pending orders; the till isn't blocked from starting a new sale
    // while a prior order's confirm round-trip is still in flight) — reading the live
    // cart here silently persisted the WRONG order's header/payments under this order's
    // sOrderID. Confirmed via tests/unit/bugs/reconciliation_live_cart_bleed_race.test.js
    // and the pre-existing BHRS/1771->1778 overwrite documented at the confirmPOSLayawayOrder1
    // call site below (~line 3736). Default (no snapshot passed) preserves prior behavior
    // for the interactive checkout path, where the live cart IS the order being processed.
    const cartToDb = JSON.parse(JSON.stringify(orderSnapshot || useCartStore.getState().cart || {}));
    // BUG-2076 follow-up: captured before DI-33 (below) can regenerate cartToDb.sOrderID.
    // DI-33's regeneration is a DB-write concern only (avoid overwriting an existing
    // db.orders row) -- it must not change what "this order's identity" means for the
    // isReconcilingStaleOrder check further down, which needs the identity the live
    // cart actually had when this call started.
    const cartToDbOriginalSOrderID = cartToDb.sOrderID;
    // Issue-7 / FK-3692,6319: see captureOrderCreationSnapshot's own docblock.
    captureOrderCreationSnapshot(cartToDb);
    // BUG-2020: revised order value must never save below the amount already paid on
    // a retrieved Advance/Layaway order. Gated to the interactive path only
    // (orderSnapshot is set only for background reconciliation of an
    // ALREADY-persisted order -- isSynced=1 -- retrying that save is not a new user
    // edit and must not be re-blocked here). Cancel/Void flows never reach here
    // interactively either -- they build their own zeroed cartToDb and either write
    // straight to db.orders or hand it to processOrderApproved WITH an orderSnapshot
    // (background sync), both of which skip this gate already.
    //
    // Widened scope: no longer gated on the GPR-only /preorder URL -- runs for any
    // retrieved Layaway/Preorder/Advance order, any client, any module
    // (isAdvanceOrderWithPayment's own isRetrived/salesHistory check does the scoping
    // now, and its isReturn check excludes Return/Exchange settlement against a paid
    // Advance/Layaway order -- cartToDb here is always the return cart, which carries
    // isReturn, even during an exchange).
    if (!orderSnapshot) {
      // atOrderProcessing: enforce the paid-amount floor for EVERY flow at completion
      // time, not just GPR's /preorder route -- a retrieved advance/layaway order (any
      // client, incl. Chocolala on /pos) must never be completed below what's already
      // been paid against it, even though its cart stays freely editable up to here.
      const floorViolation = advanceOrderFloorViolation(cartToDb, cartToDb.total, { atOrderProcessing: true });
      if (floorViolation) {
        // BUG-2020 (popup, not toast): backstop for any discount/edit path not yet covered
        // by a proactive check (e.g. manual discount call sites outside useCartOperations.js's
        // processLineManualDiscount/processTotalManualDiscount). Same Modal style as the
        // proactive checks in useCartHandlers.js/useCartOperations.js for a consistent UX
        // regardless of which stage catches the violation.
        Modal.warning({
          title: <div style={{ fontWeight: "500", fontSize: "1.2em", color: "#0F0718" }}>Amount Cannot Be Reduced Below Paid Amount</div>,
          content: (
            <div style={{ fontSize: "14px", color: "#0F0718", marginTop: "8px" }}>
              <p style={{ marginBottom: 0, color: "#0F0718", fontSize: "1em" }}>{floorViolation}</p>
            </div>
          ),
          icon: <WarningOutlined />,
          okText: "Ok",
          okButtonProps: { style: { display: "inline-block" } },
          modalRender: (node) => <div className="custom-confirm-modal-wrapper">{node}</div>,
        });
        orderState.current = 0;
        setActiveOrderProcess(false);
        window.dispatchEvent(new CustomEvent("cd:feedbackDone"));
        return;
      }
      // Advance-item-swapped-out-then-completed gap: cart.layAway/docType is
      // stamped once when the cashier picks "Advance" from the document-type
      // dropdown and never re-checked against the cart's actual contents
      // before checkout — so removing the Advance item afterwards and adding
      // regular items instead still completes and syncs as an Advance order
      // (confirmed live: chocolala_live AJ2 ADV/91, a fully-paid regular item
      // with layAway:"Y" and no Advance line at all). Scoped to the interactive,
      // not-yet-retrieved path only (same !orderSnapshot gating as the floor
      // check above) — a retrieved order being settled in full is a normal
      // completion and must not trip this.
      if (isAdvanceOrderMissingAdvanceItem(cartToDb)) {
        Modal.warning({
          title: <div style={{ fontWeight: "500", fontSize: "1.2em", color: "#0F0718" }}>{messages.errors.cart.advance_missing_at_checkout_title}</div>,
          content: (
            <div style={{ fontSize: "14px", color: "#0F0718", marginTop: "8px" }}>
              <p style={{ marginBottom: 0, color: "#0F0718", fontSize: "1em" }}>{messages.errors.cart.advance_missing_at_checkout}</p>
            </div>
          ),
          icon: <WarningOutlined />,
          okText: "Ok",
          okButtonProps: { style: { display: "inline-block" } },
          modalRender: (node) => <div className="custom-confirm-modal-wrapper">{node}</div>,
        });
        orderState.current = 0;
        setActiveOrderProcess(false);
        window.dispatchEvent(new CustomEvent("cd:feedbackDone"));
        return;
      }
    }
    if (cartToDb.estimateId) {
      // BUG-DUPLICATE-ESTIMATE-BILL: temporary diagnostic — correlates repeated
      // processOrderApproved entries against the same estimateId. Remove once confirmed.
      trace("EST-BILL", "processOrderApproved entry", {
        estimateId: cartToDb.estimateId,
        isRetrived: cartToDb.isRetrived,
        param,
        skipActiveCheck,
        documentSequence: useSessionStore.getState().documentSequence,
      });
    }
    // Capture the Prepare Bill marker NOW — the table cleanup calls below
    // (cleanupNormalTable/cleanupMergedTables) remove the per-table key as a
    // safety net, which would otherwise consume it before the receipt gate
    // reads it. The fallback only covers carts persisted before tableId
    // stamping, and only when the store's table record provably belongs to
    // this cart — otherwise a takeaway settled while a table awaits its
    // prepared bill would consume that table's marker and skip its own receipt.
    const fallbackTableId = cartToDb.tableNo && (tableData?.name === cartToDb.tableNo || tableData?.table === cartToDb.tableNo) ? tableData?.cwrFbTableId : null;
    const preparedBillTableId = cartToDb.tableId || fallbackTableId;
    const preparedBillKey = preparedBillTableId ? `isPreparedBill_${preparedBillTableId}` : "isPreparedBill";
    const isPreparedBill = localStorage.getItem(preparedBillKey) === "true";
    if (isPreparedBill) localStorage.removeItem(preparedBillKey);
    const isOCU = cart?.statusType === "OCU" && (posConfig?.enableGuestRegistration === "Y" || posConfig?.enableGuestRegistration === "N");
    let newDocumentSequence = isOCU ? documentSequence : documentSequence + 1;
    let usedOwnSequence = false;
    // Captured (not yet persisted) numeric value of a fresh preorder/advance number —
    // see the deferred-consumption block right after the order save succeeds, below.
    let preorderConsumedSeq = null;
    const tillSession = useSessionStore.getState().tillSession || {};
    const tillSessionId = tillSession.tillSessionId || "";

    if (param !== "layaway") {
      // BUG-1963 Part B (F-3): this whole block ran unconditionally on every
      // param!=="layaway" call, including all 4 reconciliation call sites (param
      // is orders[i].sOrderID there, never the literal "layaway"). It was written
      // when cartToDb WAS always the live cart (pre-orderSnapshot), so every
      // `cart.X` read below is switched to the already-snapshot-aware `cartToDb.X`
      // -- identical values on the interactive path (cartToDb is a deep copy of
      // `cart` at this point when no snapshot was passed), but now correctly
      // scoped to the order actually being reconciled instead of whatever sale
      // is live on the till right now. Previously this could flip a normal
      // reconciled order's payments negative (cart.isReturn from an unrelated
      // live return sale), push a spurious extra payment sized off the live
      // cart's total, or overwrite cartToDb.layAway from the live cart's flag.
      cartToDb.layAway = cartToDb.layAway === "Y" ? "Y" : "N";
      const change = cartToDb.change;
      const isPreOrderPage = window.location.pathname.includes("/preorder");
      if (cartToDb.items.some((it) => it.isReturn) && cartToDb.total === 0) {
        cartToDb.change = 0;
        const paymentType =
          selectedPaymentMethod.name?.toLowerCase() === "cash" || selectedPaymentMethod.name?.toLowerCase() === "card" ? selectedPaymentMethod.name.toLowerCase() : "cash";
        const pMindex = tillDataPaymentMethods.findIndex((pi) => pi.name.toLowerCase() === paymentType);
        // F-06: without a paymentEntryId, tender-delete predicates fall back to
        // name-matching and can remove every same-named payment entry instead of just this one.
        cartToDb.payments.push({
          ...tillDataPaymentMethods[pMindex],
          amount: Number(cartToDb.total) || 0,
          paymentEntryId: tillDataPaymentMethods[pMindex]?.paymentEntryId || uuidv4(),
        });
      } else if (amount < 0 && cartToDb.layAway === "Y" && cartToDb?.type === "Layaway" && isPreOrderPage) {
        cartToDb.payments.push({ ...selectedPaymentMethod, amount, paymentEntryId: selectedPaymentMethod?.paymentEntryId || uuidv4() });
      } else {
        cartToDb.change = parseFloat(parseFloat(change)?.toFixed(precision));
      }
      if (cartToDb.isReturn === true || cartToDb.isReturn === "Y") {
        for (let i = 0; i < cartToDb.payments.length; i++) {
          cartToDb.payments[i].amount *= -1;
        }
      }
    } else {
      cartToDb.layAway = "Y";
      if (selectedPaymentMethod?.name) {
        const pMindex = cartToDb.payments.findIndex((pi) => pi.name.toLowerCase() === selectedPaymentMethod.name.toLowerCase());
        if (pMindex !== -1) {
          cartToDb.payments[pMindex].amount += amount;
        } else {
          // F-06: without a paymentEntryId, tender-delete predicates fall back to
          // name-matching and can remove every same-named payment entry instead of just this one.
          cartToDb.payments.push({ ...selectedPaymentMethod, amount, paymentEntryId: selectedPaymentMethod?.paymentEntryId || uuidv4() });
        }
      }
    }

    // Loyalty points
    if (cartToDb.customer?.loyaltyLevel?.cwrLoyaltyLevelId) {
      const loyaltyLevelId = cartToDb.customer.loyaltyLevel.cwrLoyaltyLevelId;
      const loyaltyData = await db.loyalityData.where("loyaltylevelId").equals(loyaltyLevelId).first();
      if (loyaltyData) {
        const eventCalenders = loyaltyData.loyaltyEventCalenders || [];
        const isEventActive = eventCalenders.length > 0 && eventCalenders.some((ec) => dateValidator(ec.startDate, ec.endDate));
        // BUG-1963 Part B (F-4): sourced from cartToDb, not the live cart -- see the
        // F-3 comment above for why (unconditional on this order's own loyalty
        // eligibility, cartToDb.customer?.loyaltyLevel, not on !documentNo/!isSynced,
        // so it also runs during reconciliation and was awarding/deducting loyalty
        // points off whatever sale happened to be live rather than the reconciled order).
        const applicableRule = (loyaltyData.LoyaltyAccumulation || []).find((item) => {
          const productValidation = item.mProductId ? cartToDb.items.some((pc) => pc.mProductId === item.mProductId) : true;
          const categoryValidation = item.mProductCategoryId ? cartToDb.items.some((pc) => pc.mProductCategoryId === item.mProductCategoryId) : true;
          if (isEventActive) {
            return item.loyaltyEvent === (item.loyaltyEvent === "Y" ? "Y" : "N") && item.minPurchase <= Math.abs(cartToDb.total) && productValidation && categoryValidation;
          }
          return item.loyaltyEvent === "N" && item.minPurchase <= Math.abs(cartToDb.total) && productValidation && categoryValidation;
        });
        if (applicableRule) {
          const baseAmount = cartToDb.total - cartToDb.tax;
          if (tillData?.tillAccess?.csBunit?.enableLoyalty !== "Y") cartToDb.accumulationPoints = 0;
          else if (cartToDb.discount > 0 && loyaltyData.noLoyaltyOnDiscount === "Y") cartToDb.accumulationPoints = 0;
          else if (cartToDb.redemptionPoints > 0 && loyaltyData.noLoyaltyOnRedemption === "Y") cartToDb.accumulationPoints = 0;
          else {
            const rawPoints = Number((applicableRule.pointsPerUnit * baseAmount).toFixed(3));
            if (rawPoints < 0) {
              const currentBalance = Math.max(cartToDb.customer?.retlLoyaltyBalance || 0, 0);
              cartToDb.accumulationPoints = Math.max(rawPoints, -currentBalance);
            } else {
              cartToDb.accumulationPoints = rawPoints;
            }
          }
        }
      }
    }

    const now = dayjs(new Date());
    const orderTimeDetails = usePOSConfigStore.getState().orderTimeDetails || { orderStartTime: "", orderEndTime: "", paymentStartTime: "" };
    orderTimeDetails.orderEndTime = now.format("DD-MM-YYYY HH:mm:ss");
    let resolvedDocumentNo;
    // TIC/BUG advance-seq-race diagnostic (temporary): captures every input this
    // resolution reads, at the exact moment it reads it, for every layaway/advance
    // cart — success or failure — so an intermittent wrong-sequence pick (confirmed
    // real on CPE/CPD/CPAW/CPF/RAK2/RAMS: same till, same logged-in session, correct
    // then wrong then correct again within one shift) can finally be compared
    // side-by-side instead of inferred after the fact. Remove once the real trigger
    // is identified from this data.
    const isAdvanceOrLayawayCart = cartToDb?.type === "Layaway" || cartToDb?.layAway === "Y" || cartToDb?.docType === "Advance" || cartToDb?.isAdvance === "Y";
    const seqTrace = { branch: null, hasPreorderPrefix: null, seqType: null };
    if (documentNo) {
      resolvedDocumentNo = documentNo;
      seqTrace.branch = "explicit-documentNo-param";
    } else if (cart.isRetrived === "Y" || cart.type === "Layaway" || cart.isSplit === "Y" || cart?.statusType === "OCU" || cart?.isCreditSale === "Y") {
      const preorderPrefix = useSessionStore.getState().preorderSequence?.prefix;
      const hasPreorderPrefix = preorderPrefix && cart.documentno?.startsWith(preorderPrefix);
      seqTrace.hasPreorderPrefix = !!hasPreorderPrefix;
      seqTrace.preorderPrefixAtCheck = preorderPrefix || null;
      if (cart.documentno?.startsWith("PB/") || (hasPreorderPrefix && param !== "layaway")) {
        if (cart.documentno?.startsWith("PB/") && param === "layaway") {
          // First "Create Advance" on a retrieved PB/ preorder cart — use preorder sequence.
          // cartToDb.layAway is already "Y" at this point (set above), so resolveSequenceType returns "preorder".
          const seqType = resolveSequenceType(cartToDb, useSessionStore);
          seqTrace.seqType = seqType;
          if (seqType === "preorder") {
            const preSeq = useSessionStore.getState().preorderSequence;
            resolvedDocumentNo = generateDocumentNo(preSeq);
            seqTrace.branch = "retrieved-PB-layaway-preorder";
            // DI-05: consumption deferred to after the save actually succeeds — see the
            // block right after the order save below. Do NOT call
            // incrementPreorderSequence() here; an exception anywhere between here and
            // that save would otherwise burn this number for an order that was never
            // persisted (the catch block at the bottom of this function explicitly
            // handles "order was never saved" as a real, anticipated outcome).
            preorderConsumedSeq = (preSeq?.nextNo || 0) + 1;
            usedOwnSequence = true;
          } else {
            resolvedDocumentNo = `${tillData.tillAccess.cwrTill.prefix}${newDocumentSequence}`;
            seqTrace.branch = "retrieved-PB-layaway-regular-fallback";
          }
        } else {
          resolvedDocumentNo = `${tillData.tillAccess.cwrTill.prefix}${newDocumentSequence}`;
          usedOwnSequence = false;
          seqTrace.branch = "retrieved-complete-new-regular-number";
        }
      } else {
        resolvedDocumentNo = cart.documentno;
        seqTrace.branch = "retrieved-reuse-existing-documentno-as-is";
      }
    } else {
      const seqType = resolveSequenceType(cartToDb, useSessionStore);
      seqTrace.seqType = seqType;
      if (seqType === "preorder") {
        const preSeq = useSessionStore.getState().preorderSequence;
        resolvedDocumentNo = generateDocumentNo(preSeq);
        seqTrace.branch = "fresh-cart-preorder";
        // DI-05: see the matching comment in the branch above — consumption deferred
        // to after the save actually succeeds.
        preorderConsumedSeq = (preSeq?.nextNo || 0) + 1;
        usedOwnSequence = true;
      } else {
        resolvedDocumentNo = `${tillData.tillAccess.cwrTill.prefix}${newDocumentSequence}`;
        seqTrace.branch = "fresh-cart-regular";
      }
    }
    if (isAdvanceOrLayawayCart) {
      const preorderSeqNow = useSessionStore.getState().preorderSequence;
      logSequenceEvent(
        "advance-seq-race-diagnostic",
        {
          orderSaveCallId,
          sOrderID: cartToDb.sOrderID,
          resolvedDocumentNo,
          usedOwnSequence,
          ...seqTrace,
          param,
          isRetrived: cart.isRetrived,
          cartType: cart.type,
          isSplit: cart.isSplit,
          statusType: cart?.statusType,
          isCreditSale: cart?.isCreditSale,
          cartLayAway: cartToDb.layAway,
          cartIsAdvance: cartToDb.isAdvance,
          cartDocType: cartToDb.docType,
          preCartDocumentno: cart.documentno,
          preorderSequenceNow: preorderSeqNow ? { sequenceId: preorderSeqNow.sequenceId, prefix: preorderSeqNow.prefix, nextNo: preorderSeqNow.nextNo } : null,
          tillId: tillData?.tillAccess?.cwrTill?.cwrTillID || null,
          tillSessionId,
          orderTimeStamp: now.toISOString(),
        },
        db
      );
    }
    if (cartToDb.estimateId) {
      // BUG-DUPLICATE-ESTIMATE-BILL: temporary diagnostic — see matching entry log above.
      trace("EST-BILL", "resolvedDocumentNo determined", {
        estimateId: cartToDb.estimateId,
        isRetrived: cart.isRetrived,
        resolvedDocumentNo,
        newDocumentSequence,
      });
    }
    Object.assign(cartToDb, {
      orderTime: now.format("DD-MM-YYYY HH:mm:ss"),
      // BUG-2232: stamp the cashier who is actually logged in now, not the user
      // the till was last registered for (stale on a shared till).
      createdBy: resolveActingUserId(tillData),
      documentno: resolvedDocumentNo,
      orderType: orderType?.cwrSaletype?.cwrSaletypeId,
      // TSK-3249: order stays under the till's opening business date until store close,
      // not the live calendar date (matters when billing continues past midnight).
      orderDate: tillSession.businessDate ? dayjs(tillSession.businessDate).format("DD-MM-YYYY") : now.format("DD-MM-YYYY"),
      tillSessionId,
      key: uuidv4().replace(/-/g, "").toUpperCase(),
      isSynced: isSynced ? isSynced : 0,
      syncAttempts: 0,
      // Stamped here (not read later in syncOrders) because `param` -- the actual
      // signal for "which action produced this save" -- only exists as this
      // function's own parameter. syncOrders() only receives `syncTrigger`, which
      // is NOT the same thing (a 10s "auto" retry of a still-open Create Advance
      // order must not be reclassified as a completion just because the retry's
      // own trigger isn't "layaway"). Persisting the real intent onto the order
      // record itself lets every later retry read a stable, correct answer
      // instead of re-deriving it from a value it never had.
      isCompleteOrderAction: resolveIsCompleteOrderAction(param),
      giftCardData: [...giftCardData],
      // BUG-1963 Part B (F-5): cartToDb.isReturn, not the live cart's -- unconditional
      // in this Object.assign (same pattern as F-1's statusType above), so during
      // reconciliation this previously flipped the reconciled order's roundOff sign
      // based on whether an unrelated, currently-live sale happened to be a return.
      // BUG-2315 (TIC-10099): isReturn is usually the string "Y"/"N", not a boolean -- "N" is truthy
      // in JS, so this negated roundOff on every retrieved order (Layaway/Advance/
      // Credit-Sale completion, the only place isReturn is stamped as an explicit "N"
      // rather than left undefined), return or not. A fresh sale never explicitly sets
      // isReturn, so it stays undefined (falsy) and was never affected -- this only ever
      // bit retrieved orders. Matches the same isReturn === true || isReturn === "Y" check
      // already used a few lines above in this same function (F-3's payment-adjustment gate).
      roundOff: parseFloat(
        (parseFloat(cartToDb.isReturn === true || cartToDb.isReturn === "Y" ? cartToDb.roundOff * -1 : cartToDb.roundOff) || 0).toFixed(precision)
      ),
      discount: parseFloat((parseFloat(cartToDb.discount) || 0).toFixed(precision)),
      customerSearchKey: cartToDb.customer?.code,
      orderTimeDetails,
      totalQty: parseFloat((parseFloat(cartToDb.totalQty) || 0).toFixed(precision)),
      dineIn: useRestaurantStore.getState().dineIn || "Y",
      typeOrder: useRestaurantStore.getState().homeDelivery || "N",
      tableNo: tableData?.name || cartToDb.tableNo || "",
      // BUG-1963 Part B (F-1): read from cartToDb (already seeded from orderSnapshot
      // when reconciling an already-submitted order, see the cartToDb assignment
      // above) instead of the LIVE cart store -- the live cart can belong to a
      // different, newer sale by the time this reconciliation write executes,
      // which previously stamped the wrong order's statusType onto this one.
      statusType: cartToDb.statusType || "N",
      orderCompleted: cartToDb.statusType === "layAway" ? false : true,
      overPayedAmount: overPayedAmount || 0,
    });

    if (cartToDb.customer) {
      cartToDb.customer.retlLoyaltyBalance = Math.max(0, (cartToDb.customer.retlLoyaltyBalance || 0) + (cartToDb.accumulationPoints || 0));
    }

    // VDP/106665-70, CKM/103620-21: RCT/PAY logActivity entries earlier in this
    // cart's life (customer-attach, payment-add) were logged against whatever
    // documentno was showing as a preview then — captured here, before DI-31/DI-33
    // can change cartToDb.documentno/sOrderID, so the correction below can find
    // them by the identity they were actually logged under.
    const sOrderIDForLogCorrection = cartToDb.sOrderID;

    // DI-31: Soft duplicate documentno check — auto-recovers if collision found, never blocks.
    // PB/ and ADV/-prefixed completed bills generate a NEW regular number despite isRetrived=Y, so they must be checked.
    // Runs BEFORE DI-33 (below) so the sOrderID guard evaluates against the FINAL
    // documentno — otherwise DI-33 can pass using a documentno DI-31 is about to
    // change out from under it (RTN/100614→100618 gap).
    const isPBCompletion = cart.isRetrived === "Y" && cart.documentno?.startsWith("PB/");
    const preorderPrefixDI = useSessionStore.getState().preorderSequence?.prefix;
    const isAdvCompletion = cart.isRetrived === "Y" && preorderPrefixDI && cart.documentno?.startsWith(preorderPrefixDI);
    // A plain held/parked bill (isRetrived="Y" but NOT PB/ADV-prefixed — e.g. a cart put on
    // hold mid-checkout and resumed later) used to skip this check entirely, because the old
    // condition excluded every isRetrived="Y" cart except PB/ADV completions. resolvedDocumentNo
    // for it reuses cart.documentno as-is (~line 495 above) — a PREVIEW captured when the bill
    // was first held, not a reservation. If the till issues other sales while it sits on hold,
    // that previewed number gets legitimately consumed by someone else; resuming the held bill
    // later just reused it anyway (production: RAMS/21320, Al Rams RAK 5, 2026-07-28 — a cart
    // previewed RAMS/21320 at 10:51, was held, RAMS/21320 was legitimately issued to an unrelated
    // walk-in at 10:58, and the held cart was resumed and saved under the same stale RAMS/21320
    // at 15:10). Layaway/isSplit/OCU/isCreditSale keep their existing exclusion unchanged — their
    // identity legitimately carries across saves through a different, already-reviewed mechanism.
    const isLayawaySplitOcuCredit = cart.type === "Layaway" || cart.isSplit === "Y" || cart?.statusType === "OCU" || cart?.isCreditSale === "Y";
    if (!documentNo && (isPBCompletion || isAdvCompletion || !isLayawaySplitOcuCredit)) {
      try {
        const diSeqType = resolveSequenceType(cartToDb, useSessionStore);
        const diPrefix = diSeqType === "preorder" ? useSessionStore.getState().preorderSequence?.prefix : tillData.tillAccess.cwrTill.prefix;
        // TIC/BUG advance-seq-race diagnostic (temporary): this is a THIRD, independent
        // read/resolution of the same preorderSequence, after the initial resolvedDocumentNo
        // decision above. If a collision genuinely fires here (dupCheck.recovered) while
        // diSeqType has quietly flipped to "regular" for a documentno that was actually
        // preorder-prefixed, the recovery below rewrites cartToDb.documentno onto the
        // REGULAR sequence using the wrong prefix — a concrete, previously-unlogged way
        // a correctly-assigned advance number could get silently swapped for a wrong one.
        if (isAdvanceOrLayawayCart) {
          logSequenceEvent(
            "advance-seq-race-diagnostic-di31",
            {
              orderSaveCallId,
              sOrderID: cartToDb.sOrderID,
              documentnoBeforeDi31: cartToDb.documentno,
              diSeqType,
              diPrefix: diPrefix || null,
              seqTraceBranch: seqTrace.branch,
              seqTraceSeqType: seqTrace.seqType,
              seqTypeDisagreesWithResolution: !!(seqTrace.seqType && diSeqType && seqTrace.seqType !== diSeqType),
              isPBCompletion,
              isAdvCompletion,
            },
            db
          );
        }
        const dupCheck = await assertDocumentNoUnique({
          dbAdapter: db,
          documentno: cartToDb.documentno,
          sOrderID: cartToDb.sOrderID,
          prefix: diPrefix,
          sessionStore: useSessionStore,
          sequenceType: diSeqType,
        });
        if (dupCheck.recovered) {
          if (isAdvanceOrLayawayCart) {
            logSequenceEvent(
              "advance-seq-race-diagnostic-di31-recovered",
              { orderSaveCallId, sOrderID: cartToDb.sOrderID, from: cartToDb.documentno, to: dupCheck.documentno, diSeqType, diPrefix: diPrefix || null },
              db
            );
          }
          cartToDb.documentno = dupCheck.documentno;
          // Keep newDocumentSequence in sync with the recovery: every later
          // setDocumentSequence(newDocumentSequence)/preview-number call site in this
          // function reads this variable, so leaving it at its stale pre-recovery
          // value re-persisted that stale counter right after DI-31 corrected it —
          // guaranteeing the next order collides again (60x YWA / 5x CKM on 2026-07-16).
          if (diSeqType === "regular" && typeof dupCheck.recoveredSeq === "number") {
            newDocumentSequence = dupCheck.recoveredSeq;
          }
        }
      } catch (dupErr) {
        console.warn("[DI-31] Duplicate check failed (non-blocking):", dupErr?.message);
      }
    }

    // BUG-DUPLICATE-ESTIMATE-BILL: an estimate retrieval mints its own sOrderID+
    // documentno preview each time, so it's invisible to the sOrderID-scoped DI-33
    // check below — catch it by estimateId instead, across ALL orders. Same gap as
    // syncOrders' online path (~line 1568 above); this covers this store's
    // disableOfflineBilling !== "Y" path. Production incident: KKPPOS/5612 +
    // KKPPOS/5613, same s_estimate_id, two different sOrderIDs, 5 seconds apart.
    //
    // Called TWICE deliberately (here, and again immediately before the db.orders.put
    // below) — a plain read-then-write is not atomic, so a second concurrent call for
    // the same estimate can slip past this early check but still be caught by the late
    // re-check right before the write (confirmed via
    // tests/unit/bugs/duplicate_estimate_bill_race.test.js). Only the warn/reset logic
    // is shared here — the two separate db.orders.toArray() reads stay separate,
    // narrowing the race window is the whole point.
    const checkAndBlockDuplicateEstimate = async (label) => {
      if (!cartToDb.estimateId) return false;
      try {
        const allOrdersForEstimateCheck = await db.orders.toArray();
        const duplicateEstimateOrder = findDuplicateEstimateOrder(allOrdersForEstimateCheck, cartToDb.estimateId, cartToDb.sOrderID);
        if (duplicateEstimateOrder) {
          message.warning(`This estimate has already been billed as ${duplicateEstimateOrder.documentno}.`);
          setPaymentModal(false);
          setActiveOrderProcess(false);
          orderState.current = 0;
          return true;
        }
      } catch (estDupErr) {
        console.warn(`[DI-33] ${label} estimate dedup check failed (non-blocking):`, estDupErr?.message);
      }
      return false;
    };
    if (await checkAndBlockDuplicateEstimate("Early")) return;

    // DI-33: Stale cart identity guard — last line of defense. If a reload restored
    // a pre-checkout cart snapshot (sqliteStorage 100ms debounce window), this sale
    // may carry a sOrderID already consumed by a DIFFERENT saved order. Saving it
    // would overwrite that order locally (db.orders keyed on sOrderID) and be
    // silently deduped by the backend (200 with no record → bill lost but marked
    // synced). Regenerate sOrderID only: referenceId may already be anchored to a
    // completed EDC/UPI payment for THIS sale, so it is left intact here — the
    // hydration-time guards reissue it before any payment can reference it.
    //
    // Runs regardless of isSynced: cartToDb above is always read from the live
    // cart store, never from server-pushed data, so isSynced=1 here only means
    // "this local record's server round-trip succeeded" — it says nothing about
    // whether cartToDb's IDENTITY is still the order being confirmed. The four
    // isSynced=1 callers (layaway/regular sync-confirmation success handlers)
    // used to skip this check entirely, leaving them with zero identity
    // protection and zero audit trail — found auditing every db.orders write
    // path after confirming a related, separately-fixed overwrite (cartIdentityGuard.js's
    // Layaway exemption, S1S/39378 -> CS1S CAN/4, 2026-07-28) went through a
    // different, guarded-but-too-permissive call site. This path had no guard
    // at all and is closed here defensively, not because it was proven to be
    // this incident's actual cause.
    {
      try {
        const existingSameId = await db.orders.get(cartToDb.sOrderID);
        const preorderPrefix33 = useSessionStore.getState().preorderSequence?.prefix;

        // TIC/BUG advance-seq-race diagnostic (temporary): re-reads preorderSequence
        // a second time within this same order-save call (the first read is up in the
        // resolvedDocumentNo block above, captured as seqTrace.preorderPrefixAtCheck).
        // If these two reads of the SAME session-store field ever disagree within one
        // function execution, that's direct proof of a concurrent mutation racing this
        // save — the mechanism the CPD/CPAW/CPF same-session correct-then-wrong-then-
        // correct pattern points to. Remove alongside the diagnostic above.
        if (isAdvanceOrLayawayCart) {
          logSequenceEvent(
            "advance-seq-race-diagnostic-di33",
            {
              orderSaveCallId,
              sOrderID: cartToDb.sOrderID,
              documentnoAtDi33: cartToDb.documentno,
              preorderPrefixAtDi33: preorderPrefix33 || null,
              preorderPrefixAtResolution: seqTrace.preorderPrefixAtCheck || null,
              prefixReadsDisagree: !!(seqTrace.preorderPrefixAtCheck && preorderPrefix33 && seqTrace.preorderPrefixAtCheck !== preorderPrefix33),
              resolutionBranch: seqTrace.branch,
              existingSameIdFound: !!existingSameId,
              existingDocumentno: existingSameId?.documentno || null,
            },
            db
          );
        }

        // Use cartToDb (live store read at function entry), not the `cart` hook prop —
        // `cart` is a React-closure value that can lag behind the store across the
        // several awaits already run in this function (assertOrderTotals/DI-31/etc).
        // A stale `cart` here can carry a DIFFERENT transaction's total/items/exemption
        // flags (isRetrived/type/isSplit/statusType/isCreditSale), making the guard
        // wrongly conclude "same order, no need to regenerate" for a genuinely new sale
        // reusing an old sOrderID — exactly the overwrite this guard exists to prevent.
        if (shouldRegenerateIdentityAtSave({ existingOrder: existingSameId, cart: cartToDb, documentno: cartToDb.documentno, preorderPrefix: preorderPrefix33 })) {
          const staleSOrderID = cartToDb.sOrderID;
          cartToDb.sOrderID = uuidv4().replace(/-/g, "").toUpperCase();

          // A stale sOrderID can carry a stale documentno with it (Layaway/isSplit/OCU/
          // isCreditSale carts resolve resolvedDocumentNo straight from cart.documentno,
          // ~line 495/101 above — they never mint a fresh number). Regenerating only the
          // sOrderID stops this cart from overwriting existingSameId's row, but if we stop
          // there this NEW sale still saves under existingSameId's OLD invoice number — two
          // intact, different sales sharing one documentno (production: RAK2/42569,
          // S2AZ/19000, both logged with existingDocumentno === documentno, i.e. this exact
          // gap). Re-run the same soft, non-blocking uniqueness check DI-31 already uses
          // above, now that sOrderID is fresh so it can actually see the collision — a
          // genuine, non-stale continuation never reaches this branch (shouldRegenerateIdentityAtSave
          // already returned false for it), so this cannot affect any legitimate save.
          const preorderPrefixDocRecovery = preorderPrefix33;
          const docRecoverySeqType = preorderPrefixDocRecovery && cartToDb.documentno?.startsWith(preorderPrefixDocRecovery) ? "preorder" : "regular";
          const docRecoveryPrefix = docRecoverySeqType === "preorder" ? preorderPrefixDocRecovery : tillData.tillAccess.cwrTill.prefix;
          try {
            const docDupCheck = await assertDocumentNoUnique({
              dbAdapter: db,
              documentno: cartToDb.documentno,
              sOrderID: cartToDb.sOrderID,
              prefix: docRecoveryPrefix,
              sessionStore: useSessionStore,
              sequenceType: docRecoverySeqType,
            });
            if (docDupCheck.recovered) {
              cartToDb.documentno = docDupCheck.documentno;
              if (docRecoverySeqType === "regular" && typeof docDupCheck.recoveredSeq === "number") {
                newDocumentSequence = docDupCheck.recoveredSeq;
              }
            }
          } catch (docDupErr) {
            console.warn("[DI-33] Post-regeneration documentno uniqueness check failed (non-blocking):", docDupErr?.message);
          }

          console.warn("[DI-33] Stale sOrderID detected at save — regenerated to prevent order overwrite.", {
            staleSOrderID,
            newSOrderID: cartToDb.sOrderID,
            documentno: cartToDb.documentno,
            overwrittenDocumentno: existingSameId.documentno,
          });
          logSequenceEvent(
            "stale-identity-recovery",
            {
              documentno: cartToDb.documentno,
              staleSOrderID,
              newSOrderID: cartToDb.sOrderID,
              existingDocumentno: existingSameId.documentno,
              flow: "processOrderApproved",
            },
            db
          );
        } else if (existingSameId) {
          // An existing row under this sOrderID was found but the guard chose NOT to
          // regenerate — log why, so a future overwrite can be diagnosed from real
          // evaluation data instead of guessing (this exact ambiguity cost significant
          // time to narrow down for VDP/CKM/YWA/HSN/RTN/PRP historical incidents).
          logSequenceEvent(
            "stale-identity-exempted",
            {
              documentno: cartToDb.documentno,
              sOrderID: cartToDb.sOrderID,
              existingDocumentno: existingSameId.documentno,
              sameDocumentno: existingSameId.documentno === cartToDb.documentno,
              existingTotal: existingSameId.total,
              cartTotal: cartToDb.total,
              existingItemCount: existingSameId.items?.length || 0,
              cartItemCount: cartToDb.items?.length || 0,
              type: cartToDb.type,
              isSplit: cartToDb.isSplit,
              statusType: cartToDb.statusType,
              isCreditSale: cartToDb.isCreditSale,
              isRetrived: cartToDb.isRetrived,
              flow: "processOrderApproved",
            },
            db
          );
        }
      } catch (diErr) {
        console.warn("[DI-33] Stale identity check failed (non-blocking):", diErr?.message);
      }
    }

    correctLogPreviewDocumentNo({ sOrderID: sOrderIDForLogCorrection, finalDocumentNo: cartToDb.documentno, dbAdapter: db }).catch(() => {});

    // BUG-1965: self-heal a sub-₹1 header/line drift on the line side (never
    // cart.total — see reconcileOrderTotalDrift's docblock for why) BEFORE the
    // per-item transform loop below derives linenet/linetax/taxDetails from
    // item.nettotal, so those derived fields come out already consistent.
    // Skipped when isSynced (server-confirmed order being locally cached) for
    // the same reason DI-01 below skips it — matches BUG-1115's rationale.
    let driftCorrected = false;
    if (!isSynced) {
      driftCorrected = reconcileOrderTotalDrift(cartToDb, "processOrderApproved", db).corrected;
    }

    for (let i = 0; i < cartToDb.items.length; i++) {
      const item = cartToDb.items[i];
      item.discount = item.discount ? parseFloat(parseFloat(item.discount)?.toFixed(precision)) : 0;
      item.key = i;
      item.nettotal = parseFloat(parseFloat(item.nettotal)?.toFixed(precision));
      if (!item.isGiftCard) {
        const taxRate = Number(item.taxRate) || 0;
        const sunitprice = Number(item.sunitprice) || 0;
        const weight = Number(item.weight) || 1;
        const lineTax = Number(item.listPrice || 0) * (1 + taxRate / 100);
        const stdTax = sunitprice * (1 + taxRate / 100);
        const unitPrice =
          isFinite(item.nettotal / weight) && weight !== 0 ? (taxIncludeFlag === "Y" ? item.nettotal / weight / (1 + taxRate / 100) : Number(item.salePrice) || 0) : 0;
        const gross_list = taxIncludeFlag === "Y" ? item.listPrice : lineTax;
        const netList =
          taxIncludeFlag === "Y" ? item.listPrice - parseFloat(parseFloat(item.listPrice - item.listPrice / (1 + item.taxRate / 100))?.toFixed(precision)) : item.listPrice;
        const grossStd = taxIncludeFlag === "Y" ? item.sunitprice : stdTax;
        Object.assign(item, {
          listPrice: taxIncludeFlag === "Y" ? item.listPrice : parseFloat(parseFloat(lineTax)?.toFixed(precision)),
          linetax: Math.abs(item.nettotal) > 0 ? parseFloat(parseFloat(item.taxAmount)?.toFixed(precision)) : 0,
          linenet: Math.abs(item.nettotal) > 0 ? parseFloat(parseFloat(item.nettotal - item.taxAmount)?.toFixed(precision)) : 0,
          linestd: Math.abs(item.nettotal) > 0 ? parseFloat(parseFloat(grossStd * item.weight)?.toFixed(precision)) : 0,
          linegross: Math.abs(item.nettotal) > 0 ? item.nettotal : 0,
          netunit: Math.abs(item.nettotal) > 0 ? parseFloat(parseFloat(unitPrice)?.toFixed(precision)) : 0,
          listprice: Math.abs(item.nettotal) > 0 ? item.listPrice : 0,
          grossunit: Math.abs(item.nettotal) > 0 ? parseFloat(parseFloat(unitPrice)?.toFixed(precision)) * (item.isReturn ? -1 : 1) : 0,
          grossstd: Math.abs(item.nettotal) > 0 ? roundTo(grossStd, precision) : 0,
          grosslist: Math.abs(item.nettotal) > 0 ? gross_list : 0,
          netList: Math.abs(item.nettotal) > 0 ? netList : 0,
          unitPrice: Math.abs(item.nettotal) > 0 ? unitPrice : 0,
          taxAmount: Math.abs(item.nettotal) > 0 ? parseFloat(parseFloat(item.taxAmount)?.toFixed(precision)) : 0,
        });
      }

      // BUG-2043 (reopened): this loop is the last chance to make the persisted order
      // self-consistent, and it only ever walked TOP-LEVEL items. Grouped / Custom Mix lines are
      // exported as their children (see the orderLines builders below), so a child never
      // re-derived here keeps whatever linegross/linenet/linetax it was stamped with at
      // mix-build time. That asymmetry is why the drift showed up on bundle orders only: plain
      // lines were self-healed here, children never were. Uses the same shared helper the
      // pricing rules use so there is one definition of a line's monetary fields.
      if (Array.isArray(item.children) && item.children.length > 0) {
        for (const child of item.children) {
          if (child.isGiftCard) continue;
          child.discount = child.discount ? parseFloat(parseFloat(child.discount)?.toFixed(precision)) : 0;
          child.nettotal = parseFloat(parseFloat(child.nettotal)?.toFixed(precision));
          enrichLineItemFields(child, taxIncludeFlag, precision);
        }
      }
    }

    const isInterState = getIsInterState(cartToDb);

    // BUG-1965 follow-up: reconcileOrderTotalDrift above corrects item.nettotal,
    // and the loop just above re-derives linenet/linetax/taxDetails from that —
    // but cartToDb.groupedItemsByTaxRate/subTotal were already computed earlier
    // in cart.js, live during cart editing, and nothing re-ran them after the
    // correction. Both the receipt printer (CWHardwareController.js, prefers a
    // non-empty precomputed groupedItemsByTaxRate over recomputing fresh) and
    // subTotal display consumers were left holding the pre-correction figures —
    // a real order (Chocolala test, CMO/16314) showed the printed taxable amount
    // (105.948, stale) diverge from the amount actually synced to the backend
    // (106.703, from the corrected line). Re-derive both from the now-corrected
    // items so every consumer agrees.
    if (driftCorrected) {
      cartToDb.groupedItemsByTaxRate = groupItemsByTaxRate(
        cartToDb.items.filter((item) => item.weight !== 0),
        precision,
        { isInterState }
      );
      cartToDb.subTotal = cartToDb.groupedItemsByTaxRate.reduce((sum, group) => sum + (Number(group.totalLinenet) || 0), 0);
    }

    const taxDetails = [];
    const taxMap = {};
    for (const item of cartToDb.items) {
      if (!item.isGiftCard) {
        if (item.taxRate >= 0 && item.tax) {
          const tr = item.taxRate;
          const resolvedTaxId = isInterState && item.interTaxId ? item.interTaxId : item.tax;
          const ta = parseFloat(parseFloat(item.taxAmount)?.toFixed(precision));
          const nt = parseFloat(parseFloat(item.nettotal - item.taxAmount)?.toFixed(precision));
          if (taxMap[tr]) {
            taxMap[tr].taxAmount += ta;
            taxMap[tr].nettotal += nt;
          } else {
            taxMap[tr] = { taxAmount: ta, nettotal: nt, tax: resolvedTaxId, taxRate: tr, taxCategory: item.taxCategory, taxName: item.taxName };
          }
        }
        if (!item.taxRate && item.children?.length > 0) {
          for (const child of item.children) {
            if (child.taxRate && child.tax) {
              const tr = child.taxRate;
              const resolvedChildTaxId = isInterState && child.interTaxId ? child.interTaxId : child.tax;
              const ta = parseFloat(parseFloat(child.taxAmount)?.toFixed(precision));
              const nt = parseFloat(parseFloat(child.nettotal - child.taxAmount)?.toFixed(precision));
              if (taxMap[tr]) {
                taxMap[tr].taxAmount += ta;
                taxMap[tr].nettotal += nt;
              } else {
                taxMap[tr] = { taxAmount: ta, nettotal: nt, tax: resolvedChildTaxId, taxRate: tr, taxCategory: child.taxCategory, taxName: child.taxName };
              }
            }
          }
        }
      }
    }
    cartToDb.combinedTaxRates = Object.keys(taxMap).map((taxRate) => {
      const e = taxMap[taxRate];
      taxDetails.push({ csTaxID: e.tax, taxableAmt: e.nettotal, taxAmt: e.taxAmount, taxRate, taxCategory: e.taxCategory, taxName: e.taxName });
      return { csTaxID: e.tax, taxableAmt: e.nettotal, taxAmt: e.taxAmount };
    });
    cartToDb.taxDetails = taxDetails;

    localStorage.removeItem("orderTimeDetails");
    setGiftCardData([]);
    if (initialShowPayments && setShowPayments) setShowPayments(initialShowPayments);
    if (setStartIndex) setStartIndex(initialShowPayments.length);
    if (setSelectedDate) setSelectedDate(null);

    const existingOrders = await db.orders.where("tillSessionId").equals(tillSessionId).toArray();
    {
      const orders = existingOrders;
      const newOrder = {
        salesTax: [],
        returnTax: [],
        items: [],
        netTotal: 0,
        grossTotal: 0,
        totalTax: 0,
        netReturnTotal: 0,
        grossReturnTotal: 0,
        totaReturnlTax: 0,
        returnsTransactions: 0,
        salesTransactions: 0,
      };
      let orderExists = false;
      if (orders.length > 0) {
        for (let i = 0; i < orders.length; i++) {
          if (orders[i].sOrderID === cartToDb.sOrderID) {
            orders[i] = { ...cartToDb };
            orderExists = true;
            break;
          }
        }
        if (!orderExists) orders.push({ ...cartToDb });
        for (const order of orders) await processOrderData(order, newOrder, orders);
      } else {
        await processOrderData({ ...cartToDb }, newOrder, orders);
      }
      await db.ordersData
        .where("tillSessionId")
        .equals(tillSessionId)
        .modify((order) => {
          Object.assign(order, { tillSessionId, tillStatus: "open", ...newOrder });
        });
    }

    async function processOrderData(orderDetails, newOrder, allOrders) {
      const groupedItems = newOrder.items;
      const targetTaxArray = orderDetails.isReturn ? newOrder.returnTax : newOrder.salesTax;
      for (const item of orderDetails.items) {
        const idx = groupedItems.findIndex((ele) => ele.value === item.value);
        if (idx >= 0) {
          groupedItems[idx].taxAmount = +parseFloat(parseFloat(groupedItems[idx].taxAmount + item.taxAmount)?.toFixed(precision));
          groupedItems[idx].nettotal = +parseFloat(parseFloat(groupedItems[idx].nettotal + item.nettotal)?.toFixed(precision));
          // BUG-1864: isQtyDesimal is a precision digit count where 0 (unit-based products) is a
          // valid value — `||` treats 0 as falsy and wrongly promotes these to 2-decimal rounding.
          groupedItems[idx].weight = parseFloat((parseFloat(groupedItems[idx].weight) + parseFloat(item.weight))?.toFixed(item?.isQtyDesimal ?? 2));
        } else {
          groupedItems.push({
            taxAmount: +parseFloat(parseFloat(item.taxAmount)?.toFixed(precision)),
            nettotal: +parseFloat(parseFloat(item.nettotal)?.toFixed(precision)),
            weight: parseFloat(parseFloat(item.weight)?.toFixed(item?.isQtyDesimal ?? 2)),
            value: item.value,
            name: item.name,
          });
        }
      }
      for (const newTax of orderDetails.taxDetails || []) {
        const existingTax = targetTaxArray.find((tax) => tax.taxRate === newTax.taxRate);
        if (existingTax) {
          existingTax.taxAmt = +parseFloat(parseFloat(existingTax.taxAmt + newTax.taxAmt)?.toFixed(precision));
          existingTax.taxableAmt = +parseFloat(parseFloat(existingTax.taxableAmt + newTax.taxableAmt)?.toFixed(precision));
        } else {
          targetTaxArray.push({
            csTaxID: newTax.csTaxID,
            taxableAmt: parseFloat(parseFloat(newTax.taxableAmt)?.toFixed(precision)),
            taxAmt: parseFloat(parseFloat(newTax.taxAmt)?.toFixed(precision)),
            taxRate: newTax.taxRate,
            taxCategory: newTax.taxCategory,
            taxName: newTax.taxName,
          });
        }
      }
      const isReturn = orderDetails.isReturn === true || orderDetails.isReturn === "Y";
      const orderIndex = allOrders.findIndex((o) => o.sOrderID === orderDetails.sOrderID);
      if (isReturn) {
        newOrder.returnTax = targetTaxArray;
        newOrder.netReturnTotal = +parseFloat(parseFloat((newOrder.netReturnTotal || 0) + (orderDetails.total - orderDetails.tax))?.toFixed(precision));
        newOrder.grossReturnTotal = +parseFloat(parseFloat((newOrder.grossReturnTotal || 0) + orderDetails.total)?.toFixed(precision));
        newOrder.totaReturnlTax = +parseFloat(parseFloat((newOrder.totaReturnlTax || 0) + orderDetails.tax)?.toFixed(precision));
        newOrder.returnsTransactions += 1;
        upsertPOSLog(orderDetails, "SLR");
      } else {
        newOrder.salesTax = targetTaxArray;
        newOrder.netTotal = +parseFloat(parseFloat((newOrder.netTotal || 0) + (orderDetails.total - orderDetails.tax))?.toFixed(precision));
        newOrder.grossTotal = +parseFloat(parseFloat((newOrder.grossTotal || 0) + orderDetails.total)?.toFixed(precision));
        newOrder.totalTax = +parseFloat(parseFloat((newOrder.totalTax || 0) + orderDetails.tax)?.toFixed(precision));
        newOrder.salesTransactions += 1;
      }
      if (orderIndex !== -1) {
        const advPay = parseFloat(orderDetails.advancePayment || 0);
        newOrder.netTotal = +parseFloat(parseFloat(newOrder.netTotal - advPay)?.toFixed(precision));
        newOrder.grossTotal = +parseFloat(parseFloat(newOrder.grossTotal - advPay)?.toFixed(precision));
      }
    }

    // BUG-1963 Part B (F-6): this till-session payment-totals aggregation (Expected
    // Amount/Amount/Payment Return/Change feeding cash-up and day-end reports) was
    // reading the LIVE cart's payments/total/change instead of cartToDb -- unconditional,
    // so during reconciliation it added whatever sale was live on the till right now into
    // the till's cumulative totals, once per reconciled order in the sync batch, while the
    // [DI-09] audit entry below already labels itself with cartToDb.sOrderID/documentno --
    // it was just filling in the wrong order's totals under the right order's identity.
    await db.paymentsData
      .where("tillSessionId")
      .equals(tillSessionId)
      .modify((event) => {
        const filteredPayments = event.payments.filter((payment) => payment?.cwrTillId === tillId);
        for (const payment of filteredPayments) {
          let currPaymentAmount = 0;
          let currPaymentReturn = 0;
          for (const orderPayment of cartToDb.payments || []) {
            if (payment.name.toLowerCase() === orderPayment.name.toLowerCase()) {
              if (cartToDb.total > 0) {
                currPaymentAmount += parseFloat(orderPayment.amount);
                payment.transactionCount = (payment.transactionCount || 0) + 1;
              } else if (cartToDb.total < 0) {
                currPaymentReturn += parseFloat(Math.abs(orderPayment.amount));
                payment.transactionCount = (payment.transactionCount || 0) + 1;
              }
            }
          }
          payment.expectedAmount = (payment.expectedAmount || 0) + currPaymentAmount;
          payment.amount = (payment.amount || 0) + currPaymentAmount;
          payment.paymentReturn = (payment.paymentReturn || 0) + currPaymentReturn;
          payment.difference = (payment.difference || 0) + payment.expectedAmount;
          payment.change = (payment.change || 0) + cartToDb.change;
        }
        event.payments = filteredPayments;

        // [DI-09] Link payment records to their order via sOrderID for audit traceability
        if (!event.orderPayments) event.orderPayments = [];
        event.orderPayments.push({
          sOrderID: cartToDb.sOrderID,
          documentno: cartToDb.documentno,
          orderTotal: cartToDb.total,
          payments: (cartToDb.payments || []).map((p) => ({
            name: p.name,
            amount: parseFloat(p.amount) || 0,
            amountTendered: p.amountTendered != null ? parseFloat(p.amountTendered) : undefined,
            finPaymentmethodId: p.finPaymentmethodId,
          })),
          change: cartToDb.change || 0,
          timestamp: new Date().toISOString(),
        });
      });

    if (setPaymentProcessFlag) setPaymentProcessFlag(false);

    // DI-S1: Advisory stock check at final submit — fire-and-forget, console-only, no UI alert.
    if (!isSynced && (posConfig?.enableStockCheck === "N" || !posConfig?.enableStockCheck) && navigator.onLine && posConfig?.disableOfflineBilling !== "Y") {
      reVerifyStockAtCheckout({
        cart: cartToDb,
        verifyStockService,
        tillData,
      })
        .then((stockResult) => {
          if (!stockResult.allAvailable && stockResult.warnings.length > 0) {
            console.warn("[DI-S1] Stock warnings at checkout:", stockResult.warnings);
          }
        })
        .catch((stockErr) => {
          console.warn("[DI-S1] Advisory stock check failed:", stockErr?.message);
        });
    }

    // DI-01: Validate cart total vs line-item sum before persisting to DB.
    // When isSynced=1 the order was already confirmed by the server — the local
    // save is just a cache/reconciliation step, so don't block on validation.
    if (!isSynced) {
      const totalCheck = assertOrderTotals(cartToDb, "processOrderApproved", db);
      if (!totalCheck.valid) {
        message.error(`Cannot save order: ${totalCheck.message}`);
        orderState.current = 0;
        setActiveOrderProcess(false);
        return;
      }
    }

    // DI-37 (TIC-9393): purely diagnostic, never blocks — DI-01 above only catches
    // a header/line-sum mismatch, which this defect does NOT produce (cart.total is
    // built FROM the same frozen nettotal, so header and line stay consistent with
    // each other, just both wrong). Runs unconditionally, including isSynced=1
    // reconciliation saves, so a background-sync re-save can still surface it.
    try {
      assertLineWeightScaling(cartToDb, "processOrderApproved", db);
    } catch (di37Err) {
      console.warn("[DI-37] Line weight-scaling check failed (non-blocking):", di37Err?.message);
    }

    // Only reset once DI-01 has confirmed this save is actually going through --
    // a save DI-01 blocked above already returned, so nothing below this point
    // runs for it, and the UI correctly keeps showing whatever it showed before.
    //
    // Unconditional on `param` (both "Create Advance" and "Complete Order"
    // reset to "Invoice"): a prior version of this line skipped the reset when
    // param === "layaway", on the theory that Create Advance "must leave the
    // order open" on screen. That's false under this function's own cart-reset
    // logic a few hundred lines below (~1504-1567): it puts a brand-new blank
    // cart (docType: "Invoice") on screen after ANY completed save, Create
    // Advance included, same as a regular checkout -- a parked Advance order is
    // continued later by retrieving it from Parked Bills/Sales History, not by
    // leaving it live on the current screen. Skipping the reset here while the
    // cart was being wiped there desynced the two: screen showed a blank
    // "Invoice" cart with the header still reading "Advance" indefinitely, since
    // nothing else in the app flips selectedDocumentType back on its own.
    if (setSelectedDocumentType) setSelectedDocumentType("Invoice");

    // Durable, append-only record of what's about to be written — survives even if
    // db.orders later gets overwritten by a future stale-identity reuse (RTN/100634:
    // a real save vanished with no trace once its row was overwritten in place).
    logSequenceEvent(
      "pre-save-snapshot",
      {
        documentno: cartToDb.documentno,
        sOrderID: cartToDb.sOrderID,
        total: cartToDb.total,
        itemCount: cartToDb.items?.length || 0,
        flow: "processOrderApproved",
      },
      db
    );

    // TSK-2935: stamp preparedBillOrigin onto the persisted row itself, not just the
    // live cart below — handleExistingOrder (useOrderHandlers.js) looks this field up
    // directly from db.orders when a table is reselected, specifically so it can tell a
    // Prepare-Bill-confirmed order apart from a stale KOT-fire snapshot. Setting it only
    // on the live cart left that lookup unable to ever find it.
    if (cartToDb.isPrepareBillCreate === "Y") {
      cartToDb.preparedBillOrigin = "Y";
    }

    try {
      // BUG-DUPLICATE-ESTIMATE-BILL: re-check immediately before the write, using the
      // same checkAndBlockDuplicateEstimate helper as the early check above (see its
      // comment for why this runs twice). The first check runs before DI-33/tax/table
      // lookups below it, each an awaited DB round-trip — a second call for the same
      // estimate that starts after that first check but before this write can still
      // slip past it, since a plain read-then-write is not atomic (confirmed via
      // tests/unit/bugs/duplicate_estimate_bill_race.test.js: two calls racing with
      // matching I/O latency both passed the early check). Re-reading right here
      // shrinks the window to the true minimum — the read-to-write gap of this one
      // statement — instead of this function's full body.
      if (await checkAndBlockDuplicateEstimate("Late")) return;
      await db.orders.put(cartToDb);
      // DI-05: finalize preorder-sequence consumption only now that the save is confirmed
      // to have gone through — mirrors the regular documentSequence counter's existing
      // deferred-persist pattern (see the `!usedOwnSequence` branches below). Math.max-guarded
      // (only ever moves nextNo forward) so a DI-31/DI-33 recovery that already advanced
      // preorderSequence further, to skip a genuine collision, is never regressed backward.
      if (usedOwnSequence && preorderConsumedSeq != null) {
        const currentPreSeq = useSessionStore.getState().preorderSequence;
        if (currentPreSeq && preorderConsumedSeq > (currentPreSeq.nextNo || 0)) {
          useSessionStore.getState().setPreorderSequence({ ...currentPreSeq, nextNo: preorderConsumedSeq });
          logSequenceEvent(
            "preorder-seq-write",
            { orderSaveCallId, source: "di05-deferred-consume", value: { ...currentPreSeq, nextNo: preorderConsumedSeq }, documentno: cartToDb.documentno },
            db
          );
        } else {
          // TIC/BUG advance-seq-race diagnostic (temporary): this order resolved to
          // "preorder" and expected to commit preorderConsumedSeq here, but didn't —
          // either currentPreSeq is null (config vanished between resolution and this
          // deferred commit, a direct race hit) or the Math.max guard rejected it
          // (something else already advanced nextNo past this order's number). Either
          // way, this is exactly the divergence point worth seeing on the next failure.
          logSequenceEvent(
            "preorder-seq-consume-skipped",
            {
              orderSaveCallId,
              documentno: cartToDb.documentno,
              sOrderID: cartToDb.sOrderID,
              preorderConsumedSeq,
              currentPreSeqAtCommit: currentPreSeq || null,
              reason: !currentPreSeq ? "preorderSequence-null-at-commit" : "guard-rejected-not-forward",
            },
            db
          );
        }
      }
      logSequenceEvent(
        "consume",
        {
          orderSaveCallId,
          documentno: cartToDb.documentno,
          sOrderID: cartToDb.sOrderID,
          seqBefore: documentSequence,
          seqAfter: newDocumentSequence,
          usedOwnSequence,
          isRetrived: cart.isRetrived === "Y",
          type: cart.type || "",
        },
        db
      );

      // TSK-2935: Prepare Bill creates a real, server-confirmed order early so the final
      // invoice can carry the same document number. Unlike every other path below, the
      // table's order is NOT finished — the customer hasn't paid yet — so stamp the
      // confirmed identity onto the live cart and stop here, before the table-cleanup/
      // cart-reset logic every other branch of this function runs on order completion.
      if (cartToDb.isPrepareBillCreate === "Y") {
        setCart((prev) => ({
          ...prev,
          documentno: cartToDb.documentno,
          sOrderID: cartToDb.sOrderID,
          preparedBillOrigin: "Y",
          isPrepareBillCreate: "N",
          layAway: "N",
        }));
        setPaymentModal(false);
        setActiveOrderProcess(false);
        orderState.current = 0;
        window.dispatchEvent(new CustomEvent("cd:feedbackDone"));
        return;
      }

      // Immediately try to sync this order — fire-and-forget, don't block the user.
      // The 10-second interval acts as a retry for any that fail here.
      if (posConfig?.disableOfflineBilling !== "Y") {
        syncOrders(param).catch((err) => console.debug("[OrderSync] Immediate sync failed, will retry:", err.message || err));
      }
      setPaymentModal(false);
      const posConfigLocal = usePOSConfigStore.getState().posConfig;
      const takeAway = useRestaurantStore.getState().dineIn;
      const isCallCenter = isCallCenterFlow && tillLayout === 2;
      if (takeAway === "Y" && !isCallCenter) {
        // BUG (BHRS/1789 duplicate billing): this used to resolve the table to clean up
        // via useRestaurantStore.getState().tableName — the CURRENTLY selected table in the
        // UI, read fresh at this point. processOrderApproved can run asynchronously well
        // after the payment click (for disableOfflineBilling="Y" it's invoked from inside
        // syncOrders, after an awaited backend round-trip), so by the time this line runs
        // the cashier's screen selection can have moved to a different table. That silently
        // pointed cleanup at the wrong (or a since-cleared) table, leaving the real table's
        // fbOrderData row stuck at fbOrderStatus "IP" forever — table kept showing the fired
        // items with Pay Bill still live, so a second click resubmitted the same order.
        // cartToDb.tableId is stamped once at table-select time and travels with the order
        // itself, so it stays correct regardless of what the UI has moved on to since.
        let tblData = useRestaurantStore.getState().tableName || {};
        const orderTableId = cartToDb.tableId || tblData?.cwrFbTableId;
        if (orderTableId !== tblData?.cwrFbTableId) tblData = { ...tblData, cwrFbTableId: orderTableId };
        const tableRecord = await db.tableData.where("cwrFbTableId").equals(orderTableId).first();
        if (tableRecord) {
          const isMerged = Array.isArray(tableRecord.merge) && tableRecord.merge.length > 0;
          if (isMerged) {
            await cleanupMergedTables(tableRecord, cartToDb.sOrderID);
          } else {
            if (tableRecord.isSplitParent === "Y") {
              const splitName = tableData.splitTable || tableData.tableName;
              const updatedSplitTables = tableRecord.splitTables.map((st) =>
                st.splitTable === splitName
                  ? {
                      ...st,
                      status: "OPN",
                      ordered: "N",
                      cart: {
                        items: [],
                        couponInput: [],
                        advancePayment: 0,
                        total: 0,
                        tax: 0,
                        discount: 0,
                        paid: 0,
                        change: 0,
                        totalQty: 0,
                        roundOff: 0,
                        payments: [],
                        redemptionPoints: 0,
                        accumulationPoints: 0,
                        creditAmount: 0,
                        docType: "Invoice",
                        sOrderID: uuidv4().replace(/-/g, "").toUpperCase(),
                        referenceId: uuidv4().replace(/-/g, "").toUpperCase(),
                        giftCardRefId: uuidv4().replace(/-/g, "").toUpperCase(),
                        couponRefId: uuidv4().replace(/-/g, "").toUpperCase(),
                        customer: selectedSaleTypeData?.customer?.b2cCustomerId ? selectedSaleTypeData.customer : defaultCustomer,
                        salesRepId: null,
                        cardPaymentData: {},
                        // Must use newDocumentSequence: documentSequence is the stale pre-save
                        // counter, so +1 here re-issued the number just consumed by this order,
                        // causing a later duplicate collision and a DI-31 sequence jump.
                        documentno: `${tillData.tillAccess.cwrTill.prefix}${newDocumentSequence + 1}`,
                        userName: userData?.user || "",
                        tillName: tillValue?.name,
                        subTotal: 0,
                        itemsSummery: {},
                        amountInWords: "",
                        groupedItemsByTaxRate: {},
                        serviceCharge: 0,
                        tip: 0,
                        deiveryCharge: 0,
                        packingCharge: 0,
                        others: 0,
                      },
                    }
                  : st
              );
              await db.tableData.update(tableRecord.cwrFbTableId, { splitTables: updatedSplitTables });
              const allFbOrders2 = await db.fbOrderData.toArray();
              const fbOrders2 = allFbOrders2.filter((o) => o.splitTable === splitName);
              await cleanupSplitTable(fbOrders2, cartToDb.sOrderID);
              await SyncData(
                {
                  fbtableStatusId: tableRecord.fbtableStatusId,
                  cwrFbTableId: tableRecord.cwrFbTableId,
                  splitTables: updatedSplitTables,
                  statusType: tableRecord.statusType,
                  waiter: tableRecord.waiter,
                },
                "upsertTableStatus"
              );
              // Full reset with new IDs — spreading ...cart kept the completed order's
              // sOrderID and documentno alive in SQLite, which caused stale items to
              // re-appear when the split table was reopened.
              const freshSplitCart = {
                items: [],
                couponInput: [],
                advancePayment: 0,
                total: 0,
                tax: 0,
                discount: 0,
                paid: 0,
                change: 0,
                totalQty: 0,
                roundOff: 0,
                payments: [],
                redemptionPoints: 0,
                accumulationPoints: 0,
                creditAmount: 0,
                docType: "Invoice",
                sOrderID: uuidv4().replace(/-/g, "").toUpperCase(),
                referenceId: uuidv4().replace(/-/g, "").toUpperCase(),
                giftCardRefId: uuidv4().replace(/-/g, "").toUpperCase(),
                couponRefId: uuidv4().replace(/-/g, "").toUpperCase(),
                customer: selectedSaleTypeData?.customer?.b2cCustomerId ? selectedSaleTypeData.customer : defaultCustomer,
                salesRepId: null,
                cardPaymentData: {},
                documentno: `${tillData.tillAccess.cwrTill.prefix}${newDocumentSequence + 1}`,
                userName: userData?.user || "",
                tillName: tillValue?.name,
                subTotal: 0,
                itemsSummery: {},
                amountInWords: "",
                groupedItemsByTaxRate: {},
                serviceCharge: 0,
                tip: 0,
                deiveryCharge: 0,
                packingCharge: 0,
                others: 0,
              };
              useSessionStore.getState().setDocumentSequence(newDocumentSequence);
              setDocumnetSequence(newDocumentSequence);
              setCart(freshSplitCart);
              setPaymentModal(false);
              // This branch returns early, bypassing the reset below (:1113-1160) —
              // without this, selectedRowKeys/selectedProductInCart from the split
              // order stay stale into the next order (TSK-3071).
              if (setSelectedRowKeys) setSelectedRowKeys([]);
              if (setSelectedProductInCart) setSelectedProductInCart({});
              orderState.current = 0;
              setActiveOrderProcess(false);
              return;
            }
            await cleanupNormalTable(tableRecord, tblData, cartToDb.sOrderID);
          }
        }
      }

      // BUG-1186: Capture exchange state BEFORE deleteCart clears it.
      // deleteReceipt (called by deleteCart) unconditionally clears exchangeCart
      // and selectedReturnType, which prevents the exchange cart from being
      // processed after the return order completes. We capture the state here
      // so the exchange flow below can still use it.
      const pendingExchangeReturnType = useTransactionStore.getState().selectedReturnType;
      const pendingExchangeCart = useCartStore.getState().exchangeCart;
      const hasPendingExchange = pendingExchangeReturnType === "EXCHANGE" && pendingExchangeCart?.items?.length > 0;

      // BUG-1963 Part B (F-7): this entire block resets the LIVE on-screen cart
      // (deleteCart -> setCart(blank) in one branch, a hand-built blank newCart in
      // the other) and advances the shared document-sequence counter using
      // newDocumentSequence/documentSequence -- values derived from whatever is
      // live right now, not from the order actually being reconciled. It was
      // unconditional, so a background reconciliation racing ahead of the live
      // cart (the cashier starts a fresh, not-yet-checked-out sale while a prior
      // order's confirm round-trip is still in flight) would silently wipe that
      // in-progress cart and could misadvance the sequence counter for the NEXT
      // sale -- this order's own screen reset/sequence consumption already
      // happened once, at its own original interactive completion; reconciliation
      // must not repeat it.
      //
      // BUG-2076: Part B originally guarded this on `!orderSnapshot`, reusing
      // Part A's "is this call finalizing an already-submitted order" signal
      // (see the cartToDb assignment above). That signal answers the wrong
      // question here -- F-12 (dbfc0681, four days earlier) had already started
      // passing orderSnapshot from the interactive online-checkout call sites
      // too (2864/2867/2743/3956), not just from true background reconciliation,
      // because even the interactive path's live cart can race ahead by the time
      // this async call lands. So `!orderSnapshot` skipped the cart reset for
      // EVERY disableOfflineBilling==="Y" checkout, not just reconciliation of a
      // stale order -- the cart never cleared, leaving it re-submittable via
      // "Print Receipt" (duplicate orders). The real question is whether the
      // live cart on screen right now still IS the order we just processed: if
      // yes, this is that order's own original completion and the reset is
      // expected; if no, a later sale has already started and reconciliation of
      // the earlier one must leave it alone.
      // Compare against cartToDbOriginalSOrderID (captured at function entry), not
      // cartToDb.sOrderID -- DI-33 above may have regenerated the latter mid-call for a
      // stale-identity DB collision, which the live on-screen cart never observes. Using
      // the post-regeneration value here would misread the cashier's own legitimate,
      // still-on-screen order as "a different order" and skip the cart reset,
      // resurrecting BUG-2076's symptom for this specific combined case.
      const liveCartSOrderIDAtReset = useCartStore.getState().cart?.sOrderID;
      const isReconcilingStaleOrder = !!orderSnapshot && !!liveCartSOrderIDAtReset && liveCartSOrderIDAtReset !== cartToDbOriginalSOrderID;
      if (!isReconcilingStaleOrder) {
        if (cart.isRetrived !== "Y" && cart.type !== "Layaway" && splitCarts.length === 0) {
          if (!usedOwnSequence) {
            useSessionStore.getState().setDocumentSequence(newDocumentSequence);
            setDocumnetSequence(newDocumentSequence);
          }
          // TIC-9393/F-19 (mirrored here): derive the next cart's preview number
          // from a live store read taken AFTER the setDocumentSequence call above,
          // instead of letting deleteCart -> deleteReceipt fall back to the
          // `documentSequence` hook parameter. That parameter is a same-render
          // React value one render behind this synchronous checkout->reset call
          // chain, so on a DI-31 recovery earlier in this same call it goes stale
          // by more than the fallback's baked-in one-render assumption, minting a
          // preview documentno one full render behind live -- the mechanism
          // behind KVN/113686's sOrderID/documentno churn. See
          // regular_checkout_preview_number_staleness.test.js.
          const liveDocumentSequence = useSessionStore.getState().documentSequence;
          const nextCartNo = `${tillData.tillAccess.cwrTill.prefix}${liveDocumentSequence + 1}`;
          deleteCart(false, usedOwnSequence ? "ownSequence" : undefined, false, nextCartNo);
        } else {
          const isPBCompleted = cart.documentno?.startsWith("PB/") && param !== "layaway";
          const advPrefixPost = useSessionStore.getState().preorderSequence?.prefix;
          const isAdvCompleted = advPrefixPost && cart.documentno?.startsWith(advPrefixPost) && param !== "layaway";
          const consumedRegularSeq = isPBCompleted || isAdvCompleted;
          if (consumedRegularSeq) {
            useSessionStore.getState().setDocumentSequence(newDocumentSequence);
            setDocumnetSequence(newDocumentSequence);
          }
          const nextCartSeq = consumedRegularSeq ? newDocumentSequence + 1 : documentSequence + 1;
          const newCart = {
            items: [],
            couponInput: [],
            advancePayment: 0,
            total: 0,
            tax: 0,
            discount: 0,
            paid: 0,
            change: 0,
            totalQty: 0,
            roundOff: 0,
            payments: [],
            redemptionPoints: 0,
            accumulationPoints: 0,
            creditAmount: 0,
            docType: "Invoice",
            sOrderID: uuidv4().replace(/-/g, "").toUpperCase(),
            referenceId: uuidv4().replace(/-/g, "").toUpperCase(),
            giftCardRefId: uuidv4().replace(/-/g, "").toUpperCase(),
            couponRefId: uuidv4().replace(/-/g, "").toUpperCase(),
            customer: selectedSaleTypeData?.customer?.b2cCustomerId ? selectedSaleTypeData.customer : defaultCustomer,
            salesRepId: null,
            cardPaymentData: {},
            documentno: `${tillData.tillAccess.cwrTill.prefix}${nextCartSeq}`,
            userName: userData?.user || "",
            tillName: tillValue?.name,
            subTotal: 0,
            itemsSummery: {},
            amountInWords: "",
            groupedItemsByTaxRate: {},
            serviceCharge: 0,
            tip: 0,
            deiveryCharge: 0,
            packingCharge: 0,
            others: 0,
          };
          setCart(newCart);
          // Unlike the deleteCart/deleteReceipt branch above, this manually-built
          // cart never went through deleteReceipt's reset, so selectedRowKeys stays
          // pinned to whatever row was selected in the completed order — the next
          // order then silently re-selects whatever item lands at that stale index
          // once its cart grows enough items (TSK-3071).
          if (setSelectedRowKeys) setSelectedRowKeys([]);
          if (setSelectedProductInCart) setSelectedProductInCart({});
        }
      }

      if (setNotesValue) setNotesValue("");
      setOrderDelay(now.format("DD-MM-YYYY HH:mm:ss"));
      if (posConfig?.disableOfflineBilling !== "Y") {
        message.success(`Order ${cartToDb.documentno} Created Successfully`);
        useCartStore.getState().setExchangeCart(null);
        useTransactionStore.getState().setSelectedReturnType(null);
      }
      if (hasShownCustomerModalRef) hasShownCustomerModalRef.current = false;
      if (setIsInputFocused) setIsInputFocused(false);
      orderState.current = 0;
      setActiveOrderProcess(false);
      localStorage.removeItem("totalDiscountsCartRef");

      if (cartToDb.giftVoucherType) {
        const { csBunitId, cwrTillId } = tillData.tillAccess;
        const customerId = cartToDb.customer?.cwrCustomerId;
        if (csBunitId && cwrTillId && customerId) {
          const query = {
            query: `mutation GenerateGiftVoucher($cSBunitID: String, $tillId: String, $b2cCustomerId: String, $giftVoucherType: String, $voucherAmount: Float) { generateGiftVoucher(giftVoucher: { cSBunitID: $cSBunitID tillId: $tillId b2cCustomerId: $b2cCustomerId giftVoucherType: $giftVoucherType voucherAmount: $voucherAmount }) { status message voucherCode } }`,
            variables: {
              cSBunitID: csBunitId,
              tillId: cwrTillId,
              b2cCustomerId: customerId,
              giftVoucherType: cartToDb.giftVoucherType,
              voucherAmount: cartToDb.giftVoucherAmount || 0,
            },
          };
          try {
            const resp = await generateGiftVoucher(query);
            const gGV = resp?.data?.data?.generateGiftVoucher;
            cartToDb.voucherCode = gGV?.status === "200" ? gGV.voucherCode : null;
          } catch (e) {
            console.error("Error generating gift voucher:", e);
            cartToDb.voucherCode = null;
          }
        } else {
          cartToDb.voucherCode = null;
        }
      }
      if (tillaccess?.layout === "2") cartToDb.orderSelection = orderTypeSelection;
      if (eInvoiceData) cartToDb.eInvoice = eInvoiceData;
      // Skip the settlement receipt only when Prepare Bill was pressed for THIS
      // order's table (per-table key, captured and consumed at the top of this
      // function before table cleanup could remove it). A leftover marker can
      // never suppress a later, unrelated order's receipt.
      if (!isPreparedBill) {
        // BUG-1963 Part B (F-2): pass cartToDb (the order actually being processed --
        // the reconciliation snapshot when reconciling, or the live cart on the
        // interactive checkout path where cartToDb IS the live cart) as the second
        // arg too, instead of the raw live `cart`. PrintController reads its 2nd
        // arg's .items to build the KOT/receipt line array (printController.js), so
        // passing the live cart here could print a different, newer sale's items on
        // this order's receipt once a background reconciliation lands after the
        // cashier has already started a new sale.
        PrintController(cartToDb, cartToDb);
        setInvoicePrinted?.("SUCCESS");
        window.electronAPI?.hardware?.clearDisplaySmart?.("auto");
      }

      // Trigger customer display feedback
      window.dispatchEvent(new CustomEvent("cd:triggerFeedback", { detail: { orderId: cartToDb.documentno } }));
      window.dispatchEvent(new CustomEvent("restaurant:orderComplete", { detail: { sOrderID: cartToDb.sOrderID } }));

      // Restaurant (tillLayout 2) keeps whichever fulfilment tab (Dine In/Takeaway/Delivery)
      // the cashier is currently on across consecutive orders instead of force-resetting to
      // the DB "isdefault" sale type - otherwise the next order's KOT/KDS payload mislabels
      // its saleType.name as the default (e.g. "Dine In") even on a Delivery order.
      const defaultSaleType = await getDefaultSaleType();
      if (defaultSaleType && tillLayout !== 2) {
        setSelectedSaleType(defaultSaleType.cwrSaletype.name);
        setSelectedSaleTypeData(defaultSaleType);
        usePOSConfigStore.getState().setSaleTypeData(defaultSaleType);
        const cartDetails = useCartStore.getState().cart || {};
        cartDetails.customer = defaultSaleType.customer?.b2cCustomerId ? defaultSaleType.customer : tillData.tillAccess.csBunit.b2cCustomer;
        setCart(cartDetails);
      }
      if (searchInputRef?.current) searchInputRef.current.focus();
      if (tillLayout === 2) {
        setLoading(true);
        setTimeout(() => {
          setLoading(false);
          history.push("/pos");
        }, 500);
      }
      if (posConfigLocal?.redirectToInvoiceAfterPreorder === "Y") {
        history.push("/pos");
      }
    } catch (error) {
      console.error("Failed to process order:", error);
      orderState.current = 0;
      setActiveOrderProcess(false);
      setPaymentModal(false);
      // If the order was already persisted to db.orders before the exception,
      // clear the cart immediately so stale items from this completed order
      // never bleed into the next transaction.
      try {
        const savedOrder = await db.orders.get(cartToDb.sOrderID);
        if (savedOrder) {
          console.warn("[BUG-1477][catch-recovery] Order was already saved before exception. Clearing cart to prevent ghost items.", {
            sOrderID: cartToDb.sOrderID,
            documentno: cartToDb.documentno,
            itemCount: cartToDb.items?.length,
            error: error?.message,
          });
          if (!usedOwnSequence) {
            useSessionStore.getState().setDocumentSequence(newDocumentSequence);
            setDocumnetSequence(newDocumentSequence);
          }
          // TIC-9393/F-19 (mirrored here): see the identical comment on the
          // regular-completion deleteCart call above -- same stale-closure gap,
          // same fix, on this catch-block recovery path.
          const liveDocumentSequenceCatch = useSessionStore.getState().documentSequence;
          const nextCartNoCatch = `${tillData.tillAccess.cwrTill.prefix}${liveDocumentSequenceCatch + 1}`;
          deleteCart(false, usedOwnSequence ? "ownSequence" : undefined, false, nextCartNoCatch);
        } else {
        }
      } catch (recoveryErr) {}
    }
  };

  // ─── syncOrders ─── (massive GraphQL mutation builder, kept intact)
  const syncOrders = async (syncTrigger) => {
    const safeParseDatetime = (str) => {
      if (!str) return dayjs();
      const d = dayjs(str, ["YYYY-MM-DD HH:mm:ss", "DD-MM-YYYY HH:mm:ss"], true);
      return d.isValid() ? d : dayjs();
    };
    // Live store read, not the `cart` hook prop — same staleness risk as processOrderApproved
    // (see DI-33 comment there): a stale `cart` here would feed the DI-33 check below (and
    // every other activeCart.* read in this function) a different transaction's data.
    const activeCart = syncTrigger === "exchange" ? useCartStore.getState().exchangeCart : useCartStore.getState().cart;
    const nWStatus = navigator.onLine ? "online" : "offline";
    let setAuthTokens;
    if (authHeaders && authHeaders.access_token) setAuthTokens = authHeaders.access_token;
    const posConfigLocal = usePOSConfigStore.getState().posConfig;
    let saleTypeId = null;
    const _saleTypeData = usePOSConfigStore.getState().saleTypeData;
    if (_saleTypeData) {
      saleTypeId = _saleTypeData?.cwrSaletype?.cwrSaletypeId;
    }
    if (nWStatus === "online") {
      // SEQ-13: DI-32 recovery previously ran only once on mount, so an order whose sync
      // attempt was classified as a timeout (DI-16 keeps isSynced=3 in that case — see
      // catch block below) stayed permanently excluded from every pending-order query
      // until the app/tab was reloaded. Re-running the (idempotent, non-blocking) recovery
      // on every "auto" tick closes that window without changing behavior for interactive
      // triggers (pay/exchange/layaway), which never had this gap.
      if (syncTrigger === "auto") {
        await recoverStuckSyncOrders(db).catch((err) => console.warn("[DI-32] Recovery failed (non-blocking):", err?.message));
        const mirrorResult = await verifyMirrorOrders(db, checkOrderExistsOnServer).catch((err) => {
          console.warn("[DI-36] Mirror verification failed (non-blocking):", err?.message);
          return null;
        });
        // DI-36: a mirror order that exhausted MIRROR_VERIFY_MAX_ATTEMPTS lookup
        // failures was failed open (promoted to isSynced: 0) rather than left
        // stuck forever — surface it the same way the normal pending-sync loop
        // surfaces its own exhausted retries, so it doesn't fail silently.
        if (mirrorResult?.stuck?.length) {
          for (const stuckOrder of mirrorResult.stuck) {
            notifySyncFailure({
              sOrderID: stuckOrder.sOrderID,
              documentno: stuckOrder.documentno,
              orderType: "regular",
              syncAttempts: stuckOrder.attempts,
            });
          }
        }
      }

      // Sync POS logs
      db.logInformation.toArray().then(async (fetched) => {
        if (fetched.length > 0) {
          const posLogArray = [];
          const trxId = uuidv4().replace(/-/g, "").toUpperCase();
          let shouldCallAxios = false;
          for (const item of fetched) {
            const value = { ...item };
            await db.logInformation.where("id").equals(value.id).delete();
            posLogArray.push(
              `{ type: ${JSON.stringify(value.type)}, action: "LOG", description: ${JSON.stringify(value.description)}, date: ${JSON.stringify(value.date)}, time: ${JSON.stringify(value.time)}, orderNo: ${JSON.stringify(value.orderNo)}, remarks: ${JSON.stringify(value.remarks)}, transactionId: "${trxId}", status: "SCS", duration: ${value.duration ?? 0} }`
            );
            if (posConfigLocal?.[value.type] === "Y") shouldCallAxios = true;
          }
          if (shouldCallAxios) {
            try {
              if (tillData?.tillAccess?.cwrTill?.cwrTillID && tillData?.tillAccess?.csUserId && tillData?.tillAccess?.csBunit?.csBunitId) {
                upsertPOSLogService(tillData.tillAccess.cwrTill.cwrTillID, tillData.tillAccess.csUserId, tillData.tillAccess.csBunit.csBunitId, posLogArray.join(", ")).catch(
                  () => {}
                );
              }
            } catch (error) {
              console.error("Axios request failed:", error);
            }
          }
        }
      });

      const csCurrencyId = tillData?.tillAccess?.csBunit?.currencies[0]?.csCurrencyId || "";
      let orders = [];
      const isInterStateSync = getIsInterState(activeCart);
      const taxDetailsSync = [];
      const taxMapSync = {};
      for (const item of activeCart.items) {
        if (!item.isGiftCard) {
          if (item.taxRate >= 0 && item.tax) {
            const tr = item.taxRate;
            const resolvedTaxId = isInterStateSync && item.interTaxId ? item.interTaxId : item.tax;
            const ta = parseFloat(parseFloat(item.taxAmount)?.toFixed(precision));
            const nt = parseFloat(parseFloat(item.nettotal - item.taxAmount)?.toFixed(precision));
            if (taxMapSync[tr]) {
              taxMapSync[tr].taxAmount += ta;
              taxMapSync[tr].nettotal += nt;
            } else {
              taxMapSync[tr] = { taxAmount: ta, nettotal: nt, tax: resolvedTaxId, taxRate: tr, taxCategory: item.taxCategory, taxName: item.taxName };
            }
          }
          if (!item.taxRate && item.children?.length > 0) {
            for (const child of item.children) {
              if (child.taxRate && child.tax) {
                const tr = child.taxRate;
                const resolvedChildTaxId = isInterStateSync && child.interTaxId ? child.interTaxId : child.tax;
                const ta = parseFloat(parseFloat(child.taxAmount)?.toFixed(precision));
                const nt = parseFloat(parseFloat(child.nettotal - child.taxAmount)?.toFixed(precision));
                if (taxMapSync[tr]) {
                  taxMapSync[tr].taxAmount += ta;
                  taxMapSync[tr].nettotal += nt;
                } else {
                  taxMapSync[tr] = { taxAmount: ta, nettotal: nt, tax: resolvedChildTaxId, taxRate: tr, taxCategory: child.taxCategory, taxName: child.taxName };
                }
              }
            }
          }
        }
      }
      const combinedTaxRates = Object.keys(taxMapSync).map((tr) => {
        const e = taxMapSync[tr];
        taxDetailsSync.push({ csTaxID: e.tax, taxableAmt: e.nettotal, taxAmt: e.taxAmount, taxRate: tr, taxCategory: e.taxCategory, taxName: e.taxName });
        return { csTaxID: e.tax, taxableAmt: e.nettotal, taxAmt: e.taxAmount };
      });

      if (posConfigLocal?.disableOfflineBilling === "Y") {
        const nowSync = dayjs(new Date());
        const orderTimeDetailsSync = usePOSConfigStore.getState().orderTimeDetails || { orderStartTime: "", orderEndTime: "", paymentStartTime: "" };
        orderTimeDetailsSync.orderEndTime = nowSync.format("DD-MM-YYYY HH:mm:ss");
        const tillSessionSync = useSessionStore.getState().tillSession || {};
        const tillSessionIdSync = tillSessionSync.tillSessionId || "";

        let activeCartAccumulationPoints = 0;
        if (activeCart.customer?.loyaltyLevel?.cwrLoyaltyLevelId) {
          const loyaltyLevelId = activeCart.customer.loyaltyLevel.cwrLoyaltyLevelId;
          const loyaltyData = await db.loyalityData.where("loyaltylevelId").equals(loyaltyLevelId).first();
          if (loyaltyData) {
            const eventCalenders = loyaltyData.loyaltyEventCalenders || [];
            const isEventActive = eventCalenders.length > 0 && eventCalenders.some((ec) => dateValidator(ec.startDate, ec.endDate));
            const applicableRule = (loyaltyData.LoyaltyAccumulation || []).find((item) => {
              const pv = item.mProductId ? activeCart.items.some((pc) => pc.mProductId === item.mProductId) : true;
              const cv = item.mProductCategoryId ? activeCart.items.some((pc) => pc.mProductCategoryId === item.mProductCategoryId) : true;
              if (isEventActive) return (item.loyaltyEvent === "Y" || item.loyaltyEvent === "N") && item.minPurchase <= Math.abs(activeCart.total) && pv && cv;
              return item.loyaltyEvent === "N" && item.minPurchase <= Math.abs(activeCart.total) && pv && cv;
            });
            if (applicableRule) {
              const baseAmount = activeCart.total - activeCart.tax;
              if (tillData?.tillAccess?.csBunit?.enableLoyalty !== "Y") activeCartAccumulationPoints = 0;
              else if (activeCart?.discount > 0 && loyaltyData?.noLoyaltyOnDiscount === "Y") activeCartAccumulationPoints = 0;
              else if (activeCart?.redemptionPoints > 0 && loyaltyData?.noLoyaltyOnRedemption === "Y") activeCartAccumulationPoints = 0;
              else {
                const rawPoints = Number((applicableRule.pointsPerUnit * baseAmount).toFixed(3));
                if (rawPoints < 0) {
                  const currentBalance = Math.max(activeCart.customer?.retlLoyaltyBalance || 0, 0);
                  activeCartAccumulationPoints = Math.max(rawPoints, -currentBalance);
                } else {
                  activeCartAccumulationPoints = rawPoints;
                }
              }
            }
          }
        }

        const _allDbOrders1 = await db.orders.toArray();
        const pendingDbOrders = _allDbOrders1.filter((o) => o.isSynced === 0 || o.isSynced === 2 || o.isSynced === "0" || o.isSynced === "2");
        // BUG-DUPLICATE-ESTIMATE-BILL: a rescanned/re-retrieved estimate mints its own
        // sOrderID+documentno preview, so it's invisible to the sOrderID-scoped DI-33
        // check below — this catches it by estimateId instead, across ALL orders, not
        // just the current cart's own identity slot. See findDuplicateEstimateOrder.
        const duplicateEstimateOrder = findDuplicateEstimateOrder(_allDbOrders1, activeCart.estimateId, activeCart.sOrderID);
        if (pendingDbOrders.length > 0) {
          // Filter by current till session so the completion modal shows the
          // current order's amount, not a stale order from a previous session/day.
          const currentSessionOrders = tillSessionIdSync ? pendingDbOrders.filter((o) => o.tillSessionId === tillSessionIdSync) : pendingDbOrders;
          const staleSessionOrders = tillSessionIdSync ? pendingDbOrders.filter((o) => o.tillSessionId !== tillSessionIdSync) : [];
          if (currentSessionOrders.length > 0) {
            orders = staleSessionOrders.length > 0 ? [...currentSessionOrders, ...staleSessionOrders] : currentSessionOrders;
          } else {
            orders = pendingDbOrders;
          }
          if (tillLayout === 2 && orders.length > 0) {
            setLoading(true);
          }
        } else if (syncTrigger === "auto") {
          // Auto-sync interval (every 10s): NEVER build a sync payload from the in-progress cart.
          // Doing so caused KWC issues #9/#10/#13 — server received partial carts without payments,
          // sometimes returning empty documentno (continuous error popup), sometimes accepting and
          // completing the order without payment methods, leaving the rest as half-state layaway.
          // Online mode has nothing to background-sync; only manual user "Pay" should reach posOrderProcessor1.
          orders = [];
        } else if (duplicateEstimateOrder) {
          // BUG-DUPLICATE-ESTIMATE-BILL: this estimate was already billed (by an earlier
          // retrieval that completed under a different sOrderID) — do not build a second
          // order from it. Production incident: KKPPOS/5612 + KKPPOS/5613, same
          // s_estimate_id, 5 seconds apart.
          message.warning(`This estimate has already been billed as ${duplicateEstimateOrder.documentno}.`);
          orders = [];
        } else if (activeCart.items.length > 0 && activeCart.total !== undefined) {
          // DI-33 (online path): same stale-identity protection as processOrderApproved,
          // applied BEFORE the order is posted. In pure-online mode (disableOfflineBilling
          // "Y") the backend dedupes on orderId (sOrderID) and returns 200 without creating
          // a record, so posting a stale sOrderID silently loses the sale server-side.
          // This branch only runs when db.orders holds no pending rows, so any row found
          // here is a previously completed order. Regenerate and propagate to the cart
          // store so the follow-up local save (processOrderApproved with isSynced=1) and
          // the post-success db.orders lookups all use the same fresh identity.
          let freshSOrderID = activeCart.sOrderID;
          let freshDocumentnoSync = activeCart.documentno;
          let identityRegeneratedSync = false;
          try {
            const existingSameIdSync = await db.orders.get(activeCart.sOrderID);
            const preorderPrefixSync = useSessionStore.getState().preorderSequence?.prefix;
            if (
              shouldRegenerateIdentityAtSave({
                existingOrder: existingSameIdSync,
                cart: activeCart,
                documentno: activeCart.documentno,
                preorderPrefix: preorderPrefixSync,
              })
            ) {
              freshSOrderID = uuidv4().replace(/-/g, "").toUpperCase();
              identityRegeneratedSync = true;
              if (syncTrigger === "exchange") {
                useCartStore.getState().setExchangeCart((prev) => (prev ? { ...prev, sOrderID: freshSOrderID } : prev));
              } else {
                useCartStore.getState().setCart((prev) => ({ ...prev, sOrderID: freshSOrderID }));
              }
              console.warn("[DI-33] Stale sOrderID detected before online post — regenerated to prevent server-side dedup.", {
                staleSOrderID: activeCart.sOrderID,
                newSOrderID: freshSOrderID,
                documentno: activeCart.documentno,
                overwrittenDocumentno: existingSameIdSync.documentno,
              });
              // A stale sOrderID can carry a stale documentno with it (same gap as the
              // processOrderApproved DI-33 fix below — RAK2/42569, S2AZ/19000: sOrderID
              // regenerated but the OLD invoice number left on the cart, so this new sale
              // would still post under a number a different, already-completed sale owns).
              // Re-run the same soft, non-blocking DI-31 check now that sOrderID is fresh.
              try {
                const docRecoverySeqTypeSync = preorderPrefixSync && activeCart.documentno?.startsWith(preorderPrefixSync) ? "preorder" : "regular";
                const docRecoveryPrefixSync = docRecoverySeqTypeSync === "preorder" ? preorderPrefixSync : tillData.tillAccess.cwrTill.prefix;
                const docDupCheckSync = await assertDocumentNoUnique({
                  dbAdapter: db,
                  documentno: activeCart.documentno,
                  sOrderID: freshSOrderID,
                  prefix: docRecoveryPrefixSync,
                  sessionStore: useSessionStore,
                  sequenceType: docRecoverySeqTypeSync,
                });
                if (docDupCheckSync.recovered) {
                  freshDocumentnoSync = docDupCheckSync.documentno;
                  if (syncTrigger === "exchange") {
                    useCartStore.getState().setExchangeCart((prev) => (prev ? { ...prev, documentno: freshDocumentnoSync } : prev));
                  } else {
                    useCartStore.getState().setCart((prev) => ({ ...prev, documentno: freshDocumentnoSync }));
                  }
                }
              } catch (docDupErrSync) {
                console.warn("[DI-33] Post-regeneration documentno uniqueness check before online post failed (non-blocking):", docDupErrSync?.message);
              }
              logSequenceEvent(
                "stale-identity-recovery",
                {
                  documentno: freshDocumentnoSync,
                  staleSOrderID: activeCart.sOrderID,
                  newSOrderID: freshSOrderID,
                  existingDocumentno: existingSameIdSync.documentno,
                  flow: "syncOrders-online",
                },
                db
              );
            }
          } catch (diErr) {
            console.warn("[DI-33] Stale identity check before online post failed (non-blocking):", diErr?.message);
          }
          const newOrder = {
            ...activeCart,
            sOrderID: freshSOrderID,
            documentno: freshDocumentnoSync,
            combinedTaxRates,
            taxDetails: taxDetailsSync,
            accumulationPoints: activeCartAccumulationPoints,
            createdBy: resolveActingUserId(tillData), // BUG-2232: acting cashier, not till-registration user
            customerSearchKey: activeCart?.customer?.code || "",
            isSynced: 0,
            orderCompleted: activeCart.statusType === "layAway" ? false : true,
            // TSK-3249: keep the till's opening business date, see the sibling
            // stamp above (~line 524) for the same reasoning.
            orderDate: tillSessionSync.businessDate ? dayjs(tillSessionSync.businessDate).format("DD-MM-YYYY") : nowSync.format("DD-MM-YYYY"),
            orderTime: nowSync.format("DD-MM-YYYY HH:mm:ss"),
            orderTimeDetails: orderTimeDetailsSync,
            orderType: orderType?.cwrSaletype?.cwrSaletypeId,
            overPayedAmount: overPayedAmount || 0,
            syncAttempts: 0,
            tillSessionId: tillSessionIdSync,
            typeOrder: useRestaurantStore.getState().homeDelivery || "N",
            // Prevent stale "Advance" docType (left over from an abandoned layaway retrieval,
            // OR simply from enabling the Advance flag on a cart that's never been submitted
            // before) from routing a genuine first-time submission to confirmPOSLayawayOrder1
            // instead of posOrderProcessor1.
            // BUG-2339: this was gated on syncTrigger === "layaway" only, on the assumption that
            // trigger is exclusively used by a first-time "Create Advance" click. True, but it left
            // every OTHER genuinely-first-time submission unprotected -- most notably a take-away
            // order with the Advance flag enabled, paid in full, completed via "Complete Order"
            // (processOrder() with no argument -- PaymentCheckout.js/OrderDetailsSidebar.js --
            // syncTrigger is not "layaway" there). Gate on the same first-time-submission signal
            // the classifier itself uses a few hundred lines down (isRetrievedAdvance's inverse)
            // instead of the trigger name, so this correction covers every trigger a genuine
            // first-time submission can arrive through, not just the "Create Advance" button.
            // BUG-2339 follow-up: wasLayaway===false (Restaurant's own explicit first-time stamp,
            // see isRetrievedAdvance's comment above) also counts as first-time evidence, since
            // handleOrderCompletion sets advancePayment to the just-taken payment amount even when
            // it's nonzero (partial or full) on that same first submission.
            ...((activeCart.wasLayaway === false || (activeCart.advancePayment || 0) <= 0) &&
              activeCart.salesHistory !== "Y" &&
              activeCart.docType === "Advance" && { docType: "Invoice" }),
            // TSK-2935: preparedBillOrigin marks "this sOrderID was already created via
            // posOrderProcessor1" so the classifier below routes it to confirmPOSLayawayOrder1.
            // When DI-33 just regenerated sOrderID above, that claim is now false for the new
            // identity — it has never been sent to the server — so it must not carry over, or
            // the classifier calls confirmPOSLayawayOrder1 on an order the backend has no
            // s_order row for (FK violation on s_ordertax at confirm time).
            ...(identityRegeneratedSync && { preparedBillOrigin: undefined }),
          };
          // try {
          //   await db.orders.put(newOrder);
          // } catch (dbErr) {
          //   console.error("Failed to persist order before sync:", dbErr);
          // }
          orders = [newOrder];
        } else {
          orders = [];
        }
      } else {
        const _allDbOrders2 = await db.orders.toArray();
        orders = _allDbOrders2.filter((o) => o.isSynced === 0 || o.isSynced === 2 || o.isSynced === "0" || o.isSynced === "2");

        // SYNC-DIAG: confirms/denies whether the fall-through-classification gap logged
        // further below is even reachable for the stuck advance/layaway orders, or whether
        // they're being excluded HERE, before the loop ever runs — e.g. isSynced already
        // sitting at 1/3 instead of 0/2, which would point to a different bug entirely
        // (isSynced set wrong at write time) rather than the in-loop classification gap.
        // Uses the existing durable sequenceAuditLog (db.sequenceAuditLog) rather than a
        // new sink — same mechanism DI-33/init/etc. already write to, already pullable via
        // window.__seqDiag() / __seqDiagDownload(). Only worth the scan cost on the
        // periodic auto tick, not every interactive save.
        if (syncTrigger === "auto") {
          try {
            const layawayShapedAll = _allDbOrders2.filter((o) => o?.layAway === "Y" || o?.docType === "Advance");
            const layawayShapedCandidates = orders.filter((o) => o?.layAway === "Y" || o?.docType === "Advance");
            const excludedLayawayShaped = layawayShapedAll.filter((o) => !layawayShapedCandidates.includes(o));
            if (excludedLayawayShaped.length > 0) {
              logSyncDiagThrottled(
                "sync-diag-excluded-pre-loop",
                {
                  excludedCount: excludedLayawayShaped.length,
                  totalLayawayShaped: layawayShapedAll.length,
                  candidateLayawayShaped: layawayShapedCandidates.length,
                  samples: excludedLayawayShaped.slice(0, 10).map((o) => ({
                    sOrderID: o?.sOrderID,
                    documentno: o?.documentno,
                    isSynced: o?.isSynced,
                    layAway: o?.layAway,
                    docType: o?.docType,
                    paid: o?.paid,
                  })),
                },
                db
              );
            }
          } catch (e) {
            // swallow — diagnostics must never affect order processing
          }
        }
      }

      // BUG-1421's stale-"Advance"-docType correction (see comment above, ~line 1159)
      // only covered the branch that builds the order fresh from activeCart. When this
      // same cart/order already exists in the DB (pendingDbOrders branch above, or the
      // plain-offline branch above), that branch is skipped entirely and the correction
      // never runs — so a save on an order that still carries a leftover docType:"Advance"
      // (e.g. from an earlier abandoned retrieval, or simply the Advance flag enabled on a
      // cart never submitted before) misroutes to confirmPOSLayawayOrder1 instead of
      // posOrderProcessor1. Apply the same correction here so it covers every
      // order-population path, scoped strictly to the order being saved right now
      // (matched by sOrderID).
      // BUG-2339: previously additionally gated on syncTrigger === "layaway", on the
      // assumption that trigger is the only way a genuine first-time submission reaches
      // here. False — a take-away order with the Advance flag enabled, paid in full,
      // completed via "Complete Order" (processOrder() with no argument) also reaches
      // this same order-population branch, first-time, via whatever trigger that call
      // path uses (not "layaway"), and was left uncorrected. Gate on the order's own
      // first-time-submission signal instead — the same isRetrievedAdvance inverse the
      // classifier itself uses a few hundred lines down — so unrelated pending orders,
      // including genuine advance-order completions/top-ups (advancePayment > 0 or
      // salesHistory === "Y"), stay untouched regardless of which trigger saved them.
      if (activeCart?.sOrderID) {
        orders = orders.map((o) => {
          if (o?.sOrderID !== activeCart.sOrderID || o?.docType !== "Advance") return o;
          // BUG-2339 follow-up: wasLayaway===false also counts as first-time evidence — see
          // isRetrievedAdvance's comment above for why advancePayment alone isn't reliable here.
          const isGenuineFirstTimeSubmission = o.salesHistory !== "Y" && (o.wasLayaway === false || (o.advancePayment || 0) <= 0);
          return isGenuineFirstTimeSubmission ? { ...o, docType: "Invoice" } : o;
        });
      }

      const pendingOrdersCount = orders.length;
      if (pendingOrdersCount > 0) {
        if (eBillConfig?.current?.eBill === "Y") sendWebHookData(orders);

        const username = useAuthStore.getState().username;
        const salesReps = tillData?.tillAccess?.csBunit?.salesRep || [];
        const matchedRep = salesReps.find((rep) => rep.name === username);

        // BUG-1200: Single loop with per-order if/else classification (aligned with roadmap).
        // Each order is either regular (posOrderProcessor1) or layaway (confirmPOSLayawayOrder1), never both.
        for (let i = 0; i < orders.length; i += 1) {
          if (!orders[i]?.items?.length) {
            // SYNC-DIAG: silently skipped, every tick, forever, until items is populated —
            // no network call, no error, no local trace otherwise. Uses the existing
            // durable sequenceAuditLog (db.sequenceAuditLog) — same sink DI-33/init/etc.
            // already write to, pullable via window.__seqDiag() / __seqDiagDownload().
            try {
              logSyncDiagThrottled(
                "sync-diag-skipped-empty-items",
                {
                  sOrderID: orders[i]?.sOrderID,
                  documentno: orders[i]?.documentno,
                  isSynced: orders[i]?.isSynced,
                  syncAttempts: orders[i]?.syncAttempts,
                  layAway: orders[i]?.layAway,
                  docType: orders[i]?.docType,
                  paid: orders[i]?.paid,
                  total: orders[i]?.total,
                },
                db
              );
            } catch (e) {
              // swallow — diagnostics must never affect order processing
            }
            continue;
          }

          // Issue-7: prefer this order's own captured B2B state (set at creation time
          // in processOrderApproved) over the live global, which may have drifted since
          // this order was queued (different customer selected, logout, etc.). Orders
          // saved before this fix have no snapshot, so fall back to the live read —
          // identical to today's existing behavior for those.
          // FK-3692,6319/BUG-1969: only `undefined` means "never captured" (legacy
          // pre-fix order) -- cartIdentityGuard.js's captureOrderCreationSnapshot
          // explicitly writes `isB2bEnabledSnapshot = liveIsB2bEnabled ?? null`, so a
          // walk-in order's snapshot is a legitimate captured `null`, not a missing one.
          // The previous `&& !== null` check treated that legitimate null the same as
          // "never captured" and fell back to the live global anyway, defeating this
          // fix for every non-B2B order -- the majority case.
          const orderIsB2bEnabled = orders[i]?.isB2bEnabledSnapshot !== undefined ? orders[i].isB2bEnabledSnapshot : useTransactionStore.getState().isB2bEnabled;

          // FK-3692,6319/BUG-1969: same fix as orderIsB2bEnabled above, applied to the
          // captured billing/shipping address ids. Checks presence of the CONTAINER
          // object at each layer (order.customer.customerAddressIds, then the top-level
          // order.customerAddressIds fallback added for the falsy-cartToDb.customer case)
          // rather than the leaf billingId/shippingId value -- a `??` chain on the leaf
          // value would treat a legitimately-captured `{billingId: null}` (this order's
          // own confirmed "no address" snapshot) the same as "never captured", and fall
          // through to the live localStorage read, substituting in whatever customer's
          // address happens to be active at sync time.
          const resolveCapturedAddressId = (order, field) => {
            if (order?.customer?.customerAddressIds !== undefined) return order.customer.customerAddressIds?.[field] ?? null;
            if (order?.customerAddressIds !== undefined) return order.customerAddressIds?.[field] ?? null;
            // BUG-1969 (TIC-6900/6918): customerAddressIds is only ever written by
            // customer.js's createCustomer/updateCustomer -- a customer merely
            // searched-and-selected (not created/edited) in this session never
            // populates it, so both checks above fall through here even though this
            // order's OWN customer record may carry a real address id for the B2B
            // customer-list search (invoiceAddresses/deliveryAddresses). Reading it from
            // order.customer here is safe against the live-state-leak class this whole
            // function exists to prevent -- it's the same per-order-frozen snapshot
            // (JSON.parse(JSON.stringify(...)) clone, see cartToDb above) the two
            // checks above already trust, not a live read -- unlike the localStorage
            // fallback below, which is why this is checked first.
            //
            // BUG-1969 follow-up (2026-08-21, live-confirmed on KWC): order.customer.sCustomerAddress
            // was removed from this fallback -- confirmed via real captured payloads that it is NOT
            // an address id, it's a JSON-encoded STRING of full address objects (line1/city/
            // cs_region_id/...). Using it here wrote the entire blob into sCustomerBillingId/
            // sCustomerShippingId instead of an id whenever an existing customer was added via
            // search (the main customerSearch query doesn't request invoiceAddresses/
            // deliveryAddresses at all, so this was the only branch that ever fired for that flow).
            // Now correctly falls through to the localStorage read below instead of writing invalid
            // data. A real id source for search-selected customers is a separate, not-yet-covered
            // gap (getB2BCustomerList could supply one but only for B2B customers, not all
            // search-selected ones) -- deliberately not addressed here, out of scope for this fix.
            const addressListField = field === "shippingId" ? "deliveryAddresses" : "invoiceAddresses";
            const capturedFromCustomer = order?.customer?.[addressListField]?.[0]?.s_customer_address_id;
            if (capturedFromCustomer) return capturedFromCustomer;
            // BUG-2038 follow-up (TIC-6900/6918, live-confirmed 2026-08-21 on KWC/GPR: 14+4
            // customers with a billing/shipping id belonging to a DIFFERENT s_customer_id):
            // this last-resort read is the exact "legacy live read" cartIdentityGuard.js's own
            // comment (captureOrderCreationSnapshot, ~line 273) already names as the leak this
            // whole snapshot mechanism exists to prevent -- it fires only when NEITHER the
            // creation-time snapshot NOR this order's own customer record had anything to use
            // (both captured `undefined`), so localStorage is the CURRENT value, which can
            // belong to a completely different customer than this queued order by the time a
            // batch sync loop reaches it. BUG-2038 already applied this exact fix -- scoping a
            // cached value to the owning order's sOrderID -- on the WRITE side
            // (buildCustomerListPayload's canReuseExistingCustomerId); this closes the same gap
            // on this READ side. The cached blob always carries its own sOrderID (customer.js's
            // writes: `{ customerId, billingId, shippingId, sOrderID }`), so an order whose own
            // sOrderID doesn't match it is never allowed to borrow it.
            try {
              const cached = JSON.parse(localStorage.getItem("customerAddressIds") || "{}");
              return cached?.sOrderID && cached.sOrderID === order?.sOrderID ? (cached?.[field] ?? null) : null;
            } catch (e) {
              return null;
            }
          };

          // // Classify: layaway/credit-sale orders go to confirmPOSLayawayOrder1
          // const isCreditSaleOrder = orders[i]?.creditSale === "Y";
          // const isLayawayType = (orders[i].layAway === "Y" && orders[i].paid !== 0) || (isCreditSaleOrder && orders[i].paid !== 0);

          // Regular order qualification
          const layAwayStatus = orders[i]?.layAway || "N";
          const paidAmount = orders[i].layAway === "Y" && posConfigLocal?.advanceLayaway === "Y" && orders[i].advancePayment <= 0 ? 0 + orders[i]?.paid : orders[i]?.paid || 0;
          // BUG-2339 follow-up (double-counting): handleOrderCompletion (OrderListComponent.js)
          // stamps advancePayment: paid on a take-away order's FIRST-EVER submission -- the same
          // amount already counted in paidAmount above, not a separate prior-session deposit. Left
          // as-is, paidAmount + advancePayment below would count that one payment twice and fail
          // the <= totalAmount check even on a plain first-time full payment (paid === total),
          // falling through to confirmPOSLayawayOrder1 against an sOrderID the server has never
          // seen. wasLayaway === false is the same explicit first-time signal isRetrievedAdvance
          // (below) already keys on for this case -- treat the classification-only advancePayment
          // as 0 for it too, so it stops double-counting. The real orders[i]?.advancePayment value
          // used for the outgoing payload (e.g. ~L5316) is untouched.
          const advancePayment = orders[i]?.wasLayaway === false ? 0 : orders[i]?.advancePayment || 0;
          const totalAmount = orders[i]?.total || 0;
          const totalQty = orders[i]?.totalQty || 0;
          const isParked = orders[i]?.parked === "Y";
          const isCreditSaleInCart = orders[i]?.creditSale === "Y";
          // BUG-1570: The API a layaway/advance order is sent to must depend on whether this is a
          // FIRST-TIME create or a later action on an already-created order — not on how much was
          // paid this session, nor on the advanceLayaway flag. A first submission (advancePayment 0
          // and not retrieved from sales history) is a create → posOrderProcessor1, even when it
          // carries a partial or full payment. Only once the order has been created and later
          // retrieved from sales history (salesHistory:"Y", carrying the earlier advancePayment > 0)
          // does a subsequent submission — adding more advance, or paying the balance to complete —
          // become a confirm → confirmPOSLayawayOrder1.
          //
          // Previously the layaway path was only advancePayment-aware under advanceLayaway === "Y";
          // under advanceLayaway !== "Y" a first-time layaway WITH a payment matched neither regular
          // branch, fell through to the Stage-2 layaway check (paid !== 0), and misrouted a create to
          // confirmPOSLayawayOrder1. Gate the layaway case on the retrieved-vs-first-time signal for
          // both flag values instead. (advancePayment <= 0 alone preserves the BUG-1567 behaviour;
          // the salesHistory check additionally catches a retrieved order whose original advance was 0.)
          //
          // BUG-2339 follow-up (partial payment): advancePayment > 0 is NOT reliable evidence of a
          // prior create for Restaurant. handleOrderCompletion (OrderListComponent.js) stamps
          // advancePayment: paid on a take-away order's cart on its FIRST-EVER submission -- the
          // amount just entered, not a payment recorded on some earlier save -- so advancePayment
          // alone can't distinguish "genuinely first-time, happens to include a deposit" from "this
          // is a real top-up of an order that already exists server-side" for Restaurant orders.
          // wasLayaway is Restaurant's own reliable signal for this (stamped explicitly `false` by
          // handleOrderCompletion's first-time branch, `true` only by an actual retrieval --
          // restaurantPreorder/Preorder/index.js). SuperMarket/Retail/Preorder orders never set this
          // field at all (stays undefined), so `wasLayaway !== false` is true for them and this falls
          // straight back to the original advancePayment>0 signal those modules already rely on --
          // this only changes behavior for a Restaurant order explicitly marked wasLayaway:false.
          const isRetrievedAdvance = orders[i]?.salesHistory === "Y" || (orders[i]?.wasLayaway !== false && advancePayment > 0);
          // TSK-2935: a Prepare-Bill order was already created via posOrderProcessor1
          // (paid=0 at that time, so it correctly took isRegularOrderWithoutPaid below).
          // This same order's FINAL submission — now paid in full — has advancePayment
          // still 0 and no salesHistory flag, so without this check it would satisfy
          // isRegularOrderWithPaid's paidAmount+advancePayment<=totalAmount test again and
          // misroute a second time to posOrderProcessor1 instead of confirming the existing
          // order via confirmPOSLayawayOrder1.
          const isPrepareBillOriginated = orders[i]?.preparedBillOrigin === "Y";
          // TSK-2935 / BUG-fix: cancelling an order that Prepare Bill already confirmed
          // server-side must not resubmit it via posOrderProcessor1 — that's a CREATE call,
          // and for a disableOfflineBilling tenant the server ignores the client's requested
          // documentno and mints its own fresh one, silently reassigning the cancelled
          // record to a brand-new number instead of keeping the Prepare-Bill-confirmed one
          // (confirmed via production trace: BHRS/1781 cancelled, resubmitted as a "new"
          // order, came back as BHRS/1785). isPrepareBillCancelled routes it through the
          // confirm branch below instead, reusing the existing sOrderID/documentno.
          //
          // Moved above isRegularOrderWithoutPaid (was declared after it) because that
          // check now depends on it directly — see the comment there for why.
          //
          // BUG-fix (round 2): totalAmount===0 && totalQty===0 alone doesn't prove the
          // create ever reached the server — Prepare Bill's create only fires on the next
          // 10s auto-sync tick (processOrderApproved's isPrepareBillCreate branch returns
          // before the "immediately try to sync" call, see ~L952). "Cancel All" clicked
          // fast enough zeroes the row before that first tick ever runs, so it was never
          // actually created — yet this still satisfied isPrepareBillCancelled and routed
          // to confirmPOSLayawayOrder1 against a server order that doesn't exist, which
          // fails and retries forever. Gate on preparedBillConfirmed — stamped by
          // clearCart1 (Restaurant/index.js) at the moment of cancellation, from the
          // pre-cancel row's own isSynced value (1 = the create already succeeded) — so
          // an unconfirmed cancel instead falls through to isCancelledOrder below and
          // creates the (now-empty) order via posOrderProcessor1, same as any other
          // pre-sync cancel.
          const isPrepareBillCancelled = isPrepareBillOriginated && totalAmount === 0 && totalQty === 0 && orders[i]?.preparedBillConfirmed === "Y";
          const isRegularOrderWithPaid =
            !isCreditSaleInCart &&
            !isPrepareBillOriginated &&
            (layAwayStatus === "Y" ? !isRetrievedAdvance && paidAmount + advancePayment <= totalAmount && orders[i]?.docType !== "Advance" : paidAmount !== 0);
          // preparedBillOrigin is stamped onto the persisted row the moment Prepare Bill is
          // clicked (useOrderProcessing.js ~L911), BEFORE this order has ever actually reached
          // posOrderProcessor1 — not "only after that first create succeeds" as originally
          // assumed here. Excluding every isPrepareBillOriginated row from this branch (the old
          // `!isPrepareBillOriginated`) meant the CREATE call itself was silently skipped on
          // every auto-sync tick: paid is 0 at creation time so isRegularOrderWithPaid never
          // matched either, and isLayawayOrder below requires paid!==0 (or isPrepareBillCancelled,
          // not true yet) — so the order just sat at isSynced:0 forever, no server row ever
          // created. Then a later full cancel classified as isPrepareBillCancelled and routed to
          // confirmPOSLayawayOrder1 against a server order that was never created, which fails
          // and resets isSynced back to 0, retrying forever (confirmPOSLayawayOrder1 called
          // continuously). Excluding only the cancelled case (isPrepareBillCancelled) instead
          // lets a not-yet-created, not-yet-cancelled Prepare-Bill row reach its real create
          // call, so by the time a cancel happens there's an actual order to confirm.
          const isRegularOrderWithoutPaid =
            !isCreditSaleInCart && !isPrepareBillCancelled && (layAwayStatus === "N" || layAwayStatus === "Y") && paidAmount === 0 && orders[i]?.docType !== "Advance";
          // A zeroed Prepare-Bill row whose create never actually reached the server
          // (isPrepareBillCancelled false because preparedBillConfirmed isn't "Y" yet)
          // must still fall in here, not be excluded like a confirmed one — there's no
          // existing server order to confirm, only to create (as cancelled/empty).
          const isCancelledOrder = totalAmount === 0 && totalQty === 0 && !isPrepareBillCancelled;
          // TSK-2935: same posOrderProcessor1-must-fire-only-once rule as isCancelledOrder above.
          // Today the UI can't reach this combination (Hold Order requires dineIn!=="Y", Prepare
          // Bill requires dineIn==="Y" — mutually exclusive on the same flag), but the classifier
          // itself must not depend on that UI gate to stay safe: if a Prepare-Bill-confirmed order
          // ever carried parked:"Y", this would otherwise satisfy isRegularOrder and resubmit it
          // via posOrderProcessor1, minting a second backend sequence number for the same order.
          const isParkedOrder = isParked && paidAmount === 0 && !isPrepareBillOriginated;
          const isRegularOrder = isRegularOrderWithPaid || isRegularOrderWithoutPaid || isCancelledOrder || isParkedOrder;
          // [ADV-ORDER-DIAG] Records exactly why this order was/wasn't classified regular —
          // if isRegularOrder is false here, it falls through to the isLayawayOrder check
          // below and may call confirmPOSLayawayOrder1.
          // [ADV-ORDER-DIAG] paidAmount+advancePayment < totalAmount is a STRICT '<' (see
          // BUG-1421, commit b011d0c1). A layaway/advance order paid in FULL at submission
          // time (paid === total) fails this check and falls through to confirmPOSLayawayOrder1
          // even on its first-ever submission — flagging this distinctly so it's unmissable
          // in the trace if it's the case being hit.
          if (layAwayStatus === "Y" && !isCreditSaleInCart && paidAmount + advancePayment >= totalAmount && !isRegularOrder) {
            console.warn(
              "[ADV-ORDER-DIAG] FULL-PAYMENT-EDGE-CASE: layaway order paid in full (or overpaid) is falling through to confirmPOSLayawayOrder1 instead of posOrderProcessor1",
              { sOrderID: orders[i]?.sOrderID, documentno: orders[i]?.documentno, docType: orders[i]?.docType, paidAmount, advancePayment, totalAmount }
            );
          }
          if (isRegularOrder) {
            const orderLines = [];
            const childItemMetaMap = new Map();
            for (let j = 0; j < orders[i]?.items.length; j += 1) {
              if (orders[i].items[j]?.isCanceled === "Y") continue;
              // BUG-2044: weight === 0 means this line was decremented to zero on an
              // advance-payment cart -- cart.js intentionally keeps it in cart.items
              // (hidden from the cart UI, but not spliced out) instead of removing it,
              // for advance-payment accounting. cart.js's own totals loop already
              // excludes weight === 0 lines from the sums it computes; the order-line
              // builder here needs to respect that same "hidden, not real" semantics,
              // or a real product at a real price ships to the backend at qty 0 (live
              // Dadus repro: HAO/44561). Exact-zero only -- a return line's weight is
              // legitimately negative and must still be sent.
              // if (orders[i].items[j]?.weight === 0) continue;
              let unitTax = orders[i].items[j].taxAmount / orders[i].items[j].weight;
              unitTax = unitTax * (orders[i].items[j].isReturn ? -1 : 1);
              let linesMeta = [];
              if (orders[i].items[j].newCustomAttributes?.length > 0) {
                Object.entries(orders[i].items[j].newCustomAttributes[0]).forEach(([key, value]) => {
                  // Server expects string values. Arrays (MCH/UPL/ADG) / objects (AUD/Dayjs)
                  // need flattening or they serialise as "[object Object]". Upload blobs
                  // aren't sent here — only a human-readable summary reaches the order metadata.
                  let serialised = value;
                  if (Array.isArray(value)) {
                    if (value.length === 0) return;
                    const first = value[0];
                    if (first && typeof first === "object") serialised = `${value.length} item${value.length > 1 ? "s" : ""}`;
                    else serialised = value.join(", ");
                  } else if (value && typeof value === "object") {
                    if (value.dataUrl && value.duration != null) serialised = `voice note ${value.duration}s`;
                    else if (typeof value.format === "function") serialised = value.format("DD-MM-YYYY");
                    else return;
                  }
                  if (serialised === null || serialised === undefined || serialised === "") return;
                  linesMeta.push({ key, value: String(serialised) });
                });
              }
              // TIC-10890: approver + discount type/percentage/amount (built from a copy, so the
              // cart line's own approval record is never modified).
              const approvalMeta = buildApprovalMeta(orders[i].items[j], precision);
              if (approvalMeta) linesMeta.push(approvalMeta);
              const productAddons = [];
              if (orders[i].items[j]?.childItems?.length > 0) {
                for (const childItem of orders[i].items[j].childItems) {
                  const sp = parseFloat(parseFloat(childItem?.sunitprice || 0)?.toFixed(precision));
                  const quantity = childItem.weight * orders[i].items[j].weight;
                  const lineGross = parseFloat(parseFloat(sp * quantity)?.toFixed(precision));
                  const tax = taxIncludeFlag === "Y" ? lineGross - lineGross / (1 + childItem.taxRate / 100) : (lineGross * childItem.taxRate) / 100;
                  const cUnitTax = parseFloat(parseFloat(tax / quantity)?.toFixed(precision));
                  const lineTotal = parseFloat(parseFloat(lineGross + tax)?.toFixed(precision));
                  // TSK-2470: prefer inline alternate/base UOM on the child product, else resolve from db.products
                  const childCsUomId =
                    (childItem.alternateUom?.length > 0 ? childItem.alternateUom[0]?.csUomId : childItem.uomData?.[0]?.csUomId || childItem.csUomId) ||
                    (await resolveAddonUom(childItem.mProductId));
                  productAddons.push({
                    name: childItem.name,
                    price: sp,
                    addonProductId: childItem.mProductId,
                    csUomId: childCsUomId || null,
                    qty: quantity,
                    lineGross,
                    lineTax: tax,
                    lineTotal,
                    unitTax: cUnitTax,
                    grossUnit: sp,
                    value: childItem.value,
                  });
                }
              } else if (orders[i].items[j]?.selectedAddons?.length > 0) {
                for (const addon of orders[i].items[j].selectedAddons) {
                  const addonProductId = addon?.mAddonProduct?.mProductId || null;
                  // TSK-2470: addon objects only carry id+name — resolve UOM from the addon product in db.products
                  const addonCsUomId = await resolveAddonUom(addonProductId);
                  productAddons.push({
                    name: addon.name,
                    price: addon.price,
                    addonProductId,
                    csUomId: addonCsUomId,
                    // addon.weight tracks the parent product units (accumulates on re-add);
                    // addon.addonQty is the per-unit selected quantity (allowQtySelection = Y).
                    // Stock must deduct weight * addonQty, else a qty-selected addon (e.g. 7)
                    // is sent as the parent units only (1) and stock under-deducts.
                    qty: (Number(addon.weight) || 1) * (Number(addon.addonQty) || 1),
                    lineGross: 0,
                    lineTax: 0,
                    lineTotal: 0,
                    unitTax: 0,
                    grossUnit: 0,
                  });
                }
              }
              const item = orders[i].items[j];
              if (item?.realWeight) linesMeta.push({ key: item.name, value: item?.realWeight.toString() });
              const lineTaxableAmt = item.linenet !== null && item.linenet !== undefined && item.linenet !== 0 ? item.linenet : 0;

              if (Array.isArray(item.children) && item.children.length > 0) {
                const childIDs = [];
                for (let k = 0; k < item.children.length; k++) {
                  const currentItem = item.children[k];
                  const childTaxableAmt = currentItem.linenet !== null && currentItem.linenet !== undefined && currentItem.linenet !== 0 ? currentItem.linenet : 0;
                  let cUnitTax = currentItem.taxAmount / currentItem.weight;
                  const sOrderlineID = uuidv4().replace(/-/g, "").toUpperCase();
                  const parsedDate = safeParseDatetime(orders[i].orderTime);
                  orderLines.push({
                    csBunitId: currentItem.csBunitId || null,
                    wastage: currentItem.wastage === "Y" ? "Y" : null,
                    sOrderlineID,
                    childrenId: currentItem.childrenId || null,
                    sOrderlineReturnId: currentItem.sOrderlineReturnId || null,
                    created: parsedDate.format("YYYY-MM-DD HH:mm:ss"),
                    createdby: orders[i].createdBy,
                    updated: parsedDate.format("YYYY-MM-DD HH:mm:ss"),
                    updatedby: orders[i].createdBy,
                    deliveryDate: orders[i]?.deliveryDate ? safeParseDatetime(orders[i].deliveryDate).format("YYYY-MM-DD HH:mm:ss") : parsedDate.format("YYYY-MM-DD HH:mm:ss"),
                    sOrderId: orders[i].sOrderID,
                    line: (j + 1) * 10 + k,
                    description: currentItem.notes || "",
                    mProductId: currentItem.productId || currentItem.mProductId,
                    csUomId: currentItem.uom || currentItem.uomData?.[0]?.csUomId,
                    csTaxId: isInterStateSync && currentItem.interTaxId ? currentItem.interTaxId : currentItem.tax || currentItem.cTaxId,
                    qty: currentItem.weight,
                    unitprice: parseFloat(currentItem.realPrice) * (currentItem.isReturn ? -1 : 1),
                    discount: currentItem.discount,
                    returnline: currentItem.isReturn || false,
                    returnQty: currentItem.isReturn ? Math.abs(currentItem.weight) : 0,
                    mBatchId: currentItem.mBatchId || currentItem.mBatch || null,
                    mBatchNo: currentItem.batchno || null,
                    mPricingruleId: currentItem.mPricingruleId || null,
                    batchedForSale: currentItem.batchedForSale,
                    batchedForStock: currentItem.batchedForStock,
                    batchedProduct: currentItem.batchedProduct,
                    salesRepId: currentItem.salesRepId || orders[i]?.salesRepId || matchedRep?.salesRepresentId || null,
                    multiPrice: currentItem.multiPrice === "Y" ? "Y" : "N",
                    discountTypeId: null,
                    discountAmount: null,
                    // TIC-10890: this child's own discount values, not the parent's.
                    metaData: withApprovalMeta(linesMeta, buildApprovalMeta(currentItem, precision, item)),
                    unittax: cUnitTax > 0 ? parseFloat(parseFloat(cUnitTax)?.toFixed(precision)) : 0,
                    netstd: Math.abs(currentItem.netStd) ? parseFloat(currentItem.netStd) : 0,
                    listprice: currentItem.listPrice || 0,
                    linetax:
                      posConfigLocal?.disableOfflineBilling === "Y"
                        ? Math.abs(currentItem.nettotal) > 0
                          ? parseFloat(parseFloat(currentItem.taxAmount)?.toFixed(precision))
                          : 0
                        : parseFloat(currentItem.linetax || 0),
                    linenet:
                      posConfigLocal?.disableOfflineBilling === "Y"
                        ? Math.abs(currentItem.nettotal) > 0
                          ? parseFloat(parseFloat(currentItem.nettotal - currentItem.taxAmount)?.toFixed(precision))
                          : 0
                        : currentItem.linenet || 0,
                    linegross: posConfigLocal?.disableOfflineBilling === "Y" ? (Math.abs(currentItem.nettotal) > 0 ? currentItem.nettotal : 0) : currentItem.linegross || 0,
                    linestd:
                      posConfigLocal?.disableOfflineBilling === "Y"
                        ? Math.abs(currentItem.nettotal) > 0
                          ? parseFloat(
                              parseFloat(taxIncludeFlag === "Y" ? currentItem.sunitprice : currentItem.sunitprice * (1 + currentItem.taxRate / 100) * currentItem.weight)?.toFixed(
                                precision
                              )
                            )
                          : 0
                        : currentItem.linestd || 0,
                    netunit:
                      posConfigLocal?.disableOfflineBilling === "Y"
                        ? Math.abs(currentItem.nettotal) > 0
                          ? parseFloat(
                              parseFloat(
                                isFinite(currentItem.nettotal / currentItem.weight)
                                  ? taxIncludeFlag === "Y"
                                    ? currentItem.nettotal / currentItem.weight / (1 + currentItem.taxRate / 100)
                                    : currentItem.salePrice
                                  : 0
                              )?.toFixed(precision)
                            )
                          : 0
                        : currentItem.netunit || 0,
                    grossunit:
                      posConfigLocal?.disableOfflineBilling === "Y"
                        ? Math.abs(currentItem.nettotal) > 0
                          ? parseFloat(
                              parseFloat(
                                isFinite(currentItem.nettotal / currentItem.weight)
                                  ? taxIncludeFlag === "Y"
                                    ? currentItem.nettotal / currentItem.weight / (1 + currentItem.taxRate / 100)
                                    : currentItem.salePrice
                                  : 0
                              )?.toFixed(precision)
                            ) * (currentItem.isReturn ? -1 : 1)
                          : 0
                        : currentItem.grossunit || 0,
                    grossstd:
                      posConfigLocal?.disableOfflineBilling === "Y"
                        ? Math.abs(currentItem.nettotal) > 0
                          ? roundTo(taxIncludeFlag === "Y" ? currentItem.sunitprice : currentItem.sunitprice * (1 + currentItem.taxRate / 100), precision)
                          : 0
                        : roundTo(currentItem.grossstd, precision) || 0,
                    grosslist:
                      posConfigLocal?.disableOfflineBilling === "Y"
                        ? Math.abs(currentItem.nettotal) > 0
                          ? roundTo(taxIncludeFlag === "Y" ? currentItem.listPrice : currentItem.listPrice * (1 + currentItem.taxRate / 100), precision)
                          : 0
                        : roundTo(currentItem.grosslist, precision) || 0,
                    netlist:
                      posConfigLocal?.disableOfflineBilling === "Y"
                        ? Math.abs(currentItem.nettotal) > 0
                          ? taxIncludeFlag === "Y"
                            ? currentItem.listPrice - parseFloat(parseFloat(currentItem.listPrice - currentItem.listPrice / (1 + currentItem.taxRate / 100))?.toFixed(precision))
                            : currentItem.listPrice
                          : 0
                        : parseFloat(currentItem.netList),
                    tax: [
                      {
                        csTaxID: isInterStateSync && currentItem.interTaxId ? currentItem.interTaxId : currentItem.tax || currentItem.cTaxId,
                        taxableAmt: parseFloat(parseFloat(childTaxableAmt).toFixed(precision) || 0),
                        taxAmt: parseFloat(parseFloat(currentItem.taxAmount || 0)?.toFixed(precision)),
                      },
                    ],
                    addons: productAddons,
                  });
                  childIDs.push({ sOrderlineID });
                }
                childItemMetaMap.set(item.name + "_" + j, {
                  name: item.name,
                  weight: item.weight,
                  discount: item.discount,
                  taxAmount: item.taxAmount,
                  realPrice: item.realPrice,
                  nettotal: item.nettotal,
                  children: childIDs,
                });
              } else {
                const parsedDate2 = safeParseDatetime(orders[i].orderTime);
                orderLines.push({
                  csBunitId: orders[i].items[j].csBunitId || null,
                  wastage: orders[i].items[j].wastage === "Y" ? "Y" : null,
                  sOrderlineID: uuidv4().replace(/-/g, "").toUpperCase(),
                  sOrderlineReturnId: orders[i].items[j].sOrderlineReturnId || null,
                  created: parsedDate2.format("YYYY-MM-DD HH:mm:ss"),
                  createdby: orders[i].createdBy,
                  updated: parsedDate2.format("YYYY-MM-DD HH:mm:ss"),
                  updatedby: orders[i].createdBy,
                  deliveryDate: orders[i]?.deliveryDate ? safeParseDatetime(orders[i].deliveryDate).format("YYYY-MM-DD HH:mm:ss") : parsedDate2.format("YYYY-MM-DD HH:mm:ss"),
                  sOrderId: orders[i].sOrderID,
                  line: (j + 1) * 10,
                  description: orders[i].items[j].notes || "",
                  mProductId: orders[i].items[j].productId || orders[i].items[j].mProductId,
                  csUomId: orders[i].items[j].uom || orders[i].items[j].uomData?.[0]?.csUomId,
                  csTaxId: isInterStateSync && orders[i].items[j].interTaxId ? orders[i].items[j].interTaxId : orders[i].items[j].tax || orders[i].items[j].cTaxId,
                  qty: orders[i].items[j].weight,
                  unitprice: parseFloat(orders[i].items[j].realPrice) * (orders[i].items[j].isReturn ? -1 : 1),
                  discount: orders[i].items[j].discount,
                  returnline: orders[i].items[j].isReturn || false,
                  returnQty: orders[i].items[j].isReturn ? Math.abs(orders[i].items[j].weight) : 0,
                  mBatchId: orders[i].items[j].mBatchId || orders[i].items[j].mBatch || null,
                  mBatchNo: orders[i].items[j].batchno || null,
                  mPricingruleId: orders[i].items[j].mPricingruleId || null,
                  batchedForSale: orders[i].items[j].batchedForSale,
                  batchedForStock: orders[i].items[j].batchedForStock,
                  batchedProduct: orders[i].items[j].batchedProduct,
                  salesRepId: orders[i].items[j]?.salesRepId || orders[i]?.salesRepId || matchedRep?.salesRepresentId || null,
                  multiPrice: orders[i].items[j].multiPrice === "Y" ? "Y" : "N",
                  discountTypeId: null,
                  discountAmount: null,
                  metaData: linesMeta,
                  unittax: unitTax > 0 ? parseFloat(parseFloat(unitTax)?.toFixed(precision)) : 0,
                  netstd: Math.abs(orders[i].items[j].netStd) ? parseFloat(orders[i].items[j].netStd) : 0,
                  listprice: orders[i].items[j].listPrice || 0,
                  linetax:
                    posConfigLocal?.disableOfflineBilling === "Y"
                      ? Math.abs(orders[i].items[j].nettotal) > 0
                        ? parseFloat(parseFloat(orders[i].items[j].taxAmount)?.toFixed(precision))
                        : 0
                      : parseFloat(orders[i].items[j].linetax || 0),
                  linenet:
                    posConfigLocal?.disableOfflineBilling === "Y"
                      ? Math.abs(orders[i].items[j].nettotal) > 0
                        ? parseFloat(parseFloat(orders[i].items[j].nettotal - orders[i].items[j].taxAmount)?.toFixed(precision))
                        : 0
                      : orders[i].items[j].linenet || 0,
                  linegross:
                    posConfigLocal?.disableOfflineBilling === "Y"
                      ? Math.abs(orders[i].items[j].nettotal) > 0
                        ? orders[i].items[j].nettotal
                        : 0
                      : orders[i].items[j].linegross || 0,
                  linestd:
                    posConfigLocal?.disableOfflineBilling === "Y"
                      ? Math.abs(orders[i].items[j].nettotal) > 0
                        ? parseFloat(
                            parseFloat(
                              taxIncludeFlag === "Y"
                                ? orders[i].items[j].sunitprice
                                : orders[i].items[j].sunitprice * (1 + orders[i].items[j].taxRate / 100) * orders[i].items[j].weight
                            )?.toFixed(precision)
                          )
                        : 0
                      : orders[i].items[j].linestd || 0,
                  netunit:
                    posConfigLocal?.disableOfflineBilling === "Y"
                      ? Math.abs(orders[i].items[j].nettotal) > 0
                        ? parseFloat(
                            parseFloat(
                              isFinite(orders[i].items[j].nettotal / orders[i].items[j].weight)
                                ? taxIncludeFlag === "Y"
                                  ? orders[i].items[j].nettotal / orders[i].items[j].weight / (1 + orders[i].items[j].taxRate / 100)
                                  : orders[i].items[j].salePrice
                                : 0
                            )?.toFixed(precision)
                          )
                        : 0
                      : orders[i].items[j].netunit || 0,
                  grossunit:
                    posConfigLocal?.disableOfflineBilling === "Y"
                      ? Math.abs(orders[i].items[j].nettotal) > 0
                        ? parseFloat(
                            parseFloat(
                              isFinite(orders[i].items[j].nettotal / orders[i].items[j].weight)
                                ? taxIncludeFlag === "Y"
                                  ? orders[i].items[j].nettotal / orders[i].items[j].weight / (1 + orders[i].items[j].taxRate / 100)
                                  : orders[i].items[j].salePrice
                                : 0
                            )?.toFixed(precision)
                          ) * (orders[i].items[j].isReturn ? -1 : 1)
                        : 0
                      : orders[i].items[j].grossunit || 0,
                  grossstd:
                    posConfigLocal?.disableOfflineBilling === "Y"
                      ? Math.abs(orders[i].items[j].nettotal) > 0
                        ? roundTo(taxIncludeFlag === "Y" ? orders[i].items[j].sunitprice : orders[i].items[j].sunitprice * (1 + orders[i].items[j].taxRate / 100), precision)
                        : 0
                      : roundTo(orders[i].items[j].grossstd, precision) || 0,
                  grosslist:
                    posConfigLocal?.disableOfflineBilling === "Y"
                      ? Math.abs(orders[i].items[j].nettotal) > 0
                        ? roundTo(taxIncludeFlag === "Y" ? orders[i].items[j].listPrice : orders[i].items[j].listPrice * (1 + orders[i].items[j].taxRate / 100), precision)
                        : 0
                      : roundTo(orders[i].items[j].grosslist, precision) || 0,
                  netlist:
                    posConfigLocal?.disableOfflineBilling === "Y"
                      ? Math.abs(orders[i].items[j].nettotal) > 0
                        ? taxIncludeFlag === "Y"
                          ? orders[i].items[j].listPrice -
                            parseFloat(parseFloat(orders[i].items[j].listPrice - orders[i].items[j].listPrice / (1 + orders[i].items[j].taxRate / 100))?.toFixed(precision))
                          : orders[i].items[j].listPrice
                        : 0
                      : parseFloat(orders[i].items[j].netList),
                  tax: [
                    {
                      csTaxID: isInterStateSync && orders[i].items[j].interTaxId ? orders[i].items[j].interTaxId : orders[i].items[j].tax || orders[i].items[j].cTaxId,
                      taxableAmt: parseFloat(parseFloat(lineTaxableAmt).toFixed(precision) || 0),
                      taxAmt: parseFloat(parseFloat(orders[i].items[j].taxAmount)?.toFixed(precision)),
                    },
                  ],
                  addons: productAddons,
                });
              }
            }

            // Build metaData, paymentsList, orderJson — then call posOrderProcessor1
            let metaData = [];
            const paymentsList = [];
            const paymentMetaData = [];
            let amtMax = -100000000000000000000;
            let maxFinPaymentMethod;
            const isReturnOrder = orders[i]?.isReturn === true || orders[i]?.isReturn === "Y";

            for (const payment of orders[i]?.payments?.flat() || []) {
              let pmtAmount = parseFloat(payment.amount || 0);
              if (isReturnOrder) pmtAmount = -Math.abs(pmtAmount);
              paymentMetaData.push({ [payment.name]: parseFloat(pmtAmount.toFixed(precision)) });
              if (amtMax < Math.abs(pmtAmount)) {
                amtMax = Math.abs(pmtAmount);
                maxFinPaymentMethod = payment.finPaymentmethodId;
              }
              // TIC-10307: UPI (paymentProvider "PLB"/API, same as Card) went through
              // processPLBPayment/pollTransactionStatus identically to Card and populated
              // cardPaymentData.payload/response, but this gate only matched "card" by exact
              // name, so the UPI line fell through to the generic branches below and lost its
              // request/response metaData entirely. Matching "upi" too (same equivalence
              // CardPaymentModal.js already applies at its own name check) restores parity.
              if (payment.name.toLowerCase() === "card" || payment.name.toLowerCase() === "upi") {
                if (payment.integratedPayment === true) {
                  paymentsList.push({
                    finPaymentmethodID: payment.finPaymentmethodId,
                    amount: parseFloat(pmtAmount.toFixed(precision)),
                    transactionResponse: orders[i].cardPaymentData?.response?.length > 0 ? orders[i].cardPaymentData?.response[0]?.resultMessage : "",
                    transactionId: null,
                    cardType: "",
                    cardNo: "",
                    transactionTime: dayjs(new Date()).format("YYYY-MM-DD HH:mm:ss"),
                    authorizationCode: null,
                    // cardPaymentData.payload/response accumulate one entry per completed
                    // transaction (a split/partial-amount card payment can complete more than
                    // one) — keep the single-object shape when there's only one, for backward
                    // compatibility, and serialize the full list when there are several so none
                    // of the split transactions are silently dropped.
                    metaData: [
                      {
                        key: "payload",
                        value:
                          orders[i].cardPaymentData?.payload?.length > 0
                            ? JSON.stringify(orders[i].cardPaymentData.payload.length === 1 ? orders[i].cardPaymentData.payload[0] : orders[i].cardPaymentData.payload)
                            : "",
                      },
                      {
                        key: "response",
                        value:
                          orders[i].cardPaymentData?.response?.length > 0
                            ? JSON.stringify(orders[i].cardPaymentData.response.length === 1 ? orders[i].cardPaymentData.response[0] : orders[i].cardPaymentData.response)
                            : "",
                      },
                    ],
                  });
                } else {
                  paymentsList.push({
                    finPaymentmethodID: payment.finPaymentmethodId,
                    amount: parseFloat(pmtAmount.toFixed(precision)),
                    transactionResponse: null,
                    transactionId: orders[i]?.cardPaymentData?.manual?.transactionId || null,
                    cardType: orders[i].cardPaymentData?.manual?.cardType || null,
                    cardNo: orders[i].cardPaymentData?.manual?.cardNumber || null,
                    transactionTime: dayjs(new Date()).format("YYYY-MM-DD HH:mm:ss"),
                    authorizationCode: payment.authorization || null,
                    metaData: [],
                  });
                }
              } else if (payment.isBankTransfer === "Y") {
                paymentsList.push({
                  finPaymentmethodID: payment.finPaymentmethodId,
                  amount: payment.amount,
                  financialAccountId: payment?.financialAccount || null,
                  transactionTime: payment?.transactionDate ? dayjs(payment.transactionDate).format("YYYY-MM-DD") : null,
                  imageUrl: payment?.financialFile || null,
                  transactionId: payment?.transactionId || null,
                });
              } else if (payment.isGiftCard) {
                // TIC-9958: Gift Card previously fell into the bare generic branch below
                // (finPaymentmethodID + amount only), so the Backoffice order's Payment
                // Plan details always showed blank Transaction ID/Card Type for it --
                // confirmed identically reproducing on both OFC-QA and OFC-Live via the
                // archived orderjson. cardNo/redemptionId come from useGiftCard.js's
                // redeemGiftCard(), which now attaches them per-redemption instead of
                // leaving this payment line as bare as Cash's.
                paymentsList.push({
                  finPaymentmethodID: payment.finPaymentmethodId,
                  amount: parseFloat(pmtAmount.toFixed(precision)),
                  transactionId: payment.redemptionId || null,
                  cardNo: payment.cardNo || null,
                  cardType: "Gift Card",
                  transactionTime: dayjs(new Date()).format("YYYY-MM-DD HH:mm:ss"),
                });
              } else if (payment.walletAlreadyDeducted === true) {
                // TIC-10095: same defect class as the Gift Card branch above (TIC-9958) --
                // Wallet was falling into the bare generic branch below with no reference,
                // so a resent order (client-perceived timeout on an already-succeeded
                // posOrderProcessor1 call, no local pre-persist in disableOfflineBilling="Y"
                // mode to detect the resend) had nothing for the backend to recognize this
                // exact wallet redemption by, and posted a second fin_receiptplan_details
                // row for it. walletTrxId is the wallet ledger's own transaction id,
                // captured at redemption time in WalletRedeemDrawer.js/WalletOtpModal.js.
                paymentsList.push({
                  finPaymentmethodID: payment.finPaymentmethodId,
                  amount: parseFloat(pmtAmount.toFixed(precision)),
                  transactionId: payment.walletTrxId || null,
                });
              } else {
                paymentsList.push({ finPaymentmethodID: payment.finPaymentmethodId, amount: parseFloat(pmtAmount.toFixed(precision)) });
              }
              // TSK-2934: generic payment-data-popup fields captured for this line
              // (any payment method, not just Card) ride on the same metaData
              // channel Card's payload/response already use above.
              // BUG-2372: also fills transactionId/transactionTime like Card/Bank Transfer do,
              // without overwriting a value a method-specific branch above already set.
              applyPaymentDataToLine(paymentsList[paymentsList.length - 1], payment.paymentData);
            }

            if (paymentMetaData.length > 0) {
              const details = [
                ...paymentMetaData,
                { "Bill Amount": orders[i]?.total },
                { change: orders[i]?.change },
                { "Round Off": orders[i]?.roundOff },
                { "Over Payment": orders[i]?.overPayedAmount },
              ];
              if (orders[i]?.tenderedAmount > 0) details.push({ "Cash Received": parseFloat(parseFloat(orders[i]?.tenderedAmount)?.toFixed(precision)) });
              // BUG-2372: popup-captured payment details (e.g. NEFT reference/date), appended last.
              details.push(...buildPaymentDataDetails(orders[i]?.payments?.flat()));
              metaData.push({ key: "Payment Details", value: JSON.stringify(details) });
            }

            const { tableName: tableDataLocal, dineIn: restaurantDineIn } = useRestaurantStore.getState();
            if (restaurantDineIn === "Y" && tableDataLocal) {
              for (const key of ["guestName", "guestType", "cwrFbTableId", "cwrFbsectionId", "noOfPersons", "referredBy", "orderId"]) {
                if (tableDataLocal?.hasOwnProperty(key)) metaData.push({ key, value: key === "noOfPersons" ? String(tableDataLocal[key]) : tableDataLocal[key] });
              }
            }
            if (orders[i]?.orderTimeDetails) {
              Object.keys(orders[i].orderTimeDetails).forEach((obj) => {
                metaData.push({ key: obj, value: orders[i].orderTimeDetails[obj] });
              });
            }
            if (orders[i]?.feedbackData) {
              metaData.push({ key: "Customer Feedback", value: JSON.stringify(orders[i].feedbackData) });
            }

            let tcsCharges = [];
            const saleTypeDataLocal = usePOSConfigStore.getState().saleTypeData || {};
            const rawOrderTcs = Number(orders[i]?.tcs);
            const rawActiveCartTcs = Number(activeCart?.tcs);
            const tcsValue =
              !isNaN(rawOrderTcs) && rawOrderTcs !== 0
                ? parseFloat(rawOrderTcs.toFixed(precision))
                : !isNaN(rawActiveCartTcs) && rawActiveCartTcs !== 0
                  ? parseFloat(rawActiveCartTcs.toFixed(precision))
                  : null;
            const tcsId = saleTypeDataLocal?.additionalCharges?.find((c) => c.chargeType === "TCS")?.additionalChargesId;
            if (tcsValue) tcsCharges.push({ amount: tcsValue, additionalChargesId: tcsId || null });

            if (tcsValue && Number(tcsValue) !== 0) {
              const tcsPerItem = (orders[i]?.items || []).map((item) => ({
                productId: item.productId || item.product?.mProductId,
                qty: item.weight || item.qty || 1,
                amount: item.itemTcs || 0,
                nettotal: item.nettotal || 0,
              }));
              metaData.push({ key: "tcsDetails", value: JSON.stringify({ total: tcsValue, items: tcsPerItem }) });
            }

            let giftCards = [];
            orders[i]?.giftCardData?.map((res) => {
              giftCards.push({ redeemRefNo: res.redemptionId || null, cardNo: res.number || null, referenceNo: res.refId || null });
            });
            {
              const giftLines = (Array.isArray(orders[i]?.items) ? orders[i].items : []).filter((line) => line?.isGiftCard || line?.giftCardType);
              if (giftCards.length > 0 || giftLines.length > 0) {
                safeTrace("order sync: gift card data on the order", {
                  order: orders[i]?.documentno || orders[i]?.documentNo || null,
                  isReturn: !!orders[i]?.isReturn,
                  giftCardRefs: giftCards.length,
                  lines: giftLines.map(describePrintLine),
                });
              }
            }

            if (Array.isArray(orders[i]?.items) && childItemMetaMap.size > 0) {
              const customMixMeta = [];
              childItemMetaMap.forEach((metaObj) => customMixMeta.push(metaObj));
              metaData.push({ key: "Custom Mix", value: JSON.stringify(customMixMeta) });
            }

            const { couponAdditionalInfo: couponData, discountAdditionalInfo: discountData, salesReturnData, billParkingData } = useTransactionStore.getState();
            if (couponData) metaData.push({ key: "Coupon Data", value: JSON.stringify(couponData) });
            if (discountData) metaData.push({ key: "Discount Data", value: JSON.stringify(discountData) });
            if (salesReturnData) metaData.push({ key: "Sales Return Data", value: JSON.stringify(salesReturnData) });
            if (billParkingData?.remarks?.trim()) metaData.push({ key: "Bill parking", value: JSON.stringify({ remarks: billParkingData.remarks.trim() }) });
            if (documents?.length > 0) {
              const panDoc = documents.find((doc) => doc.docName === "PAN Card");
              const passportDoc = documents.find((doc) => doc.docName === "Passport");
              if (panDoc?.docUrl) metaData.push({ key: "Pan Card", value: panDoc.docUrl });
              if (passportDoc?.docUrl) metaData.push({ key: "Passport", value: passportDoc.docUrl });
            }

            let storeOpsTillID;
            const tillSessionLocal = useSessionStore.getState().tillSession || {};
            const tillSessionId = tillSessionLocal?.tillSessionId || "";
            const tillEvent = tillSessionId ? await db.tillEvents.where("tillSessionId").equals(tillSessionId).first() : null;
            if (tillEvent) storeOpsTillID = tillEvent.storeDailyOpsTillid;

            const isLoyaltyEnabled =
              (posConfigLocal?.enableLoyalty || "N") === "N" || ((posConfigLocal?.enableLoyalty || "N") === "Y" && (posConfigLocal?.disablePOSLoyaltyCalculation || "N") === "N");
            if (!orders[i] || !orders[i].items) continue;
            const parsedDate3 = safeParseDatetime(orders[i].orderTime);
            const { refundReturnCustomerId } = useTransactionStore.getState();
            const returnCustomerIdFromMemory = activeCart.isReturn === true || activeCart.isReturn === "Y" ? refundReturnCustomerId : null;

            let sCustomerId = null;
            if (retailCustomerFields && retailCustomerFields.length > 0) {
              const sId = orders[i]?.customer?.sCustomer?.sCustomerID;
              const newB2bId = orders[i]?.customer?.newB2bCustomerId;
              if (orderIsB2bEnabled === "true") {
                sCustomerId = sId || newB2bId || null;
              } else {
                sCustomerId = null;
              }
              // DIAG (Issue-7): capture state driving sCustomerId/b2bOrder. Kept after the
              // fix below to verify in production that orderIsB2bEnabled (per-order,
              // snapshotted at creation) now matches liveIsB2bEnabled far less often than
              // it used to for orders whose queue-time session diverged from creation-time.
              try {
                console.log("[B2B-DIAG]", {
                  sOrderID: orders[i]?.sOrderID,
                  documentno: orders[i]?.documentno,
                  orderIsB2bEnabled,
                  liveIsB2bEnabled: useTransactionStore.getState().isB2bEnabled,
                  sId: sId || null,
                  newB2bId: newB2bId || null,
                  resolvedSCustomerId: sCustomerId,
                  customerCwrCustomerId: orders[i]?.customer?.cwrCustomerId || null,
                  isDefaultB2cCustomer: orders[i]?.customer?.cwrCustomerId === tillData?.tillAccess?.csBunit?.b2cCustomer?.cwrCustomerId,
                });
              } catch (diagErr) {
                console.warn("[B2B-DIAG] logging failed:", diagErr?.message);
              }
            } else {
              sCustomerId = orders[i]?.customer?.sCustomer?.sCustomerID || tillData.tillAccess.csBunit.b2cCustomer.sCustomer.sCustomerID;
            }

            const exchangeCartData = useCartStore.getState().exchangeCart || {};
            if (posConfigLocal) posConfigLocal.disablePOSLoyaltyCalculation = posConfigLocal?.disablePOSLoyaltyCalculation || "N";

            const orderJson = {
              sOrderID: orders[i].sOrderID,
              returnCustomerId: returnCustomerIdFromMemory?.cwrCustomerId || null,
              cSClientID: tillData.tillAccess.csClientId,
              cSBunitID: tillData.tillAccess.csBunit.csBunitId,
              created: parsedDate3.format("YYYY-MM-DD HH:mm:ss"),
              createdby: orders[i].createdBy,
              updated: parsedDate3.format("YYYY-MM-DD HH:mm:ss"),
              updatedby: orders[i].createdBy,
              csDoctypeId: tillData.tillAccess.csBunit.cwrCsDoctypeId,
              sCustomerId,
              // FK-3692,6319: prefer this order's own captured address ids (snapshotted at
              // creation in processOrderApproved) over the live localStorage read, which can
              // hold a DIFFERENT customer's address by the time this queued order gets
              // synced. Falls back to the live read only for orders saved before this fix.
              sCustomerBillingId:
                retailCustomerFields && retailCustomerFields.length > 0
                  ? resolveCapturedAddressId(orders[i], "billingId")
                  : tillData.tillAccess.csBunit.customerAddress?.sCustomerAddressID,
              sCustomerShippingId:
                retailCustomerFields && retailCustomerFields.length > 0
                  ? resolveCapturedAddressId(orders[i], "shippingId")
                  : tillData.tillAccess.csBunit.customerAddress?.sCustomerAddressID,
              sPricelistId: tillData.tillAccess.csBunit.cwrSpricelistId,
              documentno: orders[i].documentno,
              // TSK-3249: date portion must stay on the order's own business date (already
              // correct on orders[i].orderDate — stamped from tillSession.businessDate at
              // creation), not the live clock. Time-of-day still comes from the real orderTime.
              // BUG (fixed): nextBusinessDate is stored as its date parsed at a fixed
              // midnight time (Till.js's dayjs(..., "DD/MM/YYYY").format("YYYY-MM-DD HH:mm:ss")),
              // so using it as-is sent the SAME 00:00:00 timestamp for every order placed in a
              // post-close "continue selling" session -- not just the same day, the identical
              // dateordered value, defeating the adjacent F-29 fix's own stated goal of keeping
              // same-session orders distinguishable by time. Keep only the date portion and
              // combine it with the real order time, same as the fallback branch below.
              dateordered:
                (useSessionStore.getState().nextBusinessDate
                  ? `${dayjs(useSessionStore.getState().nextBusinessDate).format("YYYY-MM-DD")} ${parsedDate3.format("HH:mm:ss")}`
                  : null) ||
                (toISOBusinessDate(orders[i].orderDate)
                  ? `${toISOBusinessDate(orders[i].orderDate)} ${parsedDate3.format("HH:mm:ss")}`
                  : parsedDate3.format("YYYY-MM-DD HH:mm:ss")),
              datepromised: orders[i].deliveryDate ? safeParseDatetime(orders[i].deliveryDate).format("YYYY-MM-DD HH:mm:ss") : parsedDate3.format("YYYY-MM-DD HH:mm:ss"),
              csPaymenttermID: null,
              finPaymentmethodId: orders[i].layAway === "N" && maxFinPaymentMethod ? maxFinPaymentMethod : null,
              csCurrencyId,
              mWarehouseId: tillData.tillAccess.csBunit.mWarehouse.mWarehouseID,
              cwrLongitude: "",
              cwrLatitude: "",
              // BUG-2232: sync the cashier recorded on the order at sale time, so the
              // server-rendered receipt "Cashier" code matches who actually rang it.
              csUserId: orders[i].createdBy || resolveActingUserId(tillData),
              cwrB2cCustomerId: orders[i].customer?.cwrCustomerId ? orders[i].customer?.cwrCustomerId : orders[i].customer?.b2cCustomerId,
              orderreference: "",
              cwrPayref: "",
              cwrPayremarks: "",
              description: orders[i].description || "",
              storeDailyOpsTillid: storeOpsTillID,
              cwrTillId: tillData.tillAccess.cwrTill.cwrTillID,
              redemption: isLoyaltyEnabled ? orders[i].redemptionPoints : 0,
              accumulation: isLoyaltyEnabled ? orders[i].accumulationPoints : 0,
              redeemRefId: orders[i].referenceId,
              roundoff: parseFloat(parseFloat(orders[i].roundOff)?.toFixed(precision)) || 0,
              cwrProductQty: orders[i].totalQty,
              cwrProductCount: orders[i].totalQty,
              // BUG-2096: "Delivered" is a literal word, not one of the AK/FR/DI/CO/CC codes
              // the Aggregator screen's status lifecycle uses -- sending it for an aggregator
              // order clobbers whatever code upsertOfdOrderStatus/the UrbanPiper webhook already
              // set, and the order silently drops out of the Aggregator screen's live queue
              // since it no longer matches any recognized status. Only stamp the literal value
              // for non-aggregator channels, where nothing tracks ofd_status as a lifecycle.
              ofdStatus: orders[i]?.saleType?.isAggregator === "Y" ? "NEW" : "Delivered",
              ofdIspaid: "Y",
              posLoyaltyCalculation: posConfigLocal?.disablePOSLoyaltyCalculation,
              mPricingruleId: orders[i].mPricingruleId ?? null,
              cwrSaletypeId: orders[i]?.saleType?.cwrSaletypeId || saleTypeId,
              salesRepId: orders[i]?.salesRepId || matchedRep?.salesRepresentId || null,
              discAmount: parseFloat((parseFloat(orders[i].discount) || 0).toFixed(precision)),
              creditReferenceNo: orders[i]?.referenceId ?? null,
              creditAmount: `${orders[i].isReturn && orders[i].customer?.iscredit ? -orders[i].creditAmount : orders[i].creditAmount}`,
              creditSale: useTransactionStore.getState().isCreditSale === "true" && posConfigLocal?.generateInvoiceForCreditSale !== "Y" ? "Y" : "N",
              metaData,
              giftCard: giftCards,
              pricingCoupon: [
                {
                  mPricingCouponId: orders[i]?.couponInput?.[0]?.mPricingCouponId || null,
                  redemptionCount: orders[i].couponRedemptionCount ? parseInt(orders[i].couponRedemptionCount) : null,
                  referenceId: orders[i]?.couponInput?.[0]?.referenceId || null,
                  couponCode: orders[i]?.couponInput?.[0]?.couponCode || null,
                },
              ],
              orderTotal: orders[i].total,
              nettotal: parseFloat(parseFloat(orders[i].total - orders[i].tax - (parseFloat(orders[i].tcs) || 0))?.toFixed(precision)),
              taxamt: parseFloat(parseFloat(orders[i].tax)?.toFixed(precision)),
              isReturn: orders[i].items.some((f) => f.isReturn) ? "Y" : "N",
              sOrderReturnId: orders[i].items?.[0]?.sOrderReturnId || exchangeCartData?.returnsOrderId || null,
              layAway: orders[i].layAway || null,
              payments: (() => {
                // FK-3692,6319/BUG-1969-class leak: this loop can batch multiple queued
                // orders (line ~1884), so a live useTransactionStore read here reflects
                // whatever the till is doing NOW, not what THIS order (orders[i]) actually
                // was when the cashier completed it. A plain Cash Refund left pending
                // (offline gap, earlier sync failure) had its `payments` wiped to [] here
                // whenever the till had since moved on to an Exchange/Store-Credit return —
                // submitting a non-zero orderTotal with zero payment lines, which the
                // backend never returned a response for (confirmed: KWC JK/26-27/276,
                // -67980 Cash). Prefer this order's own snapshot (captured at creation
                // time in captureOrderCreationSnapshot) and fall back to the live read
                // only for legacy orders saved before the snapshot existed.
                const srt = orders[i]?.selectedReturnTypeSnapshot !== undefined ? orders[i].selectedReturnTypeSnapshot : useTransactionStore.getState().selectedReturnType;
                return (srt === "STORE_CREDIT" || srt === "EXCHANGE") && orders[i].isReturn ? [] : paymentsList;
              })(),
              tax: orders[i].combinedTaxRates ? orders[i].combinedTaxRates : [],
              line: orderLines,
              charges: tcsCharges,
              estimateId: orders[i]?.estimateId || null,
              b2bOrder: orderIsB2bEnabled === "true" ? "Y" : "N",
              revenueCategoryId: useTransactionStore.getState().selectedRevenueCategoryId || null,
            };

            // DIAG (Issue-7, expanded for GST-DIAG/BUG-1682/GPR-IRN): final values actually
            // sent to posOrderProcessor1. Upgraded from console.log (invisible on an
            // unattended till) to the durable sequenceAuditLog, and expanded beyond
            // sCustomerId/b2bOrder to every field that feeds IRN generation server-side
            // (customer id, billing/shipping address ids, tax lines, credit-sale flag) —
            // this is literally what got SENT, closing the loop against the customer-side
            // gst-diag-* logs (what was captured/saved before this point).
            try {
              logSequenceEvent(
                "gst-diag-orderjson-sent-regular",
                {
                  sOrderID: orderJson.sOrderID,
                  documentno: orderJson.documentno,
                  sCustomerId: orderJson.sCustomerId,
                  sCustomerBillingId: orderJson.sCustomerBillingId,
                  sCustomerShippingId: orderJson.sCustomerShippingId,
                  cwrB2cCustomerId: orderJson.cwrB2cCustomerId,
                  b2bOrder: orderJson.b2bOrder,
                  creditSale: orderJson.creditSale,
                  csDoctypeId: orderJson.csDoctypeId,
                  cwrSaletypeId: orderJson.cwrSaletypeId,
                  layAway: orderJson.layAway,
                  taxLineCount: Array.isArray(orderJson.tax) ? orderJson.tax.length : 0,
                  customerGstNo: orders[i]?.customer?.gstNo || null,
                },
                db
              );
            } catch (diagErr) {
              console.warn("[B2B-DIAG] logging failed:", diagErr?.message);
            }

            // DI-03: Validate order JSON self-consistency (nettotal + taxamt + tcs === orderTotal)
            {
              const tcsVal = tcsCharges.reduce((s, c) => s + (Number(c.amount) || 0), 0);
              const jsonCheck = assertOrderJSON({ nettotal: orderJson.nettotal, taxamt: orderJson.taxamt, tcs: tcsVal, orderTotal: orderJson.orderTotal }, "syncOrders");
              if (!jsonCheck.valid && jsonCheck.correctedNettotal !== undefined) {
                orderJson.nettotal = jsonCheck.correctedNettotal;
              }
            }

            // DI-34: Validate the outbound `line` array sums to the header total in this
            // SAME payload — DI-03 above only checks header self-consistency, it has no
            // visibility into whether a line got dropped from `orderJson.line` downstream
            // of wherever orderTotal/tax were computed. Non-blocking (see dataIntegrity.js).
            // BUG: header nettotal folds in serviceCharge/tip/deiveryCharge/packingCharge/
            // others (none of which are ever represented as a line in orderJson.line), so
            // without passing that charge total through, every order carrying a nonzero
            // charge false-positived here on every sync.
            {
              const orderCharges =
                (Number(orders[i].serviceCharge) || 0) +
                (Number(orders[i].tip) || 0) +
                (Number(orders[i].deiveryCharge) || 0) +
                (Number(orders[i].packingCharge) || 0) +
                (Number(orders[i].others) || 0);
              const linesCheck = assertOrderLinesMatchHeader(orderJson, "syncOrders", db, orderCharges);
              if (!linesCheck.valid && linesCheck.correctedTaxamt !== undefined) {
                orderJson.taxamt = linesCheck.correctedTaxamt;
              }
            }

            // DI-35 (BUG-2041/2044 follow-up): DI-03/DI-34 above compare fields against each
            // other, but both coerce a NaN input to 0 via `Number(x) || 0` before comparing --
            // a NaN header/line field can therefore still slip through both checks undetected
            // and reach the backend as a literal JSON null (JSON.stringify(NaN) === null),
            // which fails s_order/s_orderline's NOT NULL constraints and strands the order in
            // "pending" with no diagnosable reason. Purely diagnostic (see dataIntegrity.js).
            assertOrderPayloadFinite(orderJson, "syncOrders", db);

            const stringifiedData = JSON.stringify(orderJson);
            const newStringifiedFields = stringifiedData.replace(/\\"/g, '\\"');
            const newStringRequest = JSON.stringify(newStringifiedFields);

            const paramsInput = {
              // downloadUrl: backend (cw-retail-services) confirmed live on KWC Test with a
              // real populated S3 PDF URL -- posOrderProcessor1's own response now carries
              // the finished e-invoice PDF link directly, so resolveIrnDownload can skip
              // getEInvoiceURLs entirely instead of just retrying it faster.
              query: `mutation PosOrderProcessor1($tillId: String, $orderId: String, $documentNo: String, $orderJson: String) { posOrderProcessor1(tillId: $tillId, orderId: $orderId, documentNo: $documentNo, orderJson: $orderJson) { documentno status message eInvoice { status eInvoiceNo irn qrCode message errorMessage downloadUrl } } }`,
              variables: {
                tillId: tillData.tillAccess.cwrTill.cwrTillID,
                orderId: orders[i].sOrderID,
                documentNo: posConfigLocal?.disableOfflineBilling !== "Y" ? orders[i].documentno : null,
                orderJson: newStringifiedFields,
              },
            };
            if (posConfigLocal?.disableOfflineBilling === "Y") setLoading(true);

            // DI-16: Mark order as "syncing" (isSynced=3) to prevent duplicate sync on retry.
            // sOrderID is already included as orderId in the mutation for server-side dedup.
            try {
              const orderInDb = await db.orders.where("sOrderID").equals(orders[i].sOrderID).first();
              if (orderInDb) {
                await db.orders.where("sOrderID").equals(orders[i].sOrderID).modify({ isSynced: 3, lastSyncAttempt: new Date().toISOString() });
              }
            } catch (markErr) {
              console.warn("[DI-16] Failed to mark order as syncing:", markErr?.message);
            }

            try {
              const response = await posOrderProcessor1(paramsInput);
              const result = response?.data?.data?.posOrderProcessor1;
              const { status, documentno, message: srvMessage, eInvoice: eInvoiceResponse } = result || {};
              // Pure online mode (KWC): require both 200 status AND a server-issued documentno before treating as success.
              // Without documentno we cannot print a valid receipt — treat as failure and surface error.
              const isOnlineOnly = posConfigLocal?.disableOfflineBilling === "Y";
              // Exception: a row already persisted in db.orders before this attempt is a background
              // retry of a previously-created order (not a first-time submission) and already carries
              // its own confirmed documentno from creation time. A 200 reply with no documentno for one
              // of these is the server's dedup/already-booked ack, not a malformed response — trust it
              // using the order's existing documentno instead of looping forever waiting for a fresh
              // one the server will never send (it never created a new record on this call).
              let persistedOrderForRetry = null;
              if (isOnlineOnly && !documentno && status === "200" && !isCancelledOrder) {
                persistedOrderForRetry = await db.orders.where("sOrderID").equals(orders[i].sOrderID).first();
              }
              const isOnlineSuccess = status === "200" && (!isOnlineOnly || !!documentno || isCancelledOrder || !!persistedOrderForRetry?.documentno);
              if (eInvoiceResponse?.status === "FAI") {
                message.warning(eInvoiceResponse.errorMessage || "E-Invoice generation failed. Bill completed.");
              }
              let orderSuccessModalShown = false;
              if (isOnlineSuccess) {
                if (posConfigLocal?.disableOfflineBilling !== "Y") {
                  await db.orders.where("sOrderID").equals(orders[i].sOrderID).modify({ isSynced: 1 });
                } else if (posConfigLocal?.disableOfflineBilling === "Y" && posConfigLocal?.saveParkedBills !== "Y") {
                  const dbOrderExists = persistedOrderForRetry || (await db.orders.where("sOrderID").equals(orders[i].sOrderID).first());
                  if (dbOrderExists) {
                    // Never let a dedup ack's missing documentno null out the order's real, already-confirmed one.
                    const modifyFields = { isSynced: 1, documentno: documentno || dbOrderExists.documentno };
                    if (eInvoiceResponse) modifyFields.eInvoice = eInvoiceResponse;
                    await db.orders.where("sOrderID").equals(orders[i].sOrderID).modify(modifyFields);
                    if (!isCancelledOrder) {
                      window.dispatchEvent(new CustomEvent("restaurant:orderComplete", { detail: { sOrderID: orders[i].sOrderID } }));
                    }
                    // [BUG-1393] This shortcut branch (order already in db.orders when the
                    // WebSocket ORDER_UPSERT beat the HTTP response here) never reached
                    // processOrderApproved, so the restaurant table was never released —
                    // it stayed occupied/"Bill Requested" and fbOrderData stayed "IP"
                    // indefinitely. Perform the same table cleanup processOrderApproved
                    // would have, using only this order's own stamped tableId (never the
                    // live UI selection, which may have moved on by now).
                    try {
                      await cleanupTableForBackgroundSyncedOrder(dbOrderExists);
                    } catch (cleanupErr) {
                      console.warn("[BUG-1393] Background table cleanup failed (non-blocking):", cleanupErr?.message);
                    }
                  } else {
                    // BUG-1186: Capture exchange state before processOrderApproved clears it.
                    // FK-3692,6319/BUG-1969-class: prefer orders[i]'s own snapshot over the
                    // live store, same reasoning as the payments-array fix above -- this loop
                    // can reconcile a stale queued order while the till has since moved on to
                    // an unrelated live Exchange, and a live read here would wrongly chain that
                    // unrelated exchangeCart onto this order's return.
                    const syncExchReturnType = orders[i]?.selectedReturnTypeSnapshot !== undefined ? orders[i].selectedReturnTypeSnapshot : useTransactionStore.getState().selectedReturnType;
                    const syncExchCart = useCartStore.getState().exchangeCart;
                    await processOrderApproved(orders[i].sOrderID, documentno, 1, false, eInvoiceResponse, orders[i]);
                    if (syncTrigger !== "exchange" && syncExchReturnType === "EXCHANGE") {
                      if (syncExchCart?.items?.length > 0) {
                        // Show the RETURN order success popup first (awaiting user OK) before triggering exchange.
                        // This ensures: (1) return popup appears before exchange popup, and
                        // (2) the user's acknowledgement gives the backend time to commit the return
                        //     transaction before the exchange FK reference (s_orderid) is checked,
                        //     preventing the PostgreSQL s_orders_orderid constraint violation.
                        if (posConfigLocal?.disableOfflineBilling === "Y" && tillLayout === 4) {
                          orderSuccessModalShown = true;
                          await new Promise((resolve) => {
                            Modal.info({
                              icon: null,
                              okText: "Ok",
                              okButtonProps: {
                                style: {
                                  display: "inline-block",
                                  backgroundColor: "#4285F4",
                                  borderColor: "#4285F4",
                                  color: "#fff",
                                  borderRadius: "4px",
                                  padding: "8px 24px",
                                  height: "auto",
                                  fontWeight: "500",
                                },
                              },
                              content: (
                                <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "20px 0" }}>
                                  <div style={{ position: "relative", marginBottom: "16px" }}>
                                    <div
                                      style={{
                                        width: "80px",
                                        height: "90px",
                                        backgroundColor: "#FFF9C4",
                                        borderRadius: "8px",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        position: "relative",
                                      }}
                                    >
                                      <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                                        <div style={{ width: "40px", height: "4px", backgroundColor: "#4CAF50", borderRadius: "2px" }}></div>
                                        <div style={{ width: "30px", height: "4px", backgroundColor: "#E0E0E0", borderRadius: "2px" }}></div>
                                        <div style={{ width: "35px", height: "4px", backgroundColor: "#E0E0E0", borderRadius: "2px" }}></div>
                                        <div style={{ width: "25px", height: "4px", backgroundColor: "#E0E0E0", borderRadius: "2px" }}></div>
                                      </div>
                                    </div>
                                    <div
                                      style={{
                                        position: "absolute",
                                        bottom: "-5px",
                                        right: "-5px",
                                        width: "28px",
                                        height: "28px",
                                        backgroundColor: "#4CAF50",
                                        borderRadius: "50%",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                      }}
                                    >
                                      <span style={{ color: "#fff", fontSize: "16px", fontWeight: "bold" }}>✓</span>
                                    </div>
                                  </div>
                                  <p style={{ margin: "8px 0", fontSize: "14px", fontWeight: "400", color: "#666" }}>{documentno}</p>
                                  <p style={{ margin: "8px 0", fontSize: "20px", fontWeight: "600", color: "#0F0718" }}>Invoice Completed Successfully</p>
                                  <p style={{ margin: "8px 0 24px 0", fontSize: "14px", fontWeight: "400", color: "#999", textAlign: "center" }}>
                                    Your bill has been printed successfully.
                                    <br />
                                    Redirecting to new bill...
                                  </p>
                                  <div style={{ backgroundColor: "#F5F5F5", borderRadius: "8px", padding: "16px 32px", textAlign: "center", minWidth: "180px" }}>
                                    <p style={{ margin: "0 0 4px 0", fontSize: "12px", fontWeight: "400", color: "#999" }}>Total Amount</p>
                                    <p style={{ margin: "0", fontSize: "22px", fontWeight: "600", color: "#0F0718" }}>
                                      {tillData?.tillAccess?.csBunit?.currencies[0]?.currSymbol}{" "}
                                      {parseFloat(orders[i]?.total?.toString()).toFixed(tillData?.tillAccess?.csBunit?.currencies[0]?.prcPrecision || 2)}
                                    </p>
                                  </div>
                                </div>
                              ),
                              centered: true,
                              style: { top: 0 },
                              bodyStyle: { overflow: "hidden" },
                              closable: false,
                              onOk() {
                                Modal.destroyAll();
                                resolve();
                              },
                            });
                          });
                        }
                        setCart({ ...syncExchCart, isReturn: false });
                        await syncOrders("exchange");
                      }
                    }
                  }
                } else if (posConfigLocal?.saveParkedBills === "Y") {
                  // BUG-1186: Capture exchange state before processOrderApproved clears it.
                  // FK-3692,6319/BUG-1969-class: same per-order-snapshot preference as the
                  // sibling branch above -- see that comment for the full reasoning.
                  const parkedExchReturnType = orders[i]?.selectedReturnTypeSnapshot !== undefined ? orders[i].selectedReturnTypeSnapshot : useTransactionStore.getState().selectedReturnType;
                  const parkedExchCart = useCartStore.getState().exchangeCart;
                  const dbOrderExists = await db.orders.where("sOrderID").equals(orders[i].sOrderID).first();
                  if (dbOrderExists) {
                    const parkedModifyFields = { isSynced: 1, documentno };
                    if (eInvoiceResponse) parkedModifyFields.eInvoice = eInvoiceResponse;
                    await db.orders.where("sOrderID").equals(orders[i].sOrderID).modify(parkedModifyFields);
                    if (!isCancelledOrder) {
                      window.dispatchEvent(new CustomEvent("restaurant:orderComplete", { detail: { sOrderID: orders[i].sOrderID } }));
                    }
                    // [BUG-1393] Same shortcut-branch gap as the sibling normal-online-billing
                    // branch above, for the parked-bills flow — see that comment for the full
                    // root cause. Release the table using only the order's own stamped identity.
                    try {
                      await cleanupTableForBackgroundSyncedOrder(dbOrderExists);
                    } catch (cleanupErr) {
                      console.warn("[BUG-1393] Background table cleanup failed (non-blocking):", cleanupErr?.message);
                    }
                  } else if (activeCart?.isRetrived === "Y") {
                    const cartPayload = {
                      csBunitId: tillData?.tillAccess?.csBunit?.csBunitId,
                      cwrPosCartId: useCartStore.getState().cart?.cwr_pos_cart_id || null,
                      cwrTillId: tillData?.tillAccess?.cwrTill?.cwrTillID,
                      status: "CO",
                      cartItems: useCartStore.getState().cart,
                    };
                    const resp = await upsertCart(cartPayload);
                    // F-12/BUG-1963/1966: pass orders[i] as the orderSnapshot, matching the sibling
                    // else branch below — without it, processOrderApproved falls back to the live
                    // cart, which can belong to a different sale by the time this async call lands.
                    if (resp?.status === 200) await processOrderApproved(orders[i].sOrderID, documentno, 1, false, eInvoiceResponse, orders[i]);
                    else message.warning("Something is wrong in upsertCart service");
                  } else {
                    await processOrderApproved(orders[i].sOrderID, documentno, 1, false, eInvoiceResponse, orders[i]);
                  }
                  if (syncTrigger !== "exchange" && parkedExchReturnType === "EXCHANGE") {
                    if (parkedExchCart?.items?.length > 0) {
                      if (posConfigLocal?.disableOfflineBilling === "Y" && tillLayout === 4) {
                        orderSuccessModalShown = true;
                        await new Promise((resolve) => {
                          Modal.info({
                            icon: null,
                            okText: "Ok",
                            okButtonProps: {
                              style: {
                                display: "inline-block",
                                backgroundColor: "#4285F4",
                                borderColor: "#4285F4",
                                color: "#fff",
                                borderRadius: "4px",
                                padding: "8px 24px",
                                height: "auto",
                                fontWeight: "500",
                              },
                            },
                            content: (
                              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "20px 0" }}>
                                <div style={{ position: "relative", marginBottom: "16px" }}>
                                  <div
                                    style={{
                                      width: "80px",
                                      height: "90px",
                                      backgroundColor: "#FFF9C4",
                                      borderRadius: "8px",
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      position: "relative",
                                    }}
                                  >
                                    <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                                      <div style={{ width: "40px", height: "4px", backgroundColor: "#4CAF50", borderRadius: "2px" }}></div>
                                      <div style={{ width: "30px", height: "4px", backgroundColor: "#E0E0E0", borderRadius: "2px" }}></div>
                                      <div style={{ width: "35px", height: "4px", backgroundColor: "#E0E0E0", borderRadius: "2px" }}></div>
                                      <div style={{ width: "25px", height: "4px", backgroundColor: "#E0E0E0", borderRadius: "2px" }}></div>
                                    </div>
                                  </div>
                                  <div
                                    style={{
                                      position: "absolute",
                                      bottom: "-5px",
                                      right: "-5px",
                                      width: "28px",
                                      height: "28px",
                                      backgroundColor: "#4CAF50",
                                      borderRadius: "50%",
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                    }}
                                  >
                                    <span style={{ color: "#fff", fontSize: "16px", fontWeight: "bold" }}>✓</span>
                                  </div>
                                </div>
                                <p style={{ margin: "8px 0", fontSize: "14px", fontWeight: "400", color: "#666" }}>{documentno}</p>
                                <p style={{ margin: "8px 0", fontSize: "20px", fontWeight: "600", color: "#0F0718" }}>Invoice Completed Successfully</p>
                                <p style={{ margin: "8px 0 24px 0", fontSize: "14px", fontWeight: "400", color: "#999", textAlign: "center" }}>
                                  Your bill has been printed successfully.
                                  <br />
                                  Redirecting to new bill...
                                </p>
                                <div style={{ backgroundColor: "#F5F5F5", borderRadius: "8px", padding: "16px 32px", textAlign: "center", minWidth: "180px" }}>
                                  <p style={{ margin: "0 0 4px 0", fontSize: "12px", fontWeight: "400", color: "#999" }}>Total Amount</p>
                                  <p style={{ margin: "0", fontSize: "22px", fontWeight: "600", color: "#0F0718" }}>
                                    {tillData?.tillAccess?.csBunit?.currencies[0]?.currSymbol}{" "}
                                    {parseFloat(orders[i]?.total?.toString()).toFixed(tillData?.tillAccess?.csBunit?.currencies[0]?.prcPrecision || 2)}
                                  </p>
                                </div>
                              </div>
                            ),
                            centered: true,
                            style: { top: 0 },
                            bodyStyle: { overflow: "hidden" },
                            closable: false,
                            onOk() {
                              Modal.destroyAll();
                              resolve();
                            },
                          });
                        });
                      }
                      setCart({ ...parkedExchCart, isReturn: false });
                      await syncOrders("exchange");
                    }
                  }
                }
                // Only reset UI state for online-only billing (disableOfflineBilling === "Y").
                // Background sync (offline billing) already cleared UI state when the order was first created locally.
                const isPrepareBillCreate = orders[i]?.isPrepareBillCreate === "Y";
                if (posConfigLocal?.disableOfflineBilling === "Y" && !isPrepareBillCreate) {
                  if (setCustomMixItems) setCustomMixItems([]);
                  useTransactionStore.getState().clearTransaction();
                  if (customerForm) customerForm.resetFields();
                  useRestaurantStore.getState().setTableName(null);
                  if (setSelectedCoupon) setSelectedCoupon(null);
                  if (setDisplayCustomerSearch) setDisplayCustomerSearch(false);
                  if (setIsRefundWalletOpen) setIsRefundWalletOpen(false);
                  if (setDocuments) setDocuments([]);
                }
                localStorage.removeItem("isPreparedBill");
                if (orders[i]?.tableId) {
                  localStorage.removeItem(`isPreparedBill_${orders[i].tableId}`);
                  localStorage.removeItem(`isFoodServed_${orders[i].tableId}`);
                }
                localStorage.removeItem("customerAddressIds");
                useTransactionStore.setState({
                  refundCustomerEdit: null,
                });
                if (posConfigLocal?.generateLabelforReturnItems === "Y" && orders[i]?.isReturn === true) {
                  for (const item of orders[i]?.items) {
                    const qrData = JSON.stringify({
                      productcode: item?.value || "N/A",
                      batchno: item?.batchno || "N/A",
                    });

                    const totalStickers = Math.abs(item.weight || 1) * 1;

                    for (let si = 0; si < totalStickers; si++) {
                      const printData = {
                        elements: [
                          {
                            type: "qrcode",
                            content: qrData,
                            x: 8,
                            y: 15,
                            moduleSize: 4,
                          },
                          {
                            type: "text",
                            content: item.productBrandName || "N/A",
                            x: 260,
                            y: 20,
                            style: ["bold"],
                            addNewline: false,
                          },
                          {
                            type: "text",
                            content: item.value || "N/A",
                            x: 260,
                            y: 50,
                            style: ["bold"],
                            addNewline: false,
                          },
                          {
                            type: "text",
                            content: item.batchno || 0,
                            x: 260,
                            y: 80,
                            style: ["bold"],
                            addNewline: false,
                          },
                          {
                            type: "text",
                            content: item.realPrice || 0,
                            x: 260,
                            y: 105,
                            style: ["bold"],
                            addNewline: false,
                          },
                        ],
                        settings: {
                          copies: 1,
                          labelWidth: 80,
                          labelHeight: 40,
                        },
                      };

                      try {
                        const res = await fetch(`${printerURL}labelprinter`, {
                          method: "POST",
                          headers: { "Content-Type": "application/json" },
                          body: JSON.stringify(printData),
                        });

                        if (!res.ok) {
                          throw new Error(`HTTP ${res.status}`);
                        }
                      } catch (err) {
                        console.error(`Sticker ${si + 1}/${totalStickers} → FAILED`, err);
                      }

                      await new Promise((resolve) => setTimeout(resolve, 200));
                    }
                  }
                }
                if (posConfigLocal?.disableOfflineBilling === "Y" && !isPrepareBillCreate) {
                  setInvoiceProcessing?.("SUCCESS");
                  setPendingIRNOrderId?.(orders[i].sOrderID);
                  setCompletedOrderId?.(documentno);
                }
                let rfidData = [];
                // TSK-2173: Skip IRN for pre-order advance payments — generate only on final sale
                const isPreOrderAdvance = window.location.pathname.includes("/preorder") || (cart.layAway === "Y" && cart.preOrder !== "Y");
                // BUG-2164: a Prepare-Bill creation (paid:0, order not actually settled yet)
                // must never show "Invoice Completed Successfully" / trigger e-invoice
                // generation — those belong to the real settlement call, once the customer
                // has actually paid. Mirrors processOrderApproved's own isPrepareBillCreate
                // early-return (~L1257), which this posOrderProcessor1 path lacked.
                if (
                  posConfigLocal?.disableOfflineBilling === "Y" &&
                  posConfigLocal?.generateEInvoice === "Y" &&
                  !isPrepareBillCreate &&
                  !isPreOrderAdvance &&
                  (localStorage.getItem("isB2bEnabled") === "true" || useTransactionStore.getState().isB2bEnabled === "true")
                ) {
                  // With both Till Template flags Y (Online Mode + Generate E-Invoice),
                  // posOrderProcessor1 generates the e-invoice synchronously and its eInvoice
                  // block is authoritative -- polling getEInvoiceDetails afterward wouldn't
                  // discover a different answer. So a non-SUC response here (eInvoiceResponse
                  // missing, or status "FAI"/anything else) IS the final result, not a
                  // not-yet-resolved case to retry -- surface it as failed immediately instead
                  // of starting a doomed polling loop.
                  setShowIRNModal?.(true);
                  // GST-DIAG (BUG-1682/GPR-IRN): the backend's own eInvoice sub-object,
                  // returned inline with posOrderProcessor1's response. Previously only
                  // shown transiently in the IRN failure modal (setIrnFailureMessage) and
                  // never persisted — this is the actual backend-reported reason for THIS
                  // specific IRN attempt (e.g. "gstin_of_consignee: Enter the correct
                  // gstin of consignee", matching the known GPR/KWC failure reasons).
                  try {
                    logSequenceEvent(
                      "gst-diag-einvoice-response",
                      {
                        sOrderID: orders[i]?.sOrderID,
                        documentno: orderJson.documentno,
                        status: eInvoiceResponse?.status || null,
                        eInvoiceNo: eInvoiceResponse?.eInvoiceNo || null,
                        irn: eInvoiceResponse?.irn || null,
                        message: eInvoiceResponse?.message || null,
                        errorMessage: eInvoiceResponse?.errorMessage || null,
                        b2bOrder: orderJson.b2bOrder,
                        sCustomerId: orderJson.sCustomerId,
                      },
                      db
                    );
                  } catch (diagErr) {
                    // swallow — diagnostics must never affect order completion
                  }
                  if (eInvoiceResponse?.status === "SUC") {
                    resolveIrnDownload(orders[i].sOrderID, eInvoiceResponse.eInvoiceNo, eInvoiceResponse.downloadUrl).finally(() => setIrnLoading?.(false));
                  } else {
                    setIrnStatus?.("FAILED");
                    setIrnGenerating?.("FAILED");
                    setIrnFailureMessage?.(eInvoiceResponse?.errorMessage || eInvoiceResponse?.message || "E-Invoice generation failed");
                    setIrnLoading?.(false);
                  }
                } else if (posConfigLocal?.disableOfflineBilling === "Y" && !isPrepareBillCreate && tillLayout === 4 && !orderSuccessModalShown) {
                  Modal.info({
                    icon: null,
                    okText: "Ok",
                    okButtonProps: {
                      style: {
                        display: "inline-block",
                        backgroundColor: "#4285F4",
                        borderColor: "#4285F4",
                        color: "#fff",
                        borderRadius: "4px",
                        padding: "8px 24px",
                        height: "auto",
                        fontWeight: "500",
                      },
                    },
                    content: (
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          justifyContent: "center",
                          padding: "20px 0",
                        }}
                      >
                        {/* Invoice Icon with Checkmark */}
                        <div style={{ position: "relative", marginBottom: "16px" }}>
                          <div
                            style={{
                              width: "80px",
                              height: "90px",
                              backgroundColor: "#FFF9C4",
                              borderRadius: "8px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              position: "relative",
                            }}
                          >
                            <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                              <div style={{ width: "40px", height: "4px", backgroundColor: "#4CAF50", borderRadius: "2px" }}></div>
                              <div style={{ width: "30px", height: "4px", backgroundColor: "#E0E0E0", borderRadius: "2px" }}></div>
                              <div style={{ width: "35px", height: "4px", backgroundColor: "#E0E0E0", borderRadius: "2px" }}></div>
                              <div style={{ width: "25px", height: "4px", backgroundColor: "#E0E0E0", borderRadius: "2px" }}></div>
                            </div>
                          </div>
                          <div
                            style={{
                              position: "absolute",
                              bottom: "-5px",
                              right: "-5px",
                              width: "28px",
                              height: "28px",
                              backgroundColor: "#4CAF50",
                              borderRadius: "50%",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <span style={{ color: "#fff", fontSize: "16px", fontWeight: "bold" }}>✓</span>
                          </div>
                        </div>

                        {/* Invoice Number */}
                        <p style={{ margin: "8px 0", fontSize: "14px", fontWeight: "400", color: "#666" }}>{documentno}</p>

                        {/* Success Title */}
                        <p style={{ margin: "8px 0", fontSize: "20px", fontWeight: "600", color: "#0F0718" }}>Invoice Completed Successfully</p>

                        {/* Success Message */}
                        <p style={{ margin: "8px 0 24px 0", fontSize: "14px", fontWeight: "400", color: "#999", textAlign: "center" }}>
                          Your bill has been printed successfully.
                          <br />
                          Redirecting to new bill...
                        </p>

                        {/* Total Amount Box */}
                        <div
                          style={{
                            backgroundColor: "#F5F5F5",
                            borderRadius: "8px",
                            padding: "16px 32px",
                            textAlign: "center",
                            minWidth: "180px",
                          }}
                        >
                          <p style={{ margin: "0 0 4px 0", fontSize: "12px", fontWeight: "400", color: "#999" }}>Total Amount</p>
                          <p style={{ margin: "0", fontSize: "22px", fontWeight: "600", color: "#0F0718" }}>
                            {tillData?.tillAccess?.csBunit?.currencies[0]?.currSymbol}{" "}
                            {parseFloat(orders[i]?.total?.toString()).toFixed(tillData?.tillAccess?.csBunit?.currencies[0]?.prcPrecision || 2)}
                          </p>
                        </div>
                      </div>
                    ),
                    centered: true,
                    style: {
                      top: 0,
                    },
                    bodyStyle: {
                      overflow: "hidden",
                    },
                    closable: false,
                  });
                } else if (posConfigLocal?.disableOfflineBilling === "Y" && tillLayout === 2) {
                  message.success(`Order ${documentno} Created Successfully`);
                }
                localStorage.removeItem("isB2bEnabled");
                // FK-3692,6319/BUG-1969: clearing localStorage alone leaves the Zustand
                // isB2bEnabled value live, so the next checkout on this till (often a
                // walk-in) inherits this customer's B2B flag. Every other checkout-cleanup
                // site in the repo already pairs these two calls (useAuthStore.js,
                // useSuperMarketHandlers.js, customer.js, useCustomerOperations.js,
                // AddCustomerDrawer.js) -- this call site and the one below it were the
                // only two missing it.
                useTransactionStore.getState().setIsB2bEnabled(null);
                // TIC-6900 follow-up: same leak class as isB2bEnabled above, on the sibling
                // Zustand flag isCreditSale -- paymentController.js reads this live value to
                // gate invoice-generation behavior, so leaving it set here let the next
                // ordinary sale on this till evaluate as a credit sale. isPanCustomer/
                // isNriBilling have no Zustand counterpart (useTransactionStore.js only
                // tracks isCreditSale/isB2bEnabled), so the localStorage removeItem calls
                // below are already sufficient for those two.
                useTransactionStore.getState().setIsCreditSale(null);
                localStorage.removeItem("isPanCustomer");
                localStorage.removeItem("isNriBilling");
                localStorage.removeItem("isCreditSale");
                try {
                  const attemptsMap = JSON.parse(localStorage.getItem("plbTransactionAttempts") || "{}");
                  const prefix = `${orders[i].documentno}:`;
                  let changed = false;
                  Object.keys(attemptsMap).forEach((key) => {
                    if (key.startsWith(prefix)) {
                      delete attemptsMap[key];
                      changed = true;
                    }
                  });
                  if (changed) localStorage.setItem("plbTransactionAttempts", JSON.stringify(attemptsMap));
                } catch {
                  // non-blocking — never let this interrupt order finalization
                }
                // "Same as Invoicing" toggle reset -- belongs at order completion, not at
                // customer save time (see customer.js).
                if (typeof setSwitchToggle === "function") setSwitchToggle(false);

                const rfidProducts = await db.rfidData.toArray();
                rfidProducts.forEach((ele) => {
                  if (ele.tag_status === "SO")
                    rfidData.push(
                      ` { tagValue: "${ele.tag_value}" taggingDate: null batchNumber: null batchId: null warehouseId: null tagStatus: "SO" lastScannedDate: "${dayjs(new Date()).format("DD-MM-YYYY HH:mm:ss")}" scannedBy: null expirydate: null customAttribute: null tagType: null productCode: "${ele.product_code}" }`
                    );
                });
                await RFIDTag(rfidData);
              } else {
                console.error("Failed Order Sync ====> ", response);
                try {
                  logSyncDiagThrottled(
                    "sync-diag-regular-rejected",
                    {
                      sOrderID: orders[i]?.sOrderID,
                      documentno: orders[i]?.documentno,
                      status,
                      serverMessage: srvMessage,
                      syncAttempts: orders[i]?.syncAttempts,
                    },
                    db
                  );
                } catch (e) {
                  // swallow — diagnostics must never affect order processing
                }
                if (isOnlineOnly) {
                  // Pure online mode (KWC): order was never persisted locally (Dileep's commit removed pre-persist).
                  // Server rejected or returned malformed response. Surface error so the user can retry — never silently complete.
                  const errMsg =
                    status === "200" && !documentno
                      ? "Server returned no document number. Order not completed. Please retry."
                      : srvMessage || "Order failed to sync to server. Please check connection and retry.";
                  message.error(errMsg);
                  orderState.current = 0;
                  setActiveOrderProcess(false);
                  // Unblock customer display so it can return to normal mode
                  window.dispatchEvent(new CustomEvent("cd:feedbackDone"));
                } else {
                  // Offline-allowed mode: track sync attempts in db.orders for background retry
                  const syncFailedCount = parseInt(orders[i].syncAttempts) + 1;
                  if (parseInt(orders[i].syncAttempts) < 100) await db.orders.where("sOrderID").equals(orders[i].sOrderID).modify({ syncAttempts: syncFailedCount });
                  else {
                    await db.orders.where("sOrderID").equals(orders[i].sOrderID).modify({ isSynced: 2 });
                    notifySyncFailure({ sOrderID: orders[i].sOrderID, documentno: orders[i].documentno, orderType: "regular", syncAttempts: syncFailedCount });
                  }
                }
              }
            } catch (error) {
              console.error("Order sync error:", error);
              try {
                logSyncDiagThrottled(
                  "sync-diag-regular-threw",
                  {
                    sOrderID: orders[i]?.sOrderID,
                    documentno: orders[i]?.documentno,
                    errorMessage: error?.message,
                  },
                  db
                );
              } catch (e) {
                // swallow — diagnostics must never affect order processing
              }
              // DI-16: On definite failure (not timeout), reset isSynced from 3 back to 0 so it can be retried.
              // For timeout/ambiguous errors, keep isSynced=3 to prevent immediate duplicate send.
              const isTimeout = error?.code === "ECONNABORTED" || error?.message?.includes("timeout");
              if (!isTimeout) {
                try {
                  const orderExists = await db.orders.where("sOrderID").equals(orders[i].sOrderID).first();
                  if (orderExists && orderExists.isSynced === 3) {
                    await db.orders.where("sOrderID").equals(orders[i].sOrderID).modify({ isSynced: 0 });
                  }
                } catch (resetErr) {
                  console.warn("[DI-16] Failed to reset order sync status:", resetErr?.message);
                }
              } else {
                console.warn(`[DI-16] Order ${orders[i].sOrderID} sync timed out — keeping isSynced=3 to prevent duplicate. Will retry on next sync cycle.`);
              }
              if (posConfigLocal?.disableOfflineBilling === "Y") {
                // Pure online mode: network error / timeout / unparseable response.
                // Order was NEVER sent successfully — must NOT print, must surface error, must allow retry.
                message.error("Network error — order not completed. Please check connection and retry.");
                orderState.current = 0;
                setActiveOrderProcess(false);
                window.dispatchEvent(new CustomEvent("cd:feedbackDone"));
              }
            } finally {
              setLoading(false);
            }
          } else {
            const isCreditSale = orders[i]?.creditSale === "Y";
            // TSK-2935: isPrepareBillCancelled (computed above) has paid:0/docType:"Invoice",
            // so it satisfies neither of the two existing layAway/credit conditions below —
            // OR it in explicitly so a cancelled Prepare-Bill order still reaches the confirm
            // branch instead of falling through with nothing sent at all.
            const isLayawayOrder =
              (orders[i]?.layAway === "Y" && (orders[i]?.paid !== 0 || orders[i]?.docType === "Advance")) || (isCreditSale && orders[i]?.paid !== 0) || isPrepareBillCancelled;
            // [ADV-ORDER-DIAG] This order fell through the isRegularOrder check above and
            // is now being classified here — isLayawayOrder=true means confirmPOSLayawayOrder1
            // is about to be called for it.
            if (isLayawayOrder) {
              const orderLines = [];
              const childItemMetaMap = new Map();
              const parsedDate4 = safeParseDatetime(orders[i].orderTime);
              // BUG (Advance->Invoice shipment date): this confirmPOSLayawayOrder1 call fires
              // for a first-time Advance creation, a partial top-up, AND the final full-payment
              // call that actually converts the Advance into an Invoice+Shipment. Only in that
              // last case should datepromised/deliveryDate be reset to today — a first-time
              // create (even if paid in full) or an in-progress top-up must keep the customer's
              // requested date. isRetrievedAdvance (already used above to route create-vs-confirm)
              // is false on first-time creation, so gating on it here avoids clobbering that case.
              //
              // BUG-2105: this used to be `paidAmount + advancePayment >= totalAmount` -- a pure
              // payment-total check. "Fully paid" is a standing STATE, not an event, so an order
              // paid in full up front finalized on every later retrieve+resubmit even with zero
              // new activity (e.g. retrieve -> park -> retrieve -> click "Create Advance" again),
              // which is exactly the opposite of what BUG-2069 needed (a fully-paid advance order
              // must finalize once genuinely completed). The real signal is which action the
              // cashier took: "Complete Order" (SMPaymentBar.js/PaymentPanel.js call
              // `processOrder()` with no argument) explicitly finalizes; "Create Advance" (always
              // `processOrder("layaway")`) never does, no matter how much is already paid.
              //
              // NOT simply `param === undefined`: useCoreEffects.js's tillLayout===2
              // auto-process-on-full-payment effect calls `processOrder(cart.sOrderID)` for a
              // LIVE, interactive completion (regular checkout OR cart.preOrder==="Y") -- passing
              // the order's own sOrderID, not undefined. That's a genuine finalizing action and
              // must not be misclassified the same as background reconciliation just because
              // param happens to be a truthy string.
              //
              // FIX (live crash, confirmed via browser console -- "ReferenceError: param is not
              // defined" firing on every 10s auto-sync retry for every pending Advance/Layaway
              // order, e.g. AJ2 ADV/47 and Q001/16279): this block runs inside syncOrders(syncTrigger),
              // which never receives `param` or `isSynced` -- those are processOrderApproved's own
              // parameters, not syncOrders'. Reading orders[i].isCompleteOrderAction instead, stamped
              // onto the order record by processOrderApproved at the moment `param` was actually
              // available (see the Object.assign near the top of that function), gives every later
              // retry -- including the "auto" 10s tick, which must NOT reclassify a still-open
              // Create Advance order as a completion just because its own trigger isn't "layaway" --
              // a stable, correct answer instead of an undefined reference. The isSynced===1 exclusion
              // from the old expression is redundant here: this loop only ever iterates orders[i] rows
              // with isSynced 0 or 2 (the pending-orders filter above), so a reconciliation save
              // (isSynced:1) never reaches this classification in the first place.
              const isCompleteOrderClicked = orders[i]?.isCompleteOrderAction === true;
              const isFinalizingAdvanceToInvoice = resolveIsFinalizingAdvanceToInvoice(isRetrievedAdvance, isCompleteOrderClicked);
              // BUG-2371: a removed synced line + the same product re-added must ship as ONE
              // line (the new one, carrying the old sOrderlineID), or the backend folds both
              // into one s_orderline row and the zero line wipes the re-added qty/amounts.
              // See pairReaddedAdvanceLines. Payload-only -- orders[i].items is not mutated.
              const { idForLine: readdedLineIds, skipLineIds: replacedZeroLineIds } = pairReaddedAdvanceLines(orders[i].items);
              for (let j = 0; j < orders[i].items.length; j += 1) {
                if (orders[i].items[j]?.isCanceled === "Y") continue;
                // BUG-2044: same "hidden, not real" line as the regular path above --
                // this path (advance/layaway) is exactly where cart.advancePayment > 0
                // applies, so it's the most direct route to a weight===0 line surviving
                // into the outbound order. See the regular-path comment for full context.
                //
                // Exception -- BUG-2105/Chocolala orphan-line follow-up: a weight===0 item
                // that already carries a sOrderlineID was synced to the backend by an
                // EARLIER confirm call on this same Advance/Layaway order (only retrieval of
                // an already-persisted order ever populates this field -- a brand-new item
                // never has one). If we skip it here too, the backend never learns the line
                // was deleted: covertToOrderLines (cw-retail-services) only inserts/updates
                // lines present in the payload, never deletes one that's merely absent, so
                // the stale pre-deletion row (original qty/amount) lingers forever in
                // s_orderline -- confirmed live and at the DB level on Chocolala TEST
                // (order sOrderID 4E84E8DE...924: "Advance from Customer" row untouched
                // after being deleted and the order re-confirmed with a different item).
                // Sending it still, at qty:0, lets the backend's own (order, line, product)
                // match resolve to that existing row and zero it via its normal UPDATE path.
                // Safe for BUG-2044's original incident (Dadus HAO/44561): that order's
                // zeroed line had never been synced before -- single-line, first-ever
                // submission -- so it never carried a sOrderlineID and is still excluded
                // here. Also confirmed backend-safe for the eventual Advance->Invoice
                // completion: createInvoiceLinesForOrder (POSInvoiceDaoImpl) explicitly
                // skips any line with qty==0, and createAutoBOM/createAutoRecipe filter on
                // SUM(qty) > 0, so a zeroed line can never surface as a phantom invoice line
                // or trigger phantom production -- handleStock (the only other consumer of
                // sOrder.getLine()) is disabled entirely in this backend version.
                //
                // Scoped to plain (non-grouped) items only -- excludes anything with
                // item.children (Custom Mix/grouped lines): the children sub-loop below does
                // unguarded taxAmount/weight division per child with no zero-check, and that
                // path has not been verified safe for a weight-zeroed child. This exception
                // only needs to cover the confirmed scenario (a plain productSegment:"AD"
                // placeholder, never a grouped item in any payload seen) -- the old
                // unconditional skip is preserved for grouped items, leaving that untested
                // path unchanged.
                if (orders[i].items[j]?.weight === 0 && (!orders[i].items[j]?.sOrderlineID || orders[i].items[j]?.children?.length > 0)) continue;
                // BUG-2371: this zero line's sOrderlineID was handed to the re-added line of the same product.
                if (replacedZeroLineIds.has(orders[i].items[j]?.lineId)) continue;
                let unitPrice;
                if (orders[i].items[j].weight > 0) {
                  unitPrice = orders[i].items[j].realPrice;
                  unitPrice = parseFloat(parseFloat(unitPrice)?.toFixed(precision));
                } else {
                  unitPrice = 0;
                }
                let orderLineMeta = [];
                // TIC-10890: this push had no argument, so advance/retrieved orders sent no
                // approval metadata at all. Send the same "Approval" entry as the regular-order path.
                const approvalMeta = buildApprovalMeta(orders[i].items[j], precision);
                if (approvalMeta) orderLineMeta.push(approvalMeta);
                const item = orders[i].items[j];

                if (Array.isArray(item.children) && item.children.length > 0) {
                  const childIDs = [];
                  for (let k = 0; k < item.children.length; k++) {
                    let unitPrice;
                    const currentItem = item.children[k];
                    let unitTax = currentItem.taxAmount / currentItem.weight;
                    const sOrderlineID = currentItem.sOrderlineID ? currentItem.sOrderlineID : uuidv4().replace(/-/g, "").toUpperCase();
                    const lineTaxableAmt = currentItem?.linenet !== null && currentItem?.linenet !== undefined ? currentItem.linenet : 0;
                    orderLines.push({
                      sOrderlineID: sOrderlineID,
                      sOrderlineReturnId: currentItem.sOrderlineReturnId || null,
                      created: parsedDate4.format("YYYY-MM-DD HH:mm:ss"),
                      createdby: orders[i].createdBy,
                      updated: parsedDate4.format("YYYY-MM-DD HH:mm:ss"),
                      updatedby: orders[i].createdBy,
                      deliveryDate: isFinalizingAdvanceToInvoice
                        ? parsedDate4.format("YYYY-MM-DD HH:mm:ss")
                        : activeCart?.deliveryDate
                          ? safeParseDatetime(activeCart.deliveryDate).format("YYYY-MM-DD HH:mm:ss")
                          : orders[i]?.deliveryDate
                            ? safeParseDatetime(orders[i].deliveryDate).format("YYYY-MM-DD HH:mm:ss")
                            : parsedDate4.format("YYYY-MM-DD HH:mm:ss"),
                      sOrderId: orders[i].sOrderID,
                      line: (j + 1) * 10 + k,
                      description: "",
                      mProductId: currentItem.productId ? currentItem.productId : currentItem.mProductId,
                      csUomId: currentItem.productId ? currentItem.uom || currentItem.uomData?.[0]?.csUomId : currentItem.uom?.csUomId || null,
                      csTaxId: isInterStateSync && currentItem.interTaxId ? currentItem.interTaxId : currentItem.tax || currentItem.cTaxId,
                      qty: currentItem.weight,
                      unitprice: currentItem.weight > 0 ? parseFloat(parseFloat(currentItem.realPrice)?.toFixed(precision)) : 0,
                      discount: currentItem.discount,
                      returnline: currentItem.isReturn ?? currentItem.returnline,
                      returnQty: currentItem.isReturn === true ? Math.abs(currentItem.weight) : 0,
                      mBatchId: currentItem.mBatchId || null,
                      mBatchNo: currentItem.batchno || null,
                      mPricingruleId: currentItem.mPricingruleId ?? null,
                      batchedForSale: currentItem.batchedForSale || null,
                      batchedForStock: currentItem.batchedForStock || null,
                      batchedProduct: currentItem.batchedProduct || null,
                      salesRepId: currentItem.salesRepId || orders[i]?.salesRepId || null,
                      multiPrice: currentItem.multiPrice === "Y" ? "Y" : "N",
                      discountTypeId: null,
                      discountAmount: null,
                      unittax: currentItem.weight > 0 && currentItem.taxRate > 0 ? currentItem?.unitTax || currentItem?.unittax || 0 : 0,
                      // TIC-10890: this child's own discount values, not the parent's.
                      metaData: withApprovalMeta(orderLineMeta, buildApprovalMeta(currentItem, precision, item)),
                      listprice: currentItem.weight > 0 ? currentItem?.listPrice : 0,
                      linetax:
                        posConfig?.disableOfflineBilling === "Y"
                          ? Math.abs(currentItem.nettotal) > 0
                            ? parseFloat(parseFloat(currentItem.taxAmount)?.toFixed(precision))
                            : 0
                          : parseFloat(currentItem.linetax || 0),
                      linenet:
                        posConfig?.disableOfflineBilling === "Y"
                          ? Math.abs(currentItem.nettotal) > 0
                            ? parseFloat(parseFloat(currentItem.nettotal - currentItem.taxAmount)?.toFixed(precision))
                            : 0
                          : currentItem.linenet || 0,
                      linegross: posConfig?.disableOfflineBilling === "Y" ? (Math.abs(currentItem.nettotal) > 0 ? currentItem.nettotal : 0) : currentItem.linegross || 0,
                      linestd:
                        posConfig?.disableOfflineBilling === "Y"
                          ? Math.abs(currentItem.nettotal) > 0
                            ? parseFloat(
                                parseFloat(
                                  taxIncludeFlag === "Y" ? currentItem.sunitprice : currentItem.sunitprice * (1 + currentItem.taxRate / 100) * currentItem.weight
                                )?.toFixed(precision)
                              )
                            : 0
                          : currentItem.linestd || 0,
                      netunit:
                        posConfig?.disableOfflineBilling === "Y"
                          ? Math.abs(currentItem.nettotal) > 0
                            ? parseFloat(
                                parseFloat(
                                  isFinite(currentItem.nettotal / currentItem.weight)
                                    ? taxIncludeFlag === "Y"
                                      ? currentItem.nettotal / currentItem.weight / (1 + currentItem.taxRate / 100)
                                      : currentItem.salePrice
                                    : 0
                                )?.toFixed(precision)
                              )
                            : 0
                          : currentItem.netunit || 0,
                      grossunit:
                        posConfig?.disableOfflineBilling === "Y"
                          ? Math.abs(currentItem.nettotal) > 0
                            ? parseFloat(
                                parseFloat(
                                  isFinite(currentItem.nettotal / currentItem.weight)
                                    ? taxIncludeFlag === "Y"
                                      ? currentItem.nettotal / currentItem.weight / (1 + currentItem.taxRate / 100)
                                      : currentItem.salePrice
                                    : 0
                                )?.toFixed(precision)
                              ) * (currentItem.isReturn ? -1 : 1)
                            : 0
                          : currentItem.grossunit || 0,
                      grossstd:
                        posConfig?.disableOfflineBilling === "Y"
                          ? Math.abs(currentItem.nettotal) > 0
                            ? roundTo(taxIncludeFlag === "Y" ? currentItem.sunitprice : currentItem.sunitprice * (1 + currentItem.taxRate / 100), precision)
                            : 0
                          : roundTo(currentItem.grossstd, precision) || 0,
                      grosslist:
                        posConfig?.disableOfflineBilling === "Y"
                          ? Math.abs(currentItem.nettotal) > 0
                            ? roundTo(taxIncludeFlag === "Y" ? currentItem.listPrice : currentItem.listPrice * (1 + currentItem.taxRate / 100), precision)
                            : 0
                          : roundTo(currentItem.grosslist, precision) || 0,
                      netlist:
                        posConfig?.disableOfflineBilling === "Y"
                          ? Math.abs(currentItem.nettotal) > 0
                            ? taxIncludeFlag === "Y"
                              ? currentItem.listPrice - parseFloat(parseFloat(currentItem.listPrice - currentItem.listPrice / (1 + currentItem.taxRate / 100))?.toFixed(precision))
                              : currentItem.listPrice
                            : 0
                          : parseFloat(currentItem.netList),
                      tax: [
                        {
                          // csTaxID: orders[i].taxDetails[0]?.csTaxID || "",
                          csTaxID: isInterStateSync && currentItem.interTaxId ? currentItem.interTaxId : currentItem.tax || currentItem.cTaxId,
                          taxableAmt: parseFloat(parseFloat(lineTaxableAmt).toFixed(precision)),
                          taxAmt: parseFloat(parseFloat(currentItem.taxAmount)?.toFixed(precision) || 0),
                        },
                      ],
                    });
                    childIDs.push({ sOrderlineID });
                  }
                  childItemMetaMap.set(item.name + "_" + j, {
                    name: item.name,
                    weight: item.weight,
                    discount: item.discount,
                    taxAmount: item.taxAmount,
                    realPrice: item.realPrice,
                    nettotal: item.nettotal,
                    children: childIDs,
                  });
                } else {
                  orderLines.push({
                    sOrderlineID: orders[i].items[j].sOrderlineID || readdedLineIds.get(orders[i].items[j].lineId) || uuidv4().replace(/-/g, "").toUpperCase(),
                    sOrderlineReturnId: orders[i].items[j].sOrderlineReturnId || null,
                    created: parsedDate4.format("YYYY-MM-DD HH:mm:ss"),
                    createdby: orders[i].createdBy,
                    updated: parsedDate4.format("YYYY-MM-DD HH:mm:ss"),
                    updatedby: orders[i].createdBy,
                    deliveryDate: isFinalizingAdvanceToInvoice
                      ? parsedDate4.format("YYYY-MM-DD HH:mm:ss")
                      : activeCart?.deliveryDate
                        ? safeParseDatetime(activeCart.deliveryDate).format("YYYY-MM-DD HH:mm:ss")
                        : orders[i]?.deliveryDate
                          ? safeParseDatetime(orders[i].deliveryDate).format("YYYY-MM-DD HH:mm:ss")
                          : parsedDate4.format("YYYY-MM-DD HH:mm:ss"),
                    sOrderId: orders[i].sOrderID,
                    line: (j + 1) * 10,
                    description: "",
                    mProductId: orders[i].items[j].productId ? orders[i].items[j].productId : orders[i].items[j].product.mProductId,
                    csUomId: orders[i].items[j].productId ? orders[i].items[j].uom || orders[i].items[j].uomData?.[0]?.csUomId : orders[i].items[j].uom?.csUomId || null,
                    csTaxId: isInterStateSync && orders[i].items[j].interTaxId ? orders[i].items[j].interTaxId : orders[i].items[j].tax || orders[i].items[j].cTaxId,
                    qty: orders[i].items[j].weight,
                    unitprice: unitPrice || 0,
                    discount: orders[i].items[j].discount,
                    returnline: orders[i].items[j].isReturn ?? orders[i].items[j].returnline,
                    returnQty: orders[i].items[j].isReturn === true ? Math.abs(orders[i].items[j].weight) : 0,
                    mBatchId: orders[i].items[j].mBatchId || null,
                    mBatchNo: orders[i].items[j].batchno || null,
                    mPricingruleId: orders[i].items[j].mPricingruleId ?? null,
                    batchedForSale: orders[i].items[j].batchedForSale || null,
                    batchedForStock: orders[i].items[j].batchedForStock || null,
                    batchedProduct: orders[i].items[j].batchedProduct || null,
                    salesRepId: orders[i].items[j]?.salesRepId || orders[i]?.salesRepId || null,
                    multiPrice: orders[i].items[j].multiPrice === "Y" ? "Y" : "N",
                    discountTypeId: null,
                    discountAmount: null,
                    unittax: orders[i].items[j].weight > 0 && orders[i].items[j].taxRate > 0 ? orders[i].items[j]?.unitTax || orders[i].items[j]?.unittax || 0 : 0,
                    metaData: orderLineMeta,
                    listprice: orders[i].items[j].weight > 0 ? orders[i].items[j].listPrice : 0,
                    // BUG-2208: the `: <field>-from-item` fallback below (disableOfflineBilling !== "Y",
                    // the default for most clients) trusted whatever amount cart.js had last stamped on
                    // the line -- including a pre-removal amount stranded on a weight===0 line that
                    // reaches this push via the sOrderlineID exception above (BUG-2105). Guarding each
                    // fallback on weight === 0 keeps that exception's "send qty:0 so the backend zeros
                    // the row" behavior intact while guaranteeing the amounts sent are always 0, never
                    // stale -- matches what the disableOfflineBilling === "Y" branch already does.
                    linetax:
                      posConfig?.disableOfflineBilling === "Y"
                        ? Math.abs(orders[i].items[j].nettotal) > 0
                          ? parseFloat(parseFloat(orders[i].items[j].taxAmount)?.toFixed(precision))
                          : 0
                        : orders[i].items[j].weight === 0
                          ? 0
                          : parseFloat(orders[i].items[j].linetax || 0),
                    linenet:
                      posConfig?.disableOfflineBilling === "Y"
                        ? Math.abs(orders[i].items[j].nettotal) > 0
                          ? parseFloat(parseFloat(orders[i].items[j].nettotal - orders[i].items[j].taxAmount)?.toFixed(precision))
                          : 0
                        : orders[i].items[j].weight === 0
                          ? 0
                          : orders[i].items[j].linenet || 0,
                    linegross:
                      posConfig?.disableOfflineBilling === "Y"
                        ? Math.abs(orders[i].items[j].nettotal) > 0
                          ? orders[i].items[j].nettotal
                          : 0
                        : orders[i].items[j].weight === 0
                          ? 0
                          : orders[i].items[j].linegross || 0,
                    linestd:
                      posConfig?.disableOfflineBilling === "Y"
                        ? Math.abs(orders[i].items[j].nettotal) > 0
                          ? parseFloat(
                              parseFloat(
                                taxIncludeFlag === "Y"
                                  ? orders[i].items[j].sunitprice
                                  : orders[i].items[j].sunitprice * (1 + orders[i].items[j].taxRate / 100) * orders[i].items[j].weight
                              )?.toFixed(precision)
                            )
                          : 0
                        : orders[i].items[j].weight === 0
                          ? 0
                          : orders[i].items[j].linestd || 0,
                    netunit:
                      posConfig?.disableOfflineBilling === "Y"
                        ? Math.abs(orders[i].items[j].nettotal) > 0
                          ? parseFloat(
                              parseFloat(
                                isFinite(orders[i].items[j].nettotal / orders[i].items[j].weight)
                                  ? taxIncludeFlag === "Y"
                                    ? orders[i].items[j].nettotal / orders[i].items[j].weight / (1 + orders[i].items[j].taxRate / 100)
                                    : orders[i].items[j].salePrice
                                  : 0
                              )?.toFixed(precision)
                            )
                          : 0
                        : orders[i].items[j].weight === 0
                          ? 0
                          : orders[i].items[j].netunit || 0,
                    grossunit:
                      posConfig?.disableOfflineBilling === "Y"
                        ? Math.abs(orders[i].items[j].nettotal) > 0
                          ? parseFloat(
                              parseFloat(
                                isFinite(orders[i].items[j].nettotal / orders[i].items[j].weight)
                                  ? taxIncludeFlag === "Y"
                                    ? orders[i].items[j].nettotal / orders[i].items[j].weight / (1 + orders[i].items[j].taxRate / 100)
                                    : orders[i].items[j].salePrice
                                  : 0
                              )?.toFixed(precision)
                            ) * (orders[i].items[j].isReturn ? -1 : 1)
                          : 0
                        : orders[i].items[j].weight === 0
                          ? 0
                          : orders[i].items[j].grossunit || 0,
                    grossstd:
                      posConfig?.disableOfflineBilling === "Y"
                        ? Math.abs(orders[i].items[j].nettotal) > 0
                          ? roundTo(taxIncludeFlag === "Y" ? orders[i].items[j].sunitprice : orders[i].items[j].sunitprice * (1 + orders[i].items[j].taxRate / 100), precision)
                          : 0
                        : orders[i].items[j].weight === 0
                          ? 0
                          : roundTo(orders[i].items[j].grossstd, precision) || 0,
                    grosslist:
                      posConfig?.disableOfflineBilling === "Y"
                        ? Math.abs(orders[i].items[j].nettotal) > 0
                          ? roundTo(taxIncludeFlag === "Y" ? orders[i].items[j].listPrice : orders[i].items[j].listPrice * (1 + orders[i].items[j].taxRate / 100), precision)
                          : 0
                        : orders[i].items[j].weight === 0
                          ? 0
                          : roundTo(orders[i].items[j].grosslist, precision) || 0,
                    netlist:
                      posConfig?.disableOfflineBilling === "Y"
                        ? Math.abs(orders[i].items[j].nettotal) > 0
                          ? taxIncludeFlag === "Y"
                            ? orders[i].items[j].listPrice -
                              parseFloat(parseFloat(orders[i].items[j].listPrice - orders[i].items[j].listPrice / (1 + orders[i].items[j].taxRate / 100))?.toFixed(precision))
                            : orders[i].items[j].listPrice
                          : 0
                        : orders[i].items[j].weight === 0
                          ? 0
                          : parseFloat(orders[i].items[j].netList),
                    tax: [
                      {
                        // csTaxID: orders[i].taxDetails[0]?.csTaxID || "",
                        csTaxID: isInterStateSync && orders[i].items[j].interTaxId ? orders[i].items[j].interTaxId : orders[i].items[j].tax || orders[i].items[j].cTaxId,
                        taxableAmt: parseFloat(parseFloat(orders[i].items[j].linenet)?.toFixed(precision) || 0),
                        taxAmt: parseFloat(parseFloat(orders[i].items[j].taxAmount)?.toFixed(precision) || 0),
                      },
                    ],
                  });
                }
              }
              const paymentsList = [];
              let amtMax = -100000000000000000000;
              let maxFinPaymentMethod = null;
              for (let k = 0; k < orders[i].payments.length; k += 1) {
                if (amtMax < parseFloat(orders[i].payments[k].amount)) {
                  amtMax = parseFloat(orders[i].payments[k].amount);
                  maxFinPaymentMethod = orders[i].payments[k].finPaymentmethodId;
                }
                paymentsList.push({
                  finPaymentmethodID: orders[i].payments[k].finPaymentmethodId,
                  amount: parseFloat(parseFloat(orders[i].payments[k].amount).toFixed(precision)),
                  // TIC-10095: this branch (Layaway/Advance/credit-sale settlement via
                  // confirmPOSLayawayOrder1) can legitimately be paid off with a KWC Wallet
                  // tender same as any other completion path -- see the matching fix and
                  // comment in the primary paymentsList builder above.
                  ...(orders[i].payments[k].walletAlreadyDeducted === true ? { transactionId: orders[i].payments[k].walletTrxId || null } : {}),
                });
                // TSK-2934: see the primary paymentsList builder above for context.
                applyPaymentDataToLine(paymentsList[paymentsList.length - 1], orders[i].payments[k].paymentData);
              }
              let tableData = useRestaurantStore.getState().tableName;
              let metaData = [];
              if (useRestaurantStore.getState().dineIn === "Y" && tableData) {
                const keysToCheck = ["guestName", "guestType", "cwrFbTableId", "cwrFbsectionId", "noOfPersons", "referredBy", "orderId"];
                for (const key of keysToCheck) {
                  if (tableData.hasOwnProperty(key)) {
                    metaData.push({
                      key: key,
                      value: tableData[key],
                    });
                  }
                }
              }
              if (Array.isArray(orders[i].items)) {
                if (childItemMetaMap.size > 0) {
                  const customMixMeta = [];

                  childItemMetaMap.forEach((metaObj) => {
                    customMixMeta.push(metaObj);
                  });

                  metaData.push({
                    key: "Custom Mix",
                    value: JSON.stringify(customMixMeta),
                  });
                }
              }
              if (orders[i]?.feedbackData) {
                metaData.push({ key: "Customer Feedback", value: JSON.stringify(orders[i].feedbackData) });
              }
              let accumulationPoints;
              if (activeCart.customer?.loyaltyLevel?.cwrLoyaltyLevelId) {
                const loyaltyLevelId = activeCart.customer.loyaltyLevel.cwrLoyaltyLevelId;
                const loyaltyData = await db.loyalityData.where("loyaltylevelId").equals(loyaltyLevelId).first();
                if (loyaltyData) {
                  const eventCalenders = loyaltyData.loyaltyEventCalenders || [];
                  const isEventActive = eventCalenders.length > 0 && eventCalenders.some((ec) => dateValidator(ec.startDate, ec.endDate));

                  const applicableRule = (loyaltyData.LoyaltyAccumulation || []).find((item) => {
                    const productValidation = item.mProductId ? activeCart.items.some((pc) => pc.mProductId === item.mProductId) : true;
                    const categoryValidation = item.mProductCategoryId ? activeCart.items.some((pc) => pc.mProductCategoryId === item.mProductCategoryId) : true;
                    if (isEventActive) {
                      if (item.loyaltyEvent === "Y") {
                        return item.loyaltyEvent === "Y" && item.minPurchase <= Math.abs(activeCart.total) && productValidation && categoryValidation;
                      } else {
                        return item.loyaltyEvent === "N" && item.minPurchase <= Math.abs(activeCart.total) && productValidation && categoryValidation;
                      }
                    }

                    return item.loyaltyEvent === "N" && item.minPurchase <= Math.abs(activeCart.total) && productValidation && categoryValidation;
                  });

                  if (applicableRule) {
                    const baseAmount = activeCart.total - activeCart.tax;

                    if (tillData?.tillAccess?.csBunit?.enableLoyalty !== "Y") {
                      accumulationPoints = 0;
                    } else if (activeCart?.discount > 0 && loyaltyData?.noLoyaltyOnDiscount === "Y") {
                      accumulationPoints = 0;
                    } else if (activeCart?.redemptionPoints > 0 && loyaltyData?.noLoyaltyOnRedemption === "Y") {
                      accumulationPoints = 0;
                    } else {
                      const rawPoints = Number((applicableRule.pointsPerUnit * baseAmount).toFixed(3));
                      if (rawPoints < 0) {
                        const postBalance = activeCart.customer?.retlLoyaltyBalance || 0;
                        const storedAccumulation = activeCart.accumulationPoints || 0;
                        const preBalance = Math.max(postBalance - storedAccumulation, 0);
                        accumulationPoints = Math.max(rawPoints, -preBalance);
                      } else {
                        accumulationPoints = rawPoints;
                      }
                    }
                  }
                }
              }
              const orderJson = {
                sOrderID: orders[i].sOrderID,
                cSClientID: tillData.tillAccess.csClientId,
                cSBunitID: tillData.tillAccess.csBunit.csBunitId,
                created: parsedDate4.format("YYYY-MM-DD HH:mm:ss"),
                createdby: orders[i].createdBy,
                updated: parsedDate4.format("YYYY-MM-DD HH:mm:ss"),
                updatedby: orders[i].createdBy,
                csDoctypeId: tillData.tillAccess.csBunit.cwrCsDoctypeId,
                sCustomerId: orders[i]?.customer?.sCustomer?.sCustomerID ? orders[i].customer.sCustomer.sCustomerID : tillData.tillAccess.csBunit.b2cCustomer.sCustomer.sCustomerID,
                // FK-3692,6319: same per-order-snapshot fix as the posOrderProcessor1 branch above.
                sCustomerBillingId:
                  retailCustomerFields && retailCustomerFields.length > 0
                    ? resolveCapturedAddressId(orders[i], "billingId")
                    : tillData.tillAccess.csBunit.customerAddress?.sCustomerAddressID,
                sCustomerShippingId:
                  retailCustomerFields && retailCustomerFields.length > 0
                    ? resolveCapturedAddressId(orders[i], "shippingId")
                    : tillData.tillAccess.csBunit.customerAddress?.sCustomerAddressID,
                sPricelistId: tillData.tillAccess.csBunit.cwrSpricelistId,
                documentno: orders[i].documentno,
                // TSK-3249: same reasoning as the posOrderProcessor1 branch above — keep the
                // order's own business date, only the time-of-day is live. F-29: must also
                // honor nextBusinessDate like that branch does, so a regular order and a
                // layaway order created in the same post-close "continue selling" session
                // land on the same dateordered instead of splitting across two days.
                // BUG (fixed): same fix as the posOrderProcessor1 branch above -- keep only
                // nextBusinessDate's date portion and combine it with the real order time,
                // instead of sending its fixed 00:00:00 timestamp as-is.
                dateordered:
                  (useSessionStore.getState().nextBusinessDate
                    ? `${dayjs(useSessionStore.getState().nextBusinessDate).format("YYYY-MM-DD")} ${parsedDate4.format("HH:mm:ss")}`
                    : null) ||
                  (toISOBusinessDate(orders[i].orderDate)
                    ? `${toISOBusinessDate(orders[i].orderDate)} ${parsedDate4.format("HH:mm:ss")}`
                    : parsedDate4.format("YYYY-MM-DD HH:mm:ss")),
                datepromised: isFinalizingAdvanceToInvoice
                  ? parsedDate4.format("YYYY-MM-DD HH:mm:ss")
                  : orders[i].deliveryDate
                    ? safeParseDatetime(orders[i].deliveryDate).format("YYYY-MM-DD HH:mm:ss")
                    : parsedDate4.format("YYYY-MM-DD HH:mm:ss"),
                csPaymenttermID: null,
                finPaymentmethodId: maxFinPaymentMethod || null,
                csCurrencyId: csCurrencyId,
                mWarehouseId: tillData.tillAccess.csBunit.mWarehouse.mWarehouseID,
                cwrLongitude: "",
                cwrLatitude: "",
                // BUG-2232: sync the cashier recorded on the order at sale time.
                csUserId: orders[i].createdBy || resolveActingUserId(tillData),
                cwrB2cCustomerId: orders[i].customer.cwrCustomerId,
                orderreference: "",
                cwrPayref: "",
                cwrPayremarks: "",
                description: "",
                // BUG-2069: isAdvance must reflect whether THIS confirm call is the one
                // settling the order in full, not just whether it originated as an advance
                // booking -- otherwise the backend's shipment/invoice gate (isAdvance=="N")
                // never opens for advance orders, even once fully paid.
                //
                // Deliberately NOT reusing isFinalizingAdvanceToInvoice's `paidAmount +
                // advancePayment >= totalAmount` here -- paidAmount (cart.paid) is already
                // cumulative-inclusive of advancePayment (see SideMenuDrawer.js's BUG-1570
                // comment: "cart.paid === advancePayment + fresh session payment"), so adding
                // advancePayment a second time satisfies the check at ~50% paid, not 100%
                // (confirmed live: Dadus orders shipped/invoiced at 42-85% paid). paidAmount
                // alone is the correct "fully settled" signal.
                //
                // totalQty > 0 is required too: Restaurant's clearCart1 (Cancel All,
                // useVoidHandler.js), useSuperMarketHandlers.js and useParkedBills.js all zero
                // BOTH total and totalQty together on a full cancel, but never touch
                // paid/advancePayment/statusType -- so a cancelled order that had collected an
                // advance (advancePayment>0, paid>0, now total===0 and totalQty===0) would
                // otherwise satisfy isRetrievedAdvance && paidAmount>=totalAmount trivially
                // (anything >= 0) and wrongly open the shipment/invoice gate on a cancelled
                // order with nothing left to ship.
                //
                // Deliberately totalQty, NOT totalAmount: a 100%-discounted or fully-comped
                // advance order can legitimately have totalAmount===0 while still carrying real
                // line items (totalQty>0) and being genuinely paid in full -- gating on
                // totalAmount there would silently reintroduce BUG-2069 for that subset.
                // totalQty is the reliable "was this actually cancelled" signal because every
                // cancel path found zeroes it in lockstep with total, whereas a discount only
                // ever changes total, never totalQty.
                // BUG-2105: paidAmount >= totalAmount replaced with isCompleteOrderClicked
                // (defined above, at isFinalizingAdvanceToInvoice) -- same reasoning: "fully
                // paid" is a standing state that stays true across every later retrieve, so it
                // finalized an unchanged advance order on every touch. isCompleteOrderClicked
                // reflects the actual button pressed instead. totalQty > 0 is untouched, so the
                // cancelled-order protection above still applies regardless of which button/param
                // produced this call.
                isAdvance: resolveIsAdvanceFlag(orders[i].statusType, isRetrievedAdvance, totalQty, isCompleteOrderClicked),
                cwrTillId: tillData.tillAccess.cwrTill.cwrTillID,
                redemption: orders[i].redemptionPoints || null,
                accumulation: accumulationPoints ?? orders[i].accumulationPoints ?? null,
                // BUG (fixed): Math.abs() here stripped the sign off a down-rounded total
                // (roundOff < 0, e.g. -0.591) before sending it to confirmPOSLayawayOrder1.
                // The backend reconciles orderTotal against sum(linegross) + roundoff -- with
                // the sign gone, a rounded-DOWN order got treated as rounded UP, doubling the
                // apparent gap (orderTotal vs sum(linegross)+|roundoff| is off by 2x the real
                // roundOff) and tripping its own >₹1 block ("Refusing to book - lines do not
                // match this header"). The regular (non-layaway) path a few hundred lines above
                // (posOrderProcessor1's roundoff field) never had this bug -- it sends the
                // signed value untouched; this now matches it. Confirmed live: Chocolala TEST,
                // CMOO ADV/8 -> CMO/16318, roundOff=-0.591, backend-reported diff=1.182 (=2x0.591).
                roundoff: parseFloat(parseFloat(orders[i].roundOff)?.toFixed(precision)) || 0,
                cwrProductQty: orders[i].totalQty,
                cwrProductCount: orders[i].totalQty,
                // BUG-2096: see the matching comment above the other ofdStatus assignment in
                // this file -- the literal word overwrites the Aggregator screen's tracked code.
                ofdStatus: orders[i]?.saleType?.isAggregator === "Y" ? "NEW" : "Delivered",
                ofdIspaid: "Y",
                mPricingruleId: orders[i].mPricingruleId !== null && orders[i].mPricingruleId !== undefined ? orders[i].mPricingruleId : null,
                cwrSaletypeId: orders[i].saleType.cwrSaletypeId || saleTypeId,
                salesRepId: orders[i].salesRepId !== null && orders[i].salesRepId !== undefined ? orders[i].salesRepId : null,
                discAmount: parseFloat((parseFloat(orders[i].discount) || 0).toFixed(precision)),
                creditAmount: orders[i].creditAmount,
                creditSale: orders[i].isCreditSale === "Y" ? "Y" : "N",
                metaData: metaData,
                pricingCoupon: [
                  {
                    mPricingCouponId: orders[i].mPricingCouponId || null,
                    redemptionCount: orders[i].couponRedemptionCount ? parseInt(orders[i].couponRedemptionCount) : null,
                    referenceId: orders[i]?.couponInput?.length > 0 ? orders[i].couponInput[0].referenceId : null,
                    couponCode: orders[i]?.couponInput?.length > 0 ? orders[i]?.couponInput?.[0]?.couponCode : null,
                  },
                ],
                orderTotal: orders[i].total,
                isReturn: orders[i].items.filter((f) => f.isReturn === true).length > 0 ? "Y" : "N",
                sOrderReturnId: orders[i].items?.[0]?.sOrderReturnId || null,
                layAway: orders[i].layAway,
                nettotal: parseFloat(parseFloat(orders[i].total - orders[i].tax - (parseFloat(orders[i].tcs) || 0))?.toFixed(precision)),
                taxamt: parseFloat(parseFloat(orders[i].tax)?.toFixed(precision)),
                payments: paymentsList,
                line: orderLines,
                b2bOrder: orderIsB2bEnabled === "true" ? "Y" : "N",
                revenueCategoryId: useTransactionStore.getState().selectedRevenueCategoryId || null,
              };

              // DIAG (Issue-7, expanded for GST-DIAG/BUG-1682/GPR-IRN): same upgrade as the
              // posOrderProcessor1 branch above — durable sink, full IRN-relevant field set.
              try {
                logSequenceEvent(
                  "gst-diag-orderjson-sent-layaway",
                  {
                    sOrderID: orderJson.sOrderID,
                    documentno: orderJson.documentno,
                    sCustomerId: orderJson.sCustomerId,
                    sCustomerBillingId: orderJson.sCustomerBillingId,
                    sCustomerShippingId: orderJson.sCustomerShippingId,
                    cwrB2cCustomerId: orderJson.cwrB2cCustomerId,
                    b2bOrder: orderJson.b2bOrder,
                    creditSale: orderJson.creditSale,
                    csDoctypeId: orderJson.csDoctypeId,
                    cwrSaletypeId: orderJson.cwrSaletypeId,
                    layAway: orderJson.layAway,
                    isAdvance: orderJson.isAdvance,
                    customerSCustomerID: orders[i]?.customer?.sCustomer?.sCustomerID || null,
                    customerGstNo: orders[i]?.customer?.gstNo || null,
                  },
                  db
                );
              } catch (diagErr) {
                console.warn("[B2B-DIAG] logging failed:", diagErr?.message);
              }

              // DI-03: Validate layaway order JSON self-consistency
              {
                const tcsValLayaway = parseFloat(orders[i].tcs) || 0;
                const jsonCheckLayaway = assertOrderJSON(
                  { nettotal: orderJson.nettotal, taxamt: orderJson.taxamt, tcs: tcsValLayaway, orderTotal: orderJson.orderTotal },
                  "syncOrders-layaway"
                );
                if (!jsonCheckLayaway.valid && jsonCheckLayaway.correctedNettotal !== undefined) {
                  orderJson.nettotal = jsonCheckLayaway.correctedNettotal;
                }
              }

              // DI-34: same outbound line-vs-header cross-check as the regular path above,
              // including the same charges fix (see comment on the regular-path call site).
              {
                const orderChargesLayaway =
                  (Number(orders[i].serviceCharge) || 0) +
                  (Number(orders[i].tip) || 0) +
                  (Number(orders[i].deiveryCharge) || 0) +
                  (Number(orders[i].packingCharge) || 0) +
                  (Number(orders[i].others) || 0);
                const linesCheckLayaway = assertOrderLinesMatchHeader(orderJson, "syncOrders-layaway", db, orderChargesLayaway);
                if (!linesCheckLayaway.valid && linesCheckLayaway.correctedTaxamt !== undefined) {
                  orderJson.taxamt = linesCheckLayaway.correctedTaxamt;
                }
              }

              // DI-35 (BUG-2041/2044 follow-up): same finite-value check as the regular sync
              // path above -- see the comment on that call site and dataIntegrity.js for why
              // this is needed even with DI-03/DI-34 already running.
              assertOrderPayloadFinite(orderJson, "syncOrders-layaway", db);

              const stringifiedData = JSON.stringify(orderJson);
              const newStringifiedFields = stringifiedData.replace(/\\"/g, '\\"');
              const newStringRequest = JSON.stringify(newStringifiedFields);

              const paramsInput = {
                query: `
                      mutation {
                        confirmPOSLayawayOrder1(
                          tillId: "${tillData.tillAccess.cwrTill.cwrTillID}",
                          orderId: "${orders[i].sOrderID}",
                          documentNo: "${orders[i].documentno}",
                          orderJson: ${newStringRequest}
                        ) {
                          documentno
                          status
                          message
                        }
                      }
                    `,
              };

              try {
                const response = await confirmPOSLayawayOrder1(paramsInput);
                const result = response.data.data.confirmPOSLayawayOrder1;
                const { status, documentno } = result;
                if (result.status === "200") {
                  if (isPrepareBillCancelled) {
                    // BUG-fix (round 4): confirmPOSLayawayOrder1 is genuinely succeeding here
                    // server-side (confirmed via production trace: table T14's BHRS/1828 got
                    // "status":"200","message":"Sales Order is booked sucessfully" on every
                    // retry) but isSynced was never being set, so this exact order kept getting
                    // reconfirmed every 10s forever. Cause: processOrderApproved (the
                    // disableOfflineBilling==="Y" branch below) does not use its sOrderID
                    // argument to pick which db.orders row to update — it builds cartToDb from
                    // useCartStore.getState().cart, the cashier's CURRENTLY ACTIVE on-screen
                    // cart, and writes db.orders keyed on THAT cart's own sOrderID (see
                    // processOrderApproved ~L368/L919). isPrepareBillCancelled is exclusively
                    // reached from the background auto-sync loop confirming a previously
                    // cancelled order (paid:0, zeroed totals) — by the time this round-trip
                    // resolves the cashier has typically moved to a different table, so that
                    // write lands on an unrelated cart (or a blank one) and this order's own
                    // row is never marked synced. useVoidHandler.js already released the table
                    // and updated db.fbOrderData/db.tableData synchronously at cancel time,
                    // independent of this confirm, so no further table cleanup belongs here —
                    // only the isSynced bookkeeping for this specific order. Also skip the
                    // foreground-only side effects below (processOrderApproved, IRN modal,
                    // RFID sync) — none of them are meaningful for a void/zero-total order
                    // confirmed in the background, and running them here would pop foreground
                    // UI over whatever the cashier is actually doing.
                    await db.orders.where("sOrderID").equals(orders[i].sOrderID).modify({ isSynced: 1, documentno });
                    // The plain "Order X Created Successfully" confirmation is NOT foreground
                    // UI in the same sense — the normal (non-cancelled) confirm/create success
                    // branches below (~L2780, ~L3550) show this same toast/modal unconditionally,
                    // even for a same-pattern cancelled posOrderProcessor1 order (isCancelledOrder
                    // still reaches it — only the separate `restaurant:orderComplete` popup is
                    // gated off for cancels, not this one). Mirror that here so a Prepare Bill
                    // cancelled directly gets the same confirmation as any other cancel.
                    if (posConfig?.disableOfflineBilling === "Y") {
                      if (tillLayout === 2) {
                        message.success(`Order ${documentno} Created Successfully`);
                      } else if (tillLayout === 4) {
                        Modal.info({
                          icon: null,
                          okText: "Ok",
                          okButtonProps: {
                            style: {
                              display: "inline-block",
                              backgroundColor: "#4285F4",
                              borderColor: "#4285F4",
                              color: "#fff",
                              borderRadius: "4px",
                              padding: "8px 24px",
                              height: "auto",
                              fontWeight: "500",
                            },
                          },
                          content: (
                            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "20px 0" }}>
                              <div style={{ position: "relative", marginBottom: "16px" }}>
                                <div
                                  style={{
                                    width: "80px",
                                    height: "90px",
                                    backgroundColor: "#FFF9C4",
                                    borderRadius: "8px",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    position: "relative",
                                  }}
                                >
                                  <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                                    <div style={{ width: "40px", height: "4px", backgroundColor: "#4CAF50", borderRadius: "2px" }}></div>
                                    <div style={{ width: "30px", height: "4px", backgroundColor: "#E0E0E0", borderRadius: "2px" }}></div>
                                    <div style={{ width: "35px", height: "4px", backgroundColor: "#E0E0E0", borderRadius: "2px" }}></div>
                                    <div style={{ width: "25px", height: "4px", backgroundColor: "#E0E0E0", borderRadius: "2px" }}></div>
                                  </div>
                                </div>
                                <div
                                  style={{
                                    position: "absolute",
                                    bottom: "-5px",
                                    right: "-5px",
                                    width: "28px",
                                    height: "28px",
                                    backgroundColor: "#4CAF50",
                                    borderRadius: "50%",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                  }}
                                >
                                  <span style={{ color: "#fff", fontSize: "16px", fontWeight: "bold" }}>✓</span>
                                </div>
                              </div>
                              <p style={{ margin: "8px 0", fontSize: "14px", fontWeight: "400", color: "#666" }}>{documentno}</p>
                              <p style={{ margin: "8px 0", fontSize: "20px", fontWeight: "600", color: "#0F0718" }}>Invoice Completed Successfully</p>
                              <p style={{ margin: "8px 0 24px 0", fontSize: "14px", fontWeight: "400", color: "#999", textAlign: "center" }}>
                                Your bill has been printed successfully.
                                <br />
                                Redirecting to new bill...
                              </p>
                            </div>
                          ),
                          centered: true,
                          style: { top: 0 },
                          bodyStyle: { overflow: "hidden" },
                          closable: false,
                        });
                      }
                    }
                    // syncOrders sets the shared `loading` flag once per pass, before this
                    // per-order loop even starts, whenever the pending pool is non-empty
                    // (tillLayout===2 gate, ~L1438) — it relies on SOME branch downstream to
                    // reset it. The regular-order branch has its own finally{setLoading(false)}
                    // (~L2845); processOrderApproved has its own tillLayout===2 setTimeout reset
                    // (~L1247-1251) that, being the same shared flag, incidentally used to reset
                    // this one too. Skipping processOrderApproved above removes that incidental
                    // reset — if every pending order in this pass is isPrepareBillCancelled, the
                    // flag would otherwise never come back down, leaving FBLayout's full-screen
                    // blocking overlay (BUG-1760) stuck up after Cancel All. Reset unconditionally
                    // here, matching the finally block's own unconditional pattern.
                    setLoading(false);
                    continue;
                  }
                  if (posConfig?.disableOfflineBilling !== "Y") {
                    await db.orders.where("sOrderID").equals(orders[i].sOrderID).modify({ isSynced: 1 });
                  } else if (posConfig?.disableOfflineBilling === "Y") {
                    // TSK-2935: pass the server-confirmed documentno through (matching the
                    // posOrderProcessor1 success handler above), instead of null. With null,
                    // this finalize call falls back to checking cart?.statusType === "OCU" to
                    // decide whether to keep the existing number — and `cart` here is a
                    // closure-captured value with the same staleness risk DI-33 documents
                    // elsewhere in this file. When stale, that check silently computes a brand
                    // new local number, overwriting the just-confirmed order in place (seen in
                    // production trace: BHRS/1771 confirmed by the server, then immediately
                    // overwritten locally with BHRS/1778 for the same sOrderID).
                    //
                    // Use orders[i].sOrderID, not activeCart.sOrderID — activeCart is a single
                    // snapshot of useCartStore taken once at the top of this sync pass (line
                    // ~1267), not re-read per order. If the cashier moves on to another
                    // table/order before this order's confirm round-trip resolves, activeCart no
                    // longer points at THIS order. processOrderApproved's completion branch
                    // (cleanupNormalTable/cleanupMergedTables/cleanupSplitTable) then runs
                    // against whatever activeCart currently is, leaving this order's real
                    // db.fbOrderData row stuck at fbOrderStatus "IP" with its items forever —
                    // the table shows "ONG" with stale items on reselect even though the order
                    // itself completed successfully server-side (confirmed via cwpos_backup.json:
                    // T1's fbOrderData row stayed IP after a successful confirmPOSLayawayOrder1
                    // for the same sOrderID). orders[i] is this order's own object throughout the
                    // rest of this block (see line 3279 above) and is never stale.
                    // BUG-1963/1966: pass orders[i] as the orderSnapshot too — the sOrderID
                    // fix above (6b45b578c) corrected the identity/documentno-fallback checks,
                    // but processOrderApproved's cartToDb (used for db.orders.put) still read
                    // useCartStore.getState().cart unconditionally, so this call could still
                    // persist whatever cart is live now instead of this order's own data.
                    await processOrderApproved(orders[i].sOrderID, documentno, 1, false, null, orders[i]);
                  }
                  if (posConfigLocal?.disableOfflineBilling === "Y") {
                    setInvoiceProcessing?.("SUCCESS");
                    setPendingIRNOrderId?.(orders[i].sOrderID);
                    setCompletedOrderId?.(documentno);
                  }
                  let rfidData = [];
                  const isPreOrderAdvance = window.location.pathname.includes("/preorder") || (cart.layAway === "Y" && cart.preOrder !== "Y");
                  logSequenceEvent("gst-diag-irn-gate", {
                    sOrderID: orders[i]?.sOrderID,
                    documentno,
                    disableOfflineBilling: posConfigLocal?.disableOfflineBilling,
                    isPreOrderAdvance,
                    isB2bEnabled: localStorage.getItem("isB2bEnabled") === "true" || useTransactionStore.getState().isB2bEnabled === "true",
                  }, db);
                  if (
                    posConfigLocal?.disableOfflineBilling === "Y" &&
                    !isPreOrderAdvance &&
                    (localStorage.getItem("isB2bEnabled") === "true" || useTransactionStore.getState().isB2bEnabled === "true")
                  ) {
                    setShowIRNModal?.(true);
                    setIrnGenerating?.("LOADING");
                    fetchIRNDetails(orders[i].sOrderID).then(() => {
                      setIrnLoading?.(false);
                    });
                  } else if (posConfigLocal?.disableOfflineBilling === "Y" && tillLayout === 4) {
                    Modal.info({
                      icon: null,
                      okText: "Ok",
                      okButtonProps: {
                        style: {
                          display: "inline-block",
                          backgroundColor: "#4285F4",
                          borderColor: "#4285F4",
                          color: "#fff",
                          borderRadius: "4px",
                          padding: "8px 24px",
                          height: "auto",
                          fontWeight: "500",
                        },
                      },
                      content: (
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            padding: "20px 0",
                          }}
                        >
                          {/* Invoice Icon with Checkmark */}
                          <div style={{ position: "relative", marginBottom: "16px" }}>
                            <div
                              style={{
                                width: "80px",
                                height: "90px",
                                backgroundColor: "#FFF9C4",
                                borderRadius: "8px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                position: "relative",
                              }}
                            >
                              <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                                <div style={{ width: "40px", height: "4px", backgroundColor: "#4CAF50", borderRadius: "2px" }}></div>
                                <div style={{ width: "30px", height: "4px", backgroundColor: "#E0E0E0", borderRadius: "2px" }}></div>
                                <div style={{ width: "35px", height: "4px", backgroundColor: "#E0E0E0", borderRadius: "2px" }}></div>
                                <div style={{ width: "25px", height: "4px", backgroundColor: "#E0E0E0", borderRadius: "2px" }}></div>
                              </div>
                            </div>
                            <div
                              style={{
                                position: "absolute",
                                bottom: "-5px",
                                right: "-5px",
                                width: "28px",
                                height: "28px",
                                backgroundColor: "#4CAF50",
                                borderRadius: "50%",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            >
                              <span style={{ color: "#fff", fontSize: "16px", fontWeight: "bold" }}>✓</span>
                            </div>
                          </div>

                          {/* Invoice Number */}
                          <p style={{ margin: "8px 0", fontSize: "14px", fontWeight: "400", color: "#666" }}>{documentno}</p>

                          {/* Success Title */}
                          <p style={{ margin: "8px 0", fontSize: "20px", fontWeight: "600", color: "#0F0718" }}>Invoice Completed Successfully</p>

                          {/* Success Message */}
                          <p style={{ margin: "8px 0 24px 0", fontSize: "14px", fontWeight: "400", color: "#999", textAlign: "center" }}>
                            Your bill has been printed successfully.
                            <br />
                            Redirecting to new bill...
                          </p>

                          {/* Total Amount Box */}
                          <div
                            style={{
                              backgroundColor: "#F5F5F5",
                              borderRadius: "8px",
                              padding: "16px 32px",
                              textAlign: "center",
                              minWidth: "180px",
                            }}
                          >
                            <p style={{ margin: "0 0 4px 0", fontSize: "12px", fontWeight: "400", color: "#999" }}>Total Amount</p>
                            <p style={{ margin: "0", fontSize: "22px", fontWeight: "600", color: "#0F0718" }}>
                              {tillData?.tillAccess?.csBunit?.currencies[0]?.currSymbol}{" "}
                              {parseFloat(orders[i]?.total?.toString()).toFixed(tillData?.tillAccess?.csBunit?.currencies[0]?.prcPrecision || 2)}
                            </p>
                          </div>
                        </div>
                      ),
                      centered: true,
                      style: {
                        top: 0,
                      },
                      bodyStyle: {
                        overflow: "hidden",
                      },
                      closable: false,
                    });
                  } else if (posConfigLocal?.disableOfflineBilling === "Y" && tillLayout === 2) {
                    message.success(`Order ${documentno} Created Successfully`);
                  }
                  localStorage.removeItem("isB2bEnabled");
                  // FK-3692,6319/BUG-1969: see the matching comment at the other checkout
                  // completion site above (~L3234) -- same fix, same reason.
                  useTransactionStore.getState().setIsB2bEnabled(null);
                  // TIC-6900 follow-up: see the matching comment at the other checkout
                  // completion site above -- same isCreditSale leak, same fix. This site
                  // was also missing localStorage.removeItem("isPanCustomer") entirely
                  // (the other site has it) -- isPanCustomer has no Zustand counterpart,
                  // so the missing localStorage clear alone was the full gap for it here.
                  useTransactionStore.getState().setIsCreditSale(null);
                  localStorage.removeItem("isPanCustomer");
                  localStorage.removeItem("isNriBilling");
                  localStorage.removeItem("isCreditSale");
                  try {
                    const attemptsMap = JSON.parse(localStorage.getItem("plbTransactionAttempts") || "{}");
                    const prefix = `${orders[i].documentno}:`;
                    let changed = false;
                    Object.keys(attemptsMap).forEach((key) => {
                      if (key.startsWith(prefix)) {
                        delete attemptsMap[key];
                        changed = true;
                      }
                    });
                    if (changed) localStorage.setItem("plbTransactionAttempts", JSON.stringify(attemptsMap));
                  } catch {
                    // non-blocking — never let this interrupt order finalization
                  }
                  // "Same as Invoicing" toggle reset -- see the matching comment at the
                  // other checkout-completion site above.
                  if (typeof setSwitchToggle === "function") setSwitchToggle(false);

                  const rfidProductsLA = await db.rfidData.toArray();
                  rfidProductsLA.forEach((ele) => {
                    if (ele.tag_status === "SO") {
                      rfidData.push(` {
                                    tagValue: "${ele.tag_value}"
                                    taggingDate: null
                                    batchNumber: null
                                    batchId: null
                                    warehouseId: null
                                    tagStatus: "SO"
                                    lastScannedDate: "${dayjs(new Date()).format("DD-MM-YYYY HH:mm:ss")}"
                                    scannedBy: null
                                    expirydate: null
                                    customAttribute: null
                                    tagType: null
                                    productCode: "${ele.product_code}"
                                    }`);
                    }
                  });
                  await RFIDTag(rfidData);
                } else {
                  console.error("Failed Order Sync ====> ", response);
                  // SYNC-DIAG: server responded (no exception) but said failure — distinct
                  // from the catch(error) path below, which only covers a thrown/network
                  // failure. This is the "call was attempted, backend explicitly rejected
                  // it" case, and needs its own trace to tell apart from "never attempted".
                  try {
                    logSyncDiagThrottled(
                      "sync-diag-confirm-rejected",
                      {
                        sOrderID: orders[i]?.sOrderID,
                        documentno: orders[i]?.documentno,
                        status: result?.status,
                        serverMessage: result?.message,
                        disableOfflineBilling: posConfig?.disableOfflineBilling,
                        syncAttempts: orders[i]?.syncAttempts,
                      },
                      db
                    );
                  } catch (e) {
                    // swallow — diagnostics must never affect order processing
                  }
                  if (posConfig?.disableOfflineBilling === "Y") {
                    // Pure online mode: never silently complete a layaway. Surface error and let user retry.
                    message.error(result?.message || "Layaway failed to sync to server. Please retry.");
                    orderState.current = 0;
                    setActiveOrderProcess(false);
                    window.dispatchEvent(new CustomEvent("cd:feedbackDone"));
                  } else {
                    const syncFailedCount = parseInt(orders[i].syncAttempts) + 1;
                    if (parseInt(orders[i].syncAttempts) < 100) {
                      await db.orders.where("sOrderID").equals(orders[i].sOrderID).modify({ syncAttempts: syncFailedCount });
                    } else {
                      await db.orders.where("sOrderID").equals(orders[i].sOrderID).modify({ isSynced: 2 });
                      notifySyncFailure({ sOrderID: orders[i].sOrderID, documentno: orders[i].documentno, orderType: "layaway", syncAttempts: syncFailedCount });
                    }
                  }
                }
              } catch (error) {
                console.error("Layaway sync error:", error);
                try {
                  logSyncDiagThrottled(
                    "sync-diag-confirm-threw",
                    {
                      sOrderID: orders[i]?.sOrderID,
                      documentno: orders[i]?.documentno,
                      errorMessage: error?.message,
                    },
                    db
                  );
                } catch (e) {
                  // swallow — diagnostics must never affect order processing
                }
                if (posConfig?.disableOfflineBilling === "Y") {
                  message.error("Network error — layaway not completed. Please check connection and retry.");
                  orderState.current = 0;
                  setActiveOrderProcess(false);
                  window.dispatchEvent(new CustomEvent("cd:feedbackDone"));
                }
              }
            } else {
              // SYNC-DIAG: matched neither isRegularOrder nor isLayawayOrder above — no
              // API call attempted at all, no local state change, no backend trace. This
              // order will be re-evaluated identically on the next 10s tick, forever,
              // unless something about its fields (layAway/docType/paid/creditSale)
              // changes in the meantime. Suspected root cause of the ~400 Sales-Order-only,
              // never-invoiced records — the order was created (posOrderProcessor1) but
              // its completion call is never even being attempted.
              try {
                logSyncDiagThrottled(
                  "sync-diag-no-branch-matched",
                  {
                    sOrderID: orders[i]?.sOrderID,
                    documentno: orders[i]?.documentno,
                    isSynced: orders[i]?.isSynced,
                    layAway: orders[i]?.layAway,
                    docType: orders[i]?.docType,
                    paid: orders[i]?.paid,
                    advancePayment: orders[i]?.advancePayment,
                    total: orders[i]?.total,
                    creditSale: orders[i]?.creditSale,
                    parked: orders[i]?.parked,
                    preparedBillOrigin: orders[i]?.preparedBillOrigin,
                  },
                  db
                );
              } catch (e) {
                // swallow — diagnostics must never affect order processing
              }
            }
          }
        } // end orders loop
      } // end if (pendingOrdersCount > 0)

      if (setActiveInput) setActiveInput("search");
    }
    if (syncTrigger === "orderHistory" && showOrderHistory) showOrderHistory();
    if (syncTrigger === "exchange") {
      useTransactionStore.getState().setSelectedReturnType(null);
      useCartStore.getState().setExchangeCart(null);
    }
  };

  // ─── Auto-sync interval ───
  // Pass "auto" so syncOrders knows NOT to build a sync payload from the in-progress cart
  // (which would auto-submit incomplete orders to posOrderProcessor1 every 10 seconds in online mode).
  // Auto-sync only retries pending db.orders rows (offline-mode background retry).
  useEffect(() => {
    // DI-32: On mount, recover any orders stuck in isSynced=3 from a previous crash/reload.
    recoverStuckSyncOrders(db).catch((err) => console.warn("[DI-32] Recovery failed (non-blocking):", err?.message));

    const syncOrdersInterval = setInterval(() => syncOrders("auto"), 10000);
    return () => {
      clearInterval(syncOrdersInterval);
    };
  }, []);

  return {
    // State
    activeOrderProcess,
    setActiveOrderProcess,
    orderState,
    orderDelay,
    setOrderDelay,
    showPrintTemplateModal,
    setShowPrintTemplateModal,
    selectedPrintTemplate,
    setSelectedPrintTemplate,
    pendingOrderParam,
    setPendingOrderParam,
    pendingDocumentNo,
    setPendingDocumentNo,
    pendingIsSynced,
    setPendingIsSynced,
    isPendingDirectApproved,
    setIsPendingDirectApproved,
    isPendingSyncOrder,
    setIsPendingSyncOrder,
    printTemplates,
    enableMultiPrintTemplates,

    // Functions
    processOrder,
    processOrderApproved,
    syncOrders,
    handlePrintTemplateConfirm,
    handlePrintTemplateCancel,
    getDefaultSaleType,
    fetchIRNDetails,
  };
};

export default useOrderProcessing;
